import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'fr' | 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Local storage key for language persistence
const LANGUAGE_STORAGE_KEY = 'rankserveur-language';

const translations = {
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.topVoters': 'Top Voteurs',
    'nav.favorites': 'Favoris',
    'nav.myVotes': 'Mes Votes',
    'nav.creatorTools': 'Outils Créateurs',
    'nav.profile': 'Profil',
    'nav.admin': 'Administration',
    'nav.logout': 'Déconnexion',
    'nav.login': 'Connexion',
    'nav.signup': "S'inscrire",
    'nav.addServer': 'Ajouter un serveur',
    
    // Hero Section
    'hero.title': 'Découvrez les Meilleurs Serveurs Gaming',
    'hero.title2': 'SERVEURS PRIVÉS',
    'hero.subtitle': 'Votez pour vos serveurs préférés et grimpez dans le classement !',
    'hero.subtitle2': 'Une plateforme communautaire pour explorer et voter chaque jour pour les serveurs privés qui vous passionnent.',
    'hero.search.placeholder': 'Rechercher un serveur...',
    'hero.addServer': 'Ajouter un serveur',
    
    // Stats Section
    'stats.title': 'Statistiques en Temps Réel',
    'stats.description': 'Suivez l\'activité de la communauté gaming',
    'stats.servers': 'Serveurs',
    'stats.votes': 'Votes Total',
    'stats.users': 'Utilisateurs',
    'stats.activeServers': 'Serveurs Actifs',
    'stats.totalVotes': 'Votes Total',
    'stats.totalUsers': 'Utilisateurs',
    'stats.globalStats': 'Statistiques Globales',
    'stats.gameStats': 'Statistiques {gameName}',
    'stats.realTimeData': 'Données en temps réel',
    
    // Monthly Reset
    'reset.title': 'Prochain Reset des Votes',
    'reset.description': 'Les votes sont remis à zéro chaque mois',
    'reset.resetIn': 'Reset dans:',
    'reset.monthlyResetTitle': 'Reset Mensuel des Votes',
    'reset.monthlyReset': 'Reset Mensuel',
    'reset.description2': 'Les votes de tous les serveurs seront remis à zéro le 1er du mois prochain',
    'reset.newOpportunities': 'Nouveau classement, nouvelles opportunités !',
    'reset.days': 'jours',
    'reset.hours': 'heures',
    'reset.minutes': 'minutes',
    'reset.seconds': 'secondes',
    
    // Add Server Dialog
    'addServer.title': 'Ajouter un Serveur',
    'addServer.subtitle': 'Rejoignez notre communauté gaming',
    'addServer.freeSubmission': 'Soumission gratuite',
    'addServer.prestigeFeatures': 'Fonctionnalités Prestige disponibles',
    'addServer.basicInfo': 'INFORMATIONS DE BASE',
    'addServer.serverName': 'Nom du serveur',
    'addServer.serverNamePlaceholder': 'Ex: MonServeur Minecraft',
    'addServer.gameType': 'Type de jeu',
    'addServer.selectGame': 'Sélectionner un jeu...',
    'addServer.description': 'Description',
    'addServer.descriptionPlaceholder': 'Décrivez votre serveur, ses particularités...',
    'editor.placeholder': 'Décrivez votre serveur, ses particularités...',
    'addServer.tags': 'Tags du serveur',
    'addServer.serverLogo': 'LOGO DU SERVEUR',
    'addServer.logoLabel': 'Logo du serveur',
    'addServer.logoFormat': 'Format recommandé: PNG, JPG (max 2MB)',
    'addServer.techConfig': 'CONFIGURATION TECHNIQUE',
    'addServer.technicalConfig': 'CONFIGURATION TECHNIQUE',
    'addServer.serverIP': 'IP du serveur',
    'addServer.ipAddress': 'IP du serveur',
    'addServer.ipPlaceholder': 'Ex: play.monserveur.com',
    'addServer.version': 'Version',
    'addServer.versionPlaceholder': 'Ex: 1.20.1',
    'addServer.community': 'LIENS COMMUNAUTÉ',
    'addServer.communityLinks': 'LIENS COMMUNAUTÉ',
    'addServer.discord': 'Discord (optionnel)',
    'addServer.discordUrl': 'Discord (optionnel)',
    'addServer.discordPlaceholder': 'https://discord.gg/...',
    'addServer.website': 'Site web (optionnel)',
    'addServer.websiteUrl': 'Site web (optionnel)',
    'addServer.websitePlaceholder': 'https://monserveur.com',
    'addServer.serverTags': 'TAGS DU SERVEUR',
    'addServer.presentationImage': 'Image de présentation (optionnel)',
    'addServer.presentationImageDesc': 'Cette image sera affichée dans la carte de votre serveur',
    'addServer.serverLogoDesc': 'Logo affiché à côté du nom (optionnel)',
    'addServer.videoUrl': 'URL vidéo (optionnel)',
    'addServer.videoUrlPlaceholder': 'https://youtube.com/watch?v=...',
    'addServer.videoUrlDesc': 'Lien vers une vidéo de présentation de votre serveur',
    'addServer.prestigeTitle': 'FONCTIONNALITÉS PRESTIGE',
    'addServer.prestigeInfo': 'FONCTIONNALITÉS PRESTIGE',
    'addServer.prestigeDesc': 'Boostez la visibilité de votre serveur',
    'addServer.prestigeNote': 'Disponible après ajout du serveur',
    'addServer.prestigeProfileNote': 'Rendez-vous dans votre profil pour activer le statut Prestige de vos serveurs.',
    'addServer.addServerButton': 'Ajouter le serveur',
    'addServer.limitAlert': 'Limite atteinte',
    'addServer.cancel': 'Annuler',
    'addServer.submit': 'Ajouter le serveur',
    
    // Profile Page
    'profile.title': 'MON PROFIL',
    'profile.myServers': 'Mes Serveurs',
    'profile.arcadeSpots': 'Arcade Spots',
    'profile.myReviews': 'Mes Avis',
    'profile.payments': 'Paiements',
    'profile.settings': 'Paramètres',
    'profile.tabs.profile': 'Profil',
    'profile.tabs.security': 'Sécurité',
    'profile.tabs.danger': 'Zone Dangereuse',
    'profile.profileInfo.title': 'Informations du Profil',
    'profile.profileInfo.description': 'Modifiez vos informations personnelles',
    'profile.profileInfo.emailLabel': 'Adresse email',
    'profile.profileInfo.usernameLabel': 'Nom d\'utilisateur',
    'profile.profileInfo.usernamePlaceholder': 'Votre nom d\'utilisateur',
    'profile.profileInfo.updateButton': 'Mettre à jour le profil',
    'profile.profileInfo.updating': 'Mise à jour...',
    'profile.security.title': 'Changer le Mot de Passe',
    'profile.security.description': 'Assurez-vous d\'utiliser un mot de passe fort et unique',
    'profile.security.currentPasswordLabel': 'Mot de passe actuel',
    'profile.security.currentPasswordPlaceholder': 'Votre mot de passe actuel',
    'profile.security.newPasswordLabel': 'Nouveau mot de passe',
    'profile.security.newPasswordPlaceholder': 'Votre nouveau mot de passe',
    'profile.security.confirmPasswordLabel': 'Confirmer le mot de passe',
    'profile.security.confirmPasswordPlaceholder': 'Confirmez votre nouveau mot de passe',
    'profile.security.changePasswordButton': 'Changer le mot de passe',
    'profile.security.changing': 'Mise à jour...',
    'profile.security.requirements': 'Le mot de passe doit contenir au moins 6 caractères',
    'profile.danger.title': 'Zone Dangereuse',
    'profile.danger.description': 'Actions irréversibles sur votre compte',
    'profile.danger.deleteAccount': 'Supprimer mon compte',
    'profile.danger.deleteDescription': 'Cette action supprimera définitivement votre compte et toutes vos données. Cette action est irréversible.',
    'profile.danger.deleteConfirm': 'Êtes-vous absolument sûr de vouloir supprimer votre compte ?',
    'profile.danger.deleteButton': 'Oui, supprimer mon compte',
    'profile.danger.cancel': 'Annuler',
    'profile.danger.deleting': 'Suppression...',
    'profile.emailNotEditable': 'L\'email ne peut pas être modifié',
    'profile.passwordsDontMatch': 'Les mots de passe ne correspondent pas',
    'profile.serverManagement': 'Gestion des serveurs',
    'profile.visibleServers': 'Serveurs visibles',
    'profile.available': 'Disponibles',
    'profile.baseLimit': 'Limite de base',
    'profile.servers': 'Serveurs',
    'profile.prestigeTip': 'Astuce Prestige',
    'profile.prestigeDesc': 'Activez le Prestige sur un serveur pour débloquer +1 emplacement serveur supplémentaire !',
    'profile.noServersYet': 'Vous n\'avez ajouté aucun serveur pour le moment.',
    'profile.serverTotalVotes': 'Total Votes Serveur',
    'profile.userTotalVotes': 'Total Votes Effectués',
    'profile.prestige': 'Prestige',
    'profile.welcomeMessage': 'Vous devez être connecté pour accéder à votre profil.',
    'profile.founder': 'FONDATEUR',
    'profile.admin': 'ADMIN',
    'profile.reviews': 'Avis',
    'profile.activeWarnings': 'Avertissements actifs',
    'profile.server': 'Serveur:',
    'profile.deletedServer': 'Serveur supprimé',
    'profile.disabled': '(DÉSACTIVÉ)',
    'profile.loading': 'Chargement...',
    'profile.prestigeExpires': 'Prestige expire dans {days} jour{plural}',
    'profile.prestigeExpired': 'Prestige expiré',
    'profile.activatePrestige': 'Activer Prestige',
    'profile.deactivatePrestige': 'Désactiver Prestige',
    'profile.upgradePrestige': 'Upgrade Prestige',
    'profile.manageAPI': 'Gérer API',
    'profile.edit': 'Modifier',
    'profile.visibility': 'Visibilité',
    'profile.votes': 'votes',
    
    // Search
    'search.placeholder': 'Rechercher un serveur...',
    'search.noResults': 'Aucun serveur trouvé pour "{query}"',
    'search.votes': 'votes',
    
    // Tags and Filters  
    'tags.loading': 'Chargement des tags...',
    'tags.searchPlaceholder': 'Rechercher un tag...',
    'tags.description': 'Sélectionnez les tags qui décrivent le mieux votre serveur',
    'tags.selected': 'Tags sélectionnés',
    'tags.available': 'Tags disponibles',
    'tags.proposeTag': 'Proposer un tag',
    'tags.filterByTags': 'Filtrer par tags',
    'tags.clearAll': 'Effacer',
    'tags.noTagsFound': 'Aucun tag trouvé',
    'tags.anlaibe': 'Anlaibe',
    'tags.casual': 'Casual',
    'tags.communaute': 'Communauté',
    'tags.competitif': 'Compétitif',
    'tags.creatif': 'Créatif',
    'tags.debutant': 'Débutant',
    'tags.economie': 'Économie',
    'tags.enUS': 'EN-US',
    'tags.evenements': 'Événements',
    'tags.frFR': 'FR-FR',
    'tags.fun': 'Fun',
    'tags.hardcore': 'Hardcore',
    'tags.moreVotes': 'Plus de votes',
    'tags.morePopular': 'Plus populaires',
    'tags.latestServers': 'Derniers serveurs',
    'tags.joinMoreServers': 'Join more than {count} servers on our platform',
    
    // Game Proposals
    'game.proposal.proposeGame': 'Proposer un jeu',
    'game.proposal.gameName': 'Nom du jeu',
    'game.proposal.gameNamePlaceholder': 'Ex: Minecraft, Dofus...',
    'game.proposal.officialLink': 'Lien officiel',
    'game.proposal.comment': 'Commentaire',
    'game.proposal.commentPlaceholder': 'Pourquoi ce jeu devrait-il être ajouté ?',
    'game.proposal.description': 'Proposez un nouveau jeu à ajouter à RankServeur. Votre proposition sera examinée par un administrateur.',
    'game.proposal.success': 'Proposition envoyée !',
    'game.proposal.successMessage': 'Votre proposition de jeu a été envoyée et sera examinée par un administrateur.',
    'game.proposal.error': 'Erreur',
    'game.proposal.nameRequired': 'Le nom du jeu est requis',
    'game.proposal.adminTitle': 'Propositions de Jeux',
    'game.proposal.adminDescription': 'Gérez les propositions de jeux soumises par les utilisateurs',
    'game.proposal.pending': 'En attente',
    'game.proposal.approved': 'Approuvées',
    'game.proposal.rejected': 'Rejetées',
    'game.proposal.submittedBy': 'Proposé par',
    'game.proposal.unknownUser': 'Utilisateur inconnu',
    'game.proposal.adminNotes': 'Notes administrateur',
    'game.proposal.approve': 'Approuver',
    'game.proposal.reject': 'Rejeter',
    'game.proposal.approvedMessage': 'La proposition a été approuvée.',
    'game.proposal.rejectedMessage': 'La proposition a été rejetée.',
    'game.proposal.rejectReasonRequired': 'Veuillez fournir une raison pour le rejet',
    'game.proposal.rejectConfirmTitle': 'Rejeter la proposition',
    'game.proposal.rejectConfirmDescription': 'Veuillez fournir une raison pour le rejet de cette proposition.',
    'game.proposal.rejectReasonPlaceholder': 'Raison du rejet...',
    'game.proposal.confirmReject': 'Confirmer le rejet',
    'game.proposal.noPending': 'Aucune proposition en attente',
    'game.proposal.noApproved': 'Aucune proposition approuvée',
    'game.proposal.noRejected': 'Aucune proposition rejetée',
    
     // Filters
     'filters.sortBy': 'Trier par',
     'filters.votes': 'Votes',
     'filters.newest': 'Plus récent',
     'filters.oldest': 'Plus ancien',
     'filters.clearAll': 'Effacer tout',
     'filters.proposeTag': 'Proposer un tag',
     'filters.tags': 'Filtrer par tags',
     'filters.prestigeFilter': 'Filtre Prestige',
     'filters.allServers': 'Tous les serveurs',
     'filters.prestigeOnly': 'Prestige uniquement',
     'filters.regularOnly': 'Serveurs réguliers',
    
    // Server Card
    'server.vote': 'Voter',
    'server.report': 'Signaler',
    'server.online': 'En ligne',
    'server.offline': 'Hors ligne',
    'server.players': 'joueurs',
    'server.sponsored': 'Sponsorisé',
    'server.gold': 'Gold',
    'server.rank': 'Rang',
    'server.api': 'API',
    
    // Footer
    'footer.description': 'La meilleure plateforme pour découvrir et voter pour les serveurs privés de jeux les plus populaires.',
    'footer.madeWith': 'Fait avec passion pour la communauté gaming',
    'footer.statsTitle': 'Statistiques en Temps Réel',
    'footer.serversListed': 'serveurs listés',
    'footer.votesToday': 'votes aujourd\'hui',
    'footer.addServerCTA': '+ Ajouter mon serveur gratuitement',
    'footer.serversOnline': 'serveurs en ligne',
    'footer.updatedRealTime': 'Mis à jour en temps réel',
    'footer.partnersTitle': 'Partenaires',
    'footer.linksTitle': 'Liens',
    'footer.links': 'Liens utiles',
    'footer.legal': 'Légal',
    'footer.contact': 'Contact',
    'footer.termsOfService': "Conditions d'utilisation",
    'footer.termsOfSale': 'Conditions de vente',
    'footer.privacy': 'Politique de confidentialité',
    'footer.copyright': 'Tous droits réservés.',
    'footer.discordBot': 'Bot Discord',
    
    // Discord Bot Page
    'discordBot.title': 'Bot Discord RankServeur',
    'discordBot.metaDescription': 'Découvrez toutes les commandes du bot Discord RankServeur. Consultez les classements, votez et recherchez des serveurs directement depuis Discord.',
    'discordBot.heroTitle': 'Bot Discord',
    'discordBot.heroSubtitle': 'Consultez les classements, votez et recherchez des serveurs de jeu directement depuis votre serveur Discord.',
    'discordBot.addToDiscord': 'Ajouter à Discord',
    'discordBot.viewSource': 'Code source',
    'discordBot.commandsTitle': 'Commandes disponibles',
    'discordBot.commandsSubtitle': 'Toutes les commandes slash du bot RankServeur',
    'discordBot.cmd.top': '/top',
    'discordBot.cmd.topDesc': 'Affiche le top des serveurs par votes mensuels. Filtrez par jeu et limitez le nombre de résultats.',
    'discordBot.cmd.topUsage': '/top [game:minecraft] [limit:10]',
    'discordBot.cmd.server': '/server',
    'discordBot.cmd.serverDesc': 'Affiche les informations détaillées d\'un serveur : IP, version, joueurs en ligne, tags, liens.',
    'discordBot.cmd.serverUsage': '/server name:MonServeur',
    'discordBot.cmd.stats': '/stats',
    'discordBot.cmd.statsDesc': 'Affiche les statistiques détaillées d\'un serveur avec un graphique des votes sur 7 jours.',
    'discordBot.cmd.statsUsage': '/stats name:MonServeur',
    'discordBot.cmd.vote': '/vote',
    'discordBot.cmd.voteDesc': 'Génère un lien direct pour voter pour un serveur. Un vote toutes les 2 heures.',
    'discordBot.cmd.voteUsage': '/vote name:MonServeur',
    'discordBot.cmd.search': '/search',
    'discordBot.cmd.searchDesc': 'Recherche un serveur par nom. Retourne les résultats avec liens directs.',
    'discordBot.cmd.searchUsage': '/search query:minecraft pvp',
    'discordBot.cmd.games': '/games',
    'discordBot.cmd.gamesDesc': 'Liste tous les jeux disponibles sur RankServeur.',
    'discordBot.cmd.gamesUsage': '/games',
    'discordBot.cmd.serveroftheday': '/serveroftheday',
    'discordBot.cmd.serverofthedayDesc': 'Affiche le serveur du jour — celui ayant reçu le plus de votes aujourd\'hui.',
    'discordBot.cmd.serverofthedayUsage': '/serveroftheday',
    'discordBot.hostingTitle': 'Hébergement',
    'discordBot.hostingDesc': 'Le bot nécessite un hébergement Node.js. Voici des options gratuites :',
    'discordBot.hosting.railway': 'Railway — 500h/mois gratuites',
    'discordBot.hosting.render': 'Render — Hébergement gratuit (avec cold starts)',
    'discordBot.hosting.replit': 'Replit — Gratuit avec limitations',
    'discordBot.hosting.wispbyte': 'WispByte — Hébergement bot Discord gratuit',
    'discordBot.hosting.selfhost': 'Auto-hébergement — Lancez node bot.js sur votre propre machine',
    'discordBot.languageTitle': 'Multilingue',
    'discordBot.languageDesc': 'Le bot détecte automatiquement la langue de votre Discord (FR, EN, ES) et répond dans cette langue.',
    'discordBot.required': 'Requis',
    'discordBot.optional': 'Optionnel',
    'discordBot.downloadSources': 'Télécharger les sources',
    'discordBot.perfTitle': 'Optimisé pour les hébergements gratuits',
    'discordBot.perfDesc': 'Le bot utilise seulement 1 intent Discord (Guilds) et fait des appels API légers. Il tourne facilement sur les offres gratuites (128 MB RAM, 25% CPU).',
    'discordBot.securityTitle': 'Code source sécurisé',
    'discordBot.securityDesc': 'Le code source ne contient AUCUNE clé API ni mot de passe. Chaque utilisateur doit créer son propre fichier .env avec ses propres identifiants Discord. Le partage est 100% sûr.',
    'discordBot.securityDesc2': 'Le code source est 100% sûr à partager. Il ne contient AUCUN secret. L\'API RankServeur est publique (lecture seule) — pas besoin de clé API. Vous n\'avez besoin que de votre propre Token Discord et Application ID.',
    'discordBot.setupTitle': 'Installation en 4 étapes',
    'discordBot.setupSubtitle': 'Hébergez votre propre instance du bot RankServeur',
    'discordBot.step1Title': 'Copier les fichiers sources',
    'discordBot.step1Desc': 'Créez un dossier et copiez ces fichiers :',
    'discordBot.stepCreateFolder': 'Créez un dossier, puis copiez-collez chaque fichier ci-dessous. Cliquez pour ouvrir, puis copiez avec le bouton.',
    'discordBot.step2Title': 'Créer une application Discord',
    'discordBot.step2Desc': 'Allez sur le Discord Developer Portal → New Application → Bot → copiez le Token + Application ID.',
    'discordBot.step3Title': 'Créer le fichier .env',
    'discordBot.step3Desc': 'Créez un fichier .env à la racine du projet avec vos propres identifiants :',
    'discordBot.step3Desc2': 'Créez un fichier .env avec uniquement votre Token Discord et votre Application ID :',
    'discordBot.step3Note': 'Ne partagez JAMAIS votre fichier .env.',
    'discordBot.noApiKeyNeeded': 'Pas besoin de clé API RankServeur ! L\'API est publique en lecture seule.',
    'discordBot.step4Title': 'Installer et lancer',
    'discordBot.step4npm': 'Installer les dépendances',
    'discordBot.step4deploy': 'Enregistrer les commandes Discord',
    'discordBot.step4start': 'Lancer le bot',
    'discordBot.voteNotifTitle': 'Notifications de votes',
    'discordBot.voteNotifDesc': 'Recevez automatiquement une notification dans votre salon Discord à chaque vote sur votre serveur. Basé sur le Server UUID pour éviter tout conflit.',
    'discordBot.voteNotifHow': 'Configurez un webhook Discord dans les paramètres de votre serveur sur RankServeur.',
    'discordBot.webhookSetupTitle': '📋 Comment configurer en 3 étapes :',
    'discordBot.webhookStep1': 'Dans Discord : Paramètres du serveur → Intégrations → Webhooks → Créer un Webhook → Copiez l\'URL.',
    'discordBot.webhookStep2': 'Sur RankServeur : Allez dans votre profil → Mes serveurs → Webhooks → Collez l\'URL Discord et générez un secret.',
    'discordBot.webhookStep3': 'C\'est terminé ! Chaque vote enverra automatiquement une notification dans votre salon Discord.',
    'discordBot.webhookPayloadTitle': 'Exemple de payload envoyé à chaque vote :',
    'discordBot.webhookSignatureInfo': 'Chaque requête est signée avec HMAC-SHA256 (header X-RS-Signature) pour vérifier l\'authenticité.',
    'discordBot.webhookAdvancedLink': '📖 Guide avancé : vérification HMAC, exemples PHP/Node/Python → Documentation API',
    'discordBot.source.botDesc': 'Bot principal',
    'discordBot.source.deployDesc': 'Enregistrement des commandes',
    'discordBot.source.apiDesc': 'Client API RankServeur',
    'discordBot.source.i18nDesc': 'Traductions FR/EN/ES',
    'discordBot.source.packageDesc': 'Dépendances npm',
    'discordBot.sourceNote': 'Ces fichiers ne contiennent aucun secret.',
    
    // Legal pages titles
    'terms.title': "Conditions d'utilisation",
    'terms.description': "Conditions générales d'utilisation de la plateforme RankServeur et règles d'utilisation des services",
    'terms.keywords': "conditions utilisation, règlement, plateforme, RankServeur",
    'termsOfSale.title': 'Conditions de vente',
    'termsOfSale.description': 'Conditions générales de vente pour les espaces publicitaires RankServeur',
    'termsOfSale.keywords': 'conditions de vente, espaces publicitaires, RankServeur',
    'privacy.title': 'Politique de confidentialité',
    'privacy.description': 'Comment nous collectons, utilisons et protégeons vos données personnelles',
    'privacy.keywords': 'confidentialité, données personnelles, RGPD, protection',

    // API Documentation
    'api.title': "Documentation API",
    'api.description': "API complète pour créateurs de serveurs : statistiques, avis, classements et webhooks de vote en temps réel",
    'api.keywords': "API développeurs, webhooks, statistiques serveurs, intégration gaming",
    
    // Creator Tools
    'creatorTools.title': 'Outils Créateurs',
    'creatorTools.description': 'Générateur de widgets personnalisés pour intégrer facilement votre serveur sur votre site ou launcher',
    'creatorTools.keywords': 'widgets, générateur, intégration, badge, bouton, bandeau, iframe, script',
    'creatorTools.subtitle': 'Générez des widgets personnalisés pour promouvoir votre serveur partout',
    'creatorTools.configuration': 'Configuration',
    'creatorTools.preview': 'Aperçu en direct',
    'creatorTools.code': 'Code à copier',
    'creatorTools.guide': 'Guide rapide',
    'creatorTools.selectServer': 'Sélectionner un serveur',
    'creatorTools.selectServerPlaceholder': 'Choisissez votre serveur...',
    'creatorTools.format': 'Format',
    'creatorTools.formatBadge': 'Badge',
    'creatorTools.formatButton': 'Bouton',
    'creatorTools.formatBanner': 'Bannière',
    'creatorTools.size': 'Taille',
    'creatorTools.sizeSmall': 'Petit',
    'creatorTools.sizeMedium': 'Moyen',
    'creatorTools.sizeLarge': 'Grand',
    'creatorTools.theme': 'Thème',
    'creatorTools.themeLight': 'Clair',
    'creatorTools.themeDark': 'Sombre',
    'creatorTools.themeRetro': 'Rétro Gaming',
    'creatorTools.language': 'Langue',
    'creatorTools.showVotes': 'Afficher les votes',
    'creatorTools.rounded': 'Coins arrondis',
    'creatorTools.animation': 'Animation',
    'creatorTools.tabIframe': 'Iframe',
    'creatorTools.tabScript': 'Script JS',
    'creatorTools.copyCode': 'Copier le code',
    'creatorTools.copied': 'Code copié !',
    'creatorTools.step1': 'Configurez votre widget',
    'creatorTools.step1Desc': 'Choisissez votre serveur et personnalisez l\'apparence',
    'creatorTools.step2': 'Prévisualisez le rendu',
    'creatorTools.step2Desc': 'Vérifiez que le widget correspond à vos attentes',
    'creatorTools.step3': 'Intégrez le code',
    'creatorTools.step3Desc': 'Copiez et collez le code sur votre site',
    'creatorTools.widgetFor': 'Widget pour',
    'creatorTools.server': 'serveur',
    'creatorTools.widgetRankServeur': 'Widget RankServeur',
    'creatorTools.noServers': 'Vous devez avoir au moins un serveur pour utiliser cet outil',
    'creatorTools.loginRequired': 'Connectez-vous pour accéder aux outils créateurs',
    'creatorTools.playerLabel': 'Pseudo pré-rempli (optionnel)',
    'creatorTools.playerPlaceholder': 'Ex: PlayerNickname',
    'creatorTools.playerHint': "Ajoute ?player= à l'URL de vote (non vérifié côté serveur, modifiable par le joueur).",
    'creatorTools.logoSize': 'Taille du logo',
    'creatorTools.logoSizeSmall': 'Petit',
    'creatorTools.logoSizeMedium': 'Moyen',
    'creatorTools.logoSizeLarge': 'Grand',
    'creatorTools.logoSizeNone': 'Masqué',
    'creatorTools.accentColor': "Couleur d'accent",
    'creatorTools.effect': 'Effet visuel',
    'creatorTools.effectNone': 'Aucun',
    'creatorTools.effectGlow': 'Lueur néon',
    'creatorTools.effectShadow': 'Ombre portée',
    'creatorTools.effectGradient': 'Bordure dégradée',
    'creatorTools.borderStyle': 'Style de bordure',
    'creatorTools.borderSolid': 'Solide',
    'creatorTools.borderDashed': 'Pointillé',
    'creatorTools.borderDouble': 'Double',
    'creatorTools.borderNone': 'Aucune',
    'creatorTools.ctaText': 'Texte du bouton',
    'creatorTools.ctaPlaceholder': 'Ex: Voter maintenant',
    'creatorTools.appearance': 'Apparence',
    'creatorTools.effects': 'Effets & Style',
    'creatorTools.bgColor': 'Couleur de fond',
    'creatorTools.bgImage': 'Image de fond (URL)',
    'creatorTools.bgImagePlaceholder': 'https://exemple.com/mon-fond.jpg',
    'creatorTools.bgImageHint': "L'image est hébergée chez vous, on ne la stocke pas.",

    // --- Common ---
    'common.results': 'résultats',
    
    // Votifier Guide
    'votifierGuide.title': 'Guide Votifier pour Minecraft',
    'votifierGuide.description': 'Guide complet pour configurer Votifier sur votre serveur Minecraft et recevoir les notifications de vote automatiquement',
    'votifierGuide.keywords': 'votifier, minecraft, guide, configuration, plugin, votes, notifications',
    'votifierGuide.subtitle': 'Configurez Votifier sur votre serveur Minecraft pour recevoir automatiquement les notifications de vote et récompenser vos joueurs.',
    'votifierGuide.whatIsVotifier': 'Qu\'est-ce que Votifier ?',
    'votifierGuide.whatIsVotifierDesc': 'Votifier est un plugin Minecraft qui permet à votre serveur de recevoir des notifications instantanées lorsqu\'un joueur vote sur des sites comme RankServeur. Cela permet de récompenser automatiquement les joueurs.',
    'votifierGuide.prerequisites': 'Prérequis',
    'votifierGuide.prereq1': 'Un serveur Minecraft avec Spigot, Paper ou Bukkit',
    'votifierGuide.prereq2': 'Accès administrateur au serveur',
    'votifierGuide.prereq3': 'Port réseau ouvert (par défaut 8192)',
    'votifierGuide.prereq4': 'Serveur ajouté sur RankServeur',
    'votifierGuide.step1Title': 'Télécharger le plugin Votifier',
    'votifierGuide.step1Desc': 'Choisissez la version de Votifier qui convient le mieux à votre serveur :',
    'votifierGuide.votifierOriginalDesc': 'Version originale stable, compatible avec la plupart des serveurs',
    'votifierGuide.votifierPlusDesc': 'Version améliorée avec plus de fonctionnalités et de compatibilité',
    'votifierGuide.recommended': 'Recommandé',
    'votifierGuide.recommendedDesc': 'VotifierPlus est généralement recommandé pour sa meilleure compatibilité et ses fonctionnalités avancées.',
    'votifierGuide.step2Title': 'Installer le plugin',
    'votifierGuide.step2Point1': 'Arrêtez votre serveur Minecraft',
    'votifierGuide.step2Point2': 'Placez le fichier .jar téléchargé dans le dossier /plugins/',
    'votifierGuide.step2Point3': 'Redémarrez votre serveur',
    'votifierGuide.step2Point4': 'Vérifiez que le plugin s\'est chargé correctement dans les logs',
    'votifierGuide.step3Title': 'Configurer le plugin',
    'votifierGuide.step3Desc': 'Après le premier démarrage, éditez le fichier de configuration :',
    'votifierGuide.configExplanation': 'Explication des paramètres :',
    'votifierGuide.hostExplanation': 'Laissez 0.0.0.0 pour écouter sur toutes les interfaces',
    'votifierGuide.portExplanation': 'Port sur lequel Votifier écoute (8192 par défaut)',
    'votifierGuide.tokenExplanation': 'Token de sécurité partagé (générez-en un unique)',
    'votifierGuide.debugExplanation': 'Mode debug pour diagnostiquer les problèmes',
    'votifierGuide.step4Title': 'Configuration sur RankServeur',
    'votifierGuide.step4Desc': 'Maintenant, configurez Votifier côté RankServeur :',
    'votifierGuide.step4Point1': 'Connectez-vous sur RankServeur',
    'votifierGuide.step4Point2': 'Allez dans votre Profil > Mes serveurs',
    'votifierGuide.step4Point3': 'Cliquez sur "Modifier" sur votre serveur Minecraft',
    'votifierGuide.step4Point4': 'Faites défiler jusqu\'à la section "Configuration Votifier"',
    'votifierGuide.step4Point5': 'Entrez l\'IP, le port et le token de votre serveur',
    'votifierGuide.automaticRewards': 'Récompenses automatiques',
    'votifierGuide.automaticRewardsDesc': 'Une fois configuré, votre serveur recevra automatiquement les notifications de vote. Vous pouvez utiliser des plugins comme VotingPlugin ou GAListener pour récompenser automatiquement les joueurs.',
    'votifierGuide.troubleshooting': 'Résolution de problèmes',
    'votifierGuide.problem1': 'Les votes n\'arrivent pas sur le serveur',
    'votifierGuide.solution1': 'Vérifiez que le port 8192 est ouvert dans votre pare-feu et chez votre hébergeur. Testez la connexion avec telnet ou un scanner de ports.',
    'votifierGuide.problem2': 'Erreur "Connection refused"',
    'votifierGuide.solution2': 'Le plugin Votifier n\'est peut-être pas démarré ou le port est déjà utilisé. Vérifiez les logs du serveur et changez le port si nécessaire.',
    'votifierGuide.problem3': 'Token invalide',
    'votifierGuide.solution3': 'Assurez-vous que le token configuré sur RankServeur correspond exactement à celui dans config.yml. Attention aux espaces et caractères spéciaux.',
    'votifierGuide.problem4': 'Le serveur crash au démarrage',
    'votifierGuide.solution4': 'Vérifiez la compatibilité du plugin avec votre version de serveur. Essayez une version différente de Votifier.',
    'votifierGuide.needHelp': 'Besoin d\'aide ?',
    'votifierGuide.needHelpDesc': 'Si vous rencontrez des difficultés, notre équipe est là pour vous aider !',
    'votifierGuide.contactSupport': 'Contacter le support',
    'votifierGuide.apiDocs': 'Documentation API',
    'votifierGuide.copied': 'Copié !',
    'votifierGuide.copiedDesc': 'Le code a été copié dans le presse-papiers.',
    'api.whereToFind': "Où trouver votre clé API",
    'api.whereToFindDesc': "1. Connectez-vous sur RankServeur\n2. Allez dans votre Profil\n3. Cliquez sur 'Mes serveurs'\n4. Sélectionnez votre serveur\n5. Cliquez sur l'icône 'API' (<>)\n6. Copiez votre clé API",
    'api.webhookSetup': "Configuration des Webhooks",
    'api.webhookSetupDesc': "1. Dans 'Mes serveurs'\n2. Cliquez sur l'icône Webhooks (⚙️ vert)\n3. Ajoutez l'URL de votre endpoint\n4. Sélectionnez les événements à recevoir\n5. Testez la configuration",
    'api.security': "Sécurité API",
    'api.securityDesc': "• Gardez votre clé API secrète\n• Utilisez HTTPS uniquement\n• Vérifiez la signature HMAC des webhooks\n• Limitez l'accès aux IPs nécessaires",
    'api.gettingStarted': "Pour commencer",
    'api.gettingStartedDesc': "L'API RankServeur permet aux créateurs de serveurs d'intégrer facilement le système de vote dans leurs launchers et sites web.",
    'api.step1': "Étape 1 : Récupérer votre clé API",
    'api.step2': "Étape 2 : Configurer les webhooks", 
    'api.step3': "Étape 3 : Intégrer dans votre application",
    'api.commonUse': "Cas d'usage courants",
    'api.commonUseDesc': "• Récompenser automatiquement les joueurs qui votent\n• Afficher le nombre de votes en temps réel\n• Suivre les statistiques de vote\n• Créer des classements personnalisés",
    
    // Buttons
    'button.save': 'Enregistrer',
    'button.cancel': 'Annuler',
    'button.refresh': 'Actualiser',
    'button.confirm': 'Confirmer',
    'button.close': 'Fermer',
    'button.edit': 'Modifier',
    'button.delete': 'Supprimer',
    'button.add': 'Ajouter',
    'button.search': 'Rechercher',
    'button.filter': 'Filtrer',
    
    // Games
    'games.allGames': 'Tous les jeux',
    'games.selectGame': 'Sélectionner un jeu',
    
    // Competitive Events
    'competitive.showRanking': 'Classement événement',
    'competitive.showingRanking': 'Classement événement',
    
    // Index page
    'index.addMyServer': 'AJOUTER MON SERVEUR',
    'index.joinServers': 'Rejoignez plus de {count} serveurs sur notre plateforme',
    'index.joinListedServers': 'Rejoignez les serveurs listés',
    
    // Edit Server - French translations
    'editServer.title': 'Modifier le serveur',
    'editServer.delete': 'Supprimer',
    'editServer.deleteConfirmTitle': 'Supprimer le serveur',
    'editServer.deleteConfirmText': 'Êtes-vous sûr de vouloir supprimer "{name}" ? Cette action est irréversible.',
    'editServer.deleting': 'Suppression...',
    'editServer.basicInfo': 'INFORMATIONS DE BASE',
    'editServer.serverName': 'Nom du serveur',
    'editServer.game': 'Jeu',
    'editServer.selectGame': 'Sélectionner un jeu',
    'editServer.description': 'Description',
    'editServer.descriptionPlaceholder': 'Décrivez votre serveur...',
    'editServer.descriptionImage': 'Image de description',
    'editServer.serverImages': 'IMAGES DU SERVEUR',
    'editServer.serverLogo': 'Logo du serveur',
    'editServer.serverBanner': 'Bannière du serveur',
    'editServer.prestigeRequired': 'Prestige requis',
    'editServer.bannerAvailableWithPrestige': 'Bannière disponible avec Prestige',
    'editServer.upgradeForBanner': 'Passez au Prestige pour ajouter une bannière à votre serveur',
    'editServer.locked': 'VERROUILLÉ',
    'editServer.technicalConfig': 'CONFIGURATION TECHNIQUE',
    'editServer.ipAddress': 'Adresse IP',
    'editServer.version': 'Version',
    'editServer.communityLinks': 'LIENS COMMUNAUTAIRES',
    'editServer.discord': 'Discord',
    'editServer.website': 'Site web',
    'editServer.serverTags': 'TAGS DU SERVEUR',
    'editServer.prestigeCustomization': 'PERSONNALISATION PRESTIGE',
    'editServer.customColor': 'Couleur personnalisée',
    'editServer.upgradeForColor': 'Passez au Prestige pour personnaliser la couleur de votre serveur',
     'editServer.updating': 'Mise à jour...',
     'editServer.updateServer': 'Mettre à jour le serveur',
    'editServer.serverEvents': 'Événements en cours et à venir',
    'editServer.save' : 'Sauvegarder',
    'editServer.videoUrl': 'URL vidéo',
    'editServer.videoUrlPlaceholder': 'https://youtube.com/watch?v=...',
    'editServer.videoUrlDesc': 'Lien vers une vidéo de présentation (optionnel)',
    
    // Prestige - French translations  
    'prestige.upgradeTitle': 'Passer au Prestige',
    'prestige.duration': 'Durée',
    'prestige.day': 'jour',
    'prestige.days': 'jours',
    'prestige.unitPrice': 'Prix unitaire',
    'prestige.total': 'Total',
    'prestige.featuresIncluded': 'Fonctionnalités incluses',
    'prestige.cancel': 'Annuler',
    'prestige.loading': 'Chargement...',
    'prestige.pay': 'Payer',
    
    // Vote API Manager - French translations
    'voteApi.title': 'API de Vote',
    'voteApi.description': 'Configurez l\'API de vote pour votre serveur et suivez les statistiques en temps réel.',
    'voteApi.tokenLabel': 'Token API',
    'voteApi.regenerateToken': 'Régénérer le token',
    'voteApi.showToken': 'Afficher le token',
    'voteApi.hideToken': 'Masquer le token',
    'voteApi.copyToken': 'Copier le token',
    'voteApi.apiDocumentation': 'Documentation API',
    'voteApi.endpointLabel': 'Point d\'accès API',
    'voteApi.copyEndpoint': 'Copier l\'endpoint',
    'voteApi.exampleRequest': 'Exemple de requête',
    'voteApi.copyExample': 'Copier l\'exemple',
    'voteApi.tokenRegenerated': 'Token régénéré',
    'voteApi.tokenRegeneratedDesc': 'Votre nouveau token API a été généré avec succès',
    'voteApi.copySuccess': 'Copié !',
    'voteApi.copySuccessDesc': 'Le contenu a été copié dans le presse-papiers',
    'voteApi.error': 'Erreur',
    'voteApi.regenerateError': 'Impossible de régénérer le token',
    'voteApi.keepSecret': '⚠️ Gardez cette clé secrète et sécurisée',
    'voteApi.apiUrl': 'URL de l\'API',
    'voteApi.replaceUserIp': 'Remplacez {USER_IP} par l\'IP de l\'utilisateur',
    'voteApi.endpointFormat': 'Format du point d\'accès',
    'voteApi.successResponse': 'Réponse de succès',
    'voteApi.failureResponse': 'Réponse d\'échec',
    'voteApi.phpExample': 'Exemple PHP',
    'voteApi.importantInfoTitle': 'Informations importantes',
    'voteApi.votesValid24h': 'Les votes sont valides 24h (1 vote/jour/serveur)',
    'voteApi.ipHashingSecurity': 'Hachage IP SHA-256 pour la sécurité',
    'voteApi.limitTokenAccess': 'Limitez l\'accès au token API',
    'voteApi.testIntegration': 'Testez votre intégration avant la mise en production',

    // Edit Server Dialog - New translations
    'editServer.editServer': 'Modifier le serveur',
    'vote.created': 'vote.created',
    'editServer.events': 'Événements',
    'editServer.tags': 'Tags',
    'editServer.mediaTags': 'Médias & Tags',
    'editServer.integrations': 'Intégrations',
    'editServer.prestige': 'Prestige',

    // Votifier Configuration - French translations
    'votifier.title': 'Configuration Votifier',
    'votifier.description': 'Votifier permet à votre serveur Minecraft de recevoir des notifications instantanées lorsqu\'un joueur vote. Configurez votre plugin Votifier sur votre serveur et ajoutez les informations ci-dessous.',
    'votifier.activeConfigs': 'Configurations actives',
    'votifier.addConfig': 'Ajouter une configuration Votifier',
    'votifier.serverHost': 'Adresse IP du serveur *',
    'votifier.serverHostPlaceholder': '127.0.0.1 ou votre-domaine.com',
    'votifier.port': 'Port Votifier *',
    'votifier.portPlaceholder': '8192',
    'votifier.protocol': 'Protocole Votifier',
    'votifier.protocolV2': 'v2 (Recommandé - JSON/HTTP)',
    'votifier.protocolV1': 'v1 (Legacy - RSA/TCP)',
    'votifier.token': 'Token/Clé *',
    'votifier.tokenPlaceholder': 'Token de sécurité Votifier',
    'votifier.generate': 'Générer',
    'votifier.active': 'Actif',
    'votifier.inactive': 'Inactif',
    'votifier.creating': 'Création...',
    'votifier.createConfig': 'Créer la configuration',
    'votifier.installationGuide': 'Guide d\'installation',
    'votifier.pluginInstall': 'Installation du plugin :',
    'votifier.installStep1': 'Téléchargez Votifier ou VotifierPlus depuis SpigotMC',
    'votifier.installStep2': 'Placez le .jar dans votre dossier plugins/',
    'votifier.installStep3': 'Redémarrez votre serveur',
    'votifier.installStep4': 'Configurez le plugin avec vos paramètres ci-dessus',
    'votifier.installStep5': 'Activez la configuration sur cette page',
    'votifier.pluginConfig': 'Configuration plugin (config.yml) :',
    'votifier.importantNote': 'Important :',
    'votifier.firewallNote': 'Assurez-vous que le port {port} est ouvert dans votre pare-feu et redirigé correctement si vous utilisez un hébergeur.',
    'votifier.configCreated': 'Configuration Votifier créée',
    'votifier.configCreatedDesc': 'Votre serveur recevra maintenant les notifications de votes.',
    'votifier.configUpdated': 'Configuration mise à jour',
    'votifier.configUpdatedDesc': 'Le statut Votifier a été modifié.',
    'votifier.configDeleted': 'Configuration supprimée',
    'votifier.configDeletedDesc': 'La configuration Votifier a été supprimée.',
    'votifier.requiredFields': 'Champs requis',
    'votifier.requiredFieldsDesc': 'Veuillez remplir tous les champs obligatoires.',
    'votifier.invalidPort': 'Port invalide',
    'votifier.invalidPortDesc': 'Le port doit être entre 1 et 65535.',
    'votifier.loading': 'Chargement...',
    'votifier.error': 'Erreur',
    'votifier.createError': 'Impossible de créer la configuration Votifier.',

    // Webhook Integration
    'webhookIntegration.title': 'Intégration Webhooks',
    'webhookIntegration.description': 'Recevez des notifications en temps réel quand des événements se produisent',
    'webhookIntegration.urlLabel': 'URL du webhook',
    'webhookIntegration.secretLabel': 'Secret HMAC',
    'webhookIntegration.generateSecret': 'Générer',
    'webhookIntegration.createWebhook': 'Créer le webhook',
    'webhookIntegration.testWebhook': 'Tester',
    'webhookIntegration.deleteWebhook': 'Supprimer',
    'webhookIntegration.webhookCreated': 'Webhook créé',
    'webhookIntegration.webhookCreatedDesc': 'Le webhook a été créé avec succès.',
    'webhookIntegration.webhookUpdated': 'Webhook mis à jour',
    'webhookIntegration.webhookDeleted': 'Webhook supprimé',
    'webhookIntegration.testSuccess': 'Test webhook réussi',
    'webhookIntegration.testError': 'Erreur test webhook',
    'webhookIntegration.active': 'Actif',
    'webhookIntegration.inactive': 'Inactif',
    'webhookIntegration.documentation': 'Documentation',
    'webhookIntegration.supportedEvents': 'Événements supportés:',
    'webhookIntegration.voteCreated': 'Déclenché quand un joueur vote pour votre serveur',
    'webhookIntegration.security': 'Sécurité:',
    'webhookIntegration.hmacSigned': 'Chaque requête est signée avec HMAC-SHA256',
    'webhookIntegration.eventHeader': 'Header',
    'webhookIntegration.signatureHeader': 'Header',
    'webhookIntegration.signatureFormat': 'Format de la signature:',
    'webhookIntegration.urlRequired': 'URL et secret sont obligatoires.',
    'webhookIntegration.urlPlaceholder': 'https://votre-domaine.com/webhook',
    'webhookIntegration.secretPlaceholder': 'Secret pour signer les requêtes',
    'webhookIntegration.testPayloadLabel': 'Payload de test (JSON)',
    'webhookIntegration.sendTest': 'Envoyer le test',
    'webhookIntegration.testing': 'Test en cours...',
    'webhookIntegration.creating': 'Création...',
    'webhookIntegration.configured': 'Webhooks configurés',

    // Common
    'common.status': 'Statut',
    'index.voteEvery2Hours': 'Votez toutes les 2 heures pour vos serveurs préférés !',
    'index.nextVoteIn': 'Prochain vote dans : {hours}h {minutes}m',
    'index.canVoteNow': 'Vous pouvez voter maintenant !',
    'index.topServers': 'TOP SERVEURS',
    'index.rankingRealTime': 'Classement mis à jour en temps réel ({count} serveur au total)',
    
     // Favorites Page
     'favorites.title': 'MES FAVORIS',
     'favorites.subtitle': 'Vos serveurs préférés ({count})',
     'favorites.loginRequired': 'Vous devez être connecté pour voir vos favoris.',
     'favorites.noFavoritesTitle': 'Aucun favori pour le moment',
     'favorites.noFavoritesDesc': 'Ajoutez des serveurs à vos favoris en cliquant sur le cœur dans la liste des serveurs.',
     'favorites.loading': 'Chargement...',

     // Common
     'common.loading': 'Chargement...',
     'common.error': 'Une erreur est survenue',
     'common.noResults': 'Aucun résultat trouvé',
     'common.viewMore': 'Voir plus',
     'common.viewLess': 'Voir moins',
     'common.create': 'Créer',
     'common.add': 'Ajouter', 
     'common.edit': 'Modifier',
     'common.delete': 'Supprimer',
     'common.cancel': 'Annuler',
     'common.submit': 'Soumettre',
     'common.submitting': 'Envoi en cours...',
     'common.success': 'Succès',
     'common.none': 'Aucun',
     'common.all': 'Tous',
     'common.filter': 'Filtrer',
     'common.search': 'Rechercher',
      'common.sort': 'Trier',
      'common.servers': 'Serveurs Privés',
      'common.showMore': 'Voir plus',
      'common.showLess': 'Voir moins',
      'common.available': 'Disponible',
      'common.notAvailable': 'Non disponible',
     'common.noServersFound': 'Aucun serveur trouvé',
     'common.noServersWithFilters': 'Aucun serveur trouvé avec ces filtres',
     'common.loadingServers': 'Chargement des serveurs...',
     'common.errorLoadingServers': 'Erreur lors du chargement des serveurs',
     
       // Tags
       'tags.selectedTags': 'Tags sélectionnés',
       'tags.popular': 'Tags populaires',
       'tags.searchResults': 'Résultats de recherche',
      'tags.moreClicks': 'Plus de clics',
     
     // Server Management
     'serverManagement.loading': 'Chargement...',
     'serverManagement.serverDisabled': 'Serveur désactivé',
     'serverManagement.activeWarnings': 'Avertissements actifs',
     'serverManagement.server': 'Serveur :',
     'serverManagement.serversVisible': 'Serveurs visibles',
     'serverManagement.available': 'Disponibles',
     'serverManagement.baseLimit': 'Limite de base',
     'serverManagement.prestigeBonus': 'Bonus Prestige',
     'serverManagement.hiddenServers': 'Serveurs masqués',
     'serverManagement.management': 'Gestion des serveurs',
     'serverManagement.remaining': 'restant',
     'serverManagement.hidden': 'masqué',
      'serverManagement.servers': 'serveurs',
      'serverManagement.server_single': 'serveur',
      
      // Image Upload
      'imageUpload.supportedFormats': 'Formats acceptés: JPG, PNG, WebP. Taille max: 5MB',
      'imageUpload.dragDropText': 'Glissez votre image de description ici ou',
      'imageUpload.selectFile': 'Sélectionner un fichier',
      'imageUpload.recommendedSize.logo': ' - Taille recommandée: 96x96px',
      'imageUpload.recommendedSize.banner': ' - Taille recommandée: 1200x400px',
      'imageUpload.recommendedSize.backdrop': ' - Taille recommandée: 1920x1080px',
      'imageUpload.recommendedSize.description': ' - Taille recommandée: 800x600px',
      'imageUpload.cropInfo': "Vous pourrez recadrer l'image avant l'upload",
      
      // Image Crop Dialog
      'imageCrop.title': "Recadrer l'image",
      'imageCrop.cancel': 'Annuler',
      'imageCrop.confirm': 'Valider',
      'imageCrop.processing': 'Traitement...',

     // Success Pages
     'success.arcade.title': 'Paiement confirmé !',
     'success.arcade.verifying': 'Vérification du paiement...',
     'success.arcade.description': 'Merci pour votre achat ! Votre Arcade Spot a été réservé avec succès.',
     'success.arcade.subtitle': 'Vous pouvez gérer vos Arcade Spots depuis votre profil.',
     'success.arcade.viewArcadeSpots': 'Voir mes Arcade Spots',
     'success.arcade.verifyingDesc': 'Nous vérifions votre paiement. Cela peut prendre quelques instants...',
     'success.prestige.title': 'Prestige activé !',
     'success.prestige.verifying': 'Vérification du paiement...',
     'success.prestige.description': 'Félicitations ! Votre serveur a été mis à niveau avec le statut Prestige.',
     'success.prestige.subtitle': 'Votre serveur bénéficie maintenant d\'une visibilité accrue et d\'un badge distinctif.',
     'success.prestige.viewServers': 'Voir mes serveurs',
     'success.prestige.verifyingDesc': 'Nous vérifions votre paiement. Cela peut prendre quelques instants...',
     'success.common.returnHome': 'Retour à l\'accueil',
     'success.common.missingSession': 'Session ID manquante',

     // Admin Notifications
     'admin.server.reactivated': 'Serveur réactivé avec succès',
     'admin.server.reactivateError': 'Erreur lors de la réactivation du serveur',
     'admin.server.disabled': 'Serveur désactivé avec succès',
     'admin.server.disableError': 'Erreur lors de la désactivation du serveur',
     'admin.user.roleUpdated': 'Rôle utilisateur mis à jour avec succès',
     'admin.user.roleUpdateError': 'Erreur lors de la mise à jour du rôle',
     'admin.user.deleted': 'Utilisateur supprimé avec succès',
     'admin.user.deleteError': 'Erreur lors de la suppression',
     'admin.warning.added': 'Avertissement ajouté avec succès',
     'admin.warning.addError': 'Erreur lors de l\'ajout de l\'avertissement',
     'admin.warning.removed': 'Avertissement retiré avec succès',
     'admin.warning.removeError': 'Erreur lors du retrait de l\'avertissement',
     'admin.moderation.resolved': 'Action effectuée',
     'admin.moderation.resolveError': 'Erreur lors de la résolution du signalement',
     'admin.user.banned': 'Utilisateur banni avec succès',
     'admin.user.banError': 'Erreur lors du bannissement',
     'admin.user.unbanned': 'Utilisateur débanni avec succès',
     'admin.user.unbanError': 'Erreur lors du débannissement',
     'admin.ip.banned': 'IP bannié avec succès',
     'admin.ip.banError': 'Erreur lors du bannissement IP',
     'admin.ip.unbanned': 'IP débanni avec succès',
     'admin.ip.unbanError': 'Erreur lors du débannissement IP',

     // Notifications communes
     'notifications.auth.signedIn': 'Connexion réussie',
     'notifications.auth.welcome': 'Bienvenue sur RankServeur !',
     'notifications.auth.signedOut': 'Déconnexion',
     'notifications.auth.goodbye': 'À bientôt !',
     'notifications.auth.loginError': 'Erreur de connexion',
     'notifications.auth.signupError': 'Erreur d\'inscription',
     'notifications.auth.signupSuccess': 'Inscription réussie',
     'notifications.auth.signupDesc': 'Vérifiez votre email pour confirmer votre compte.',
     'notifications.auth.logoutError': 'Erreur de déconnexion',
     'notifications.error': 'Erreur',
     'notifications.success': 'Succès',
     'notifications.serverReassigned': 'Serveur réassigné',
     'notifications.serverReassignedDesc': 'Le propriétaire a été mis à jour.',
     'notifications.cannotReassign': 'Impossible de réassigner',
    
    // Arcade Spot Booking
    'arcade.title': 'Réserver un Arcade Spot',
    'arcade.subtitle': 'Réservez votre espace publicitaire premium',
    'arcade.position': 'Position',
    'arcade.selectPosition': 'Sélectionner une position',
    'arcade.selectDates': 'Sélectionner les dates',
    'arcade.uploadImage': 'Télécharger une image',
    'arcade.websiteUrl': 'URL du site web',
    'arcade.totalPrice': 'Prix total',
    'arcade.book': 'Réserver',
    'arcade.pricePerDay': '€/jour',
    'arcade.selectPositionFirst': 'Veuillez sélectionner une position',
    'arcade.selectDatesFirst': 'Veuillez sélectionner des dates',
    'arcade.uploadImageFirst': 'Veuillez télécharger une image',
    'arcade.bookingSuccess': 'Réservation effectuée avec succès !',
    'arcade.selectMonth': 'Tout le mois',
    'arcade.clearSelection': 'Effacer',
    
    // Terms of Sale
    'tos.title': 'Conditions Générales de Vente (CGV)',
    'tos.lastUpdate': 'Dernière mise à jour :',
    'tos.object': '1. Objet',
    'tos.objectText': 'Les présentes conditions régissent la vente d\'espaces publicitaires (bannières, encarts sponsorisés, mise en avant) sur le site https://rankserveur.com, immatriculé sous le SIREN 989 131 347.',
    'tos.services': '2. Services proposés',
    'tos.servicesText': 'Le client peut réserver un espace publicitaire pour une durée définie (au jour, à la semaine ou au mois). Les prix sont affichés sur le site et peuvent varier selon l\'emplacement et la durée.',

    // Arcade Spots (page d'accueil)
    'arcade.leftSpot': 'Arcade Spot Gauche',
    'arcade.centerSpot': 'Arcade Spot Centre', 
    'arcade.rightSpot': 'Arcade Spot Droite',
    'arcade.clickToReserve': 'Cliquez pour réserver',
    'arcade.bookSpot': 'Réserver un Arcade Spot',
    'arcade.bookingDescription': 'Réservez votre emplacement publicitaire même si tous les spots sont occupés',
     'arcade.suspended': 'Suspendu',
     'arcade.visit': 'Visiter',
     'arcade.reserved': 'Réservé',
     'arcade.startsOn': 'Démarre le',
    
    // Authentication Dialog
    'auth.title': 'AUTHENTIFICATION',
    'auth.signin': 'Connexion',
    'auth.signup': 'Inscription',
    'auth.reset': 'Oubli',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.username': 'Nom d\'utilisateur',
    'auth.emailPlaceholder': 'votre@email.com',
    'auth.passwordPlaceholder': '••••••••',
    'auth.usernamePlaceholder': 'VotreUsername',
    'auth.signInButton': 'Se connecter',
    'auth.signUpButton': 'S\'inscrire',
    'auth.forgotPassword': 'Mot de passe oublié ?',
    'auth.resetDescription': 'Entrez votre email pour recevoir un lien de réinitialisation',
    'auth.resetPassword': 'Réinitialiser',
    'auth.backToSignin': 'Retour à la connexion',
    'auth.or': 'ou',
    'auth.googleSignIn': 'Continuer avec Google',
    'auth.googleSignUp': 'S\'inscrire avec Google',
    'auth.googleError': 'Connexion Google impossible',
    'resetpw.checking': 'Vérification du lien...',
    'resetpw.title': 'Nouveau mot de passe',
    'resetpw.subtitle': 'Choisissez un nouveau mot de passe sécurisé',
    'resetpw.new': 'Nouveau mot de passe',
    'resetpw.confirm': 'Confirmer le mot de passe',
    'resetpw.submit': 'Mettre à jour le mot de passe',
    'resetpw.updating': 'Mise à jour...',
    'resetpw.invalidTitle': 'Lien invalide',
    'resetpw.invalidDesc': "Ce lien de réinitialisation n'est pas valide ou a expiré. Merci d'en demander un nouveau.",
    'resetpw.mismatch': 'Les mots de passe ne correspondent pas.',
    'resetpw.tooShort': 'Le mot de passe doit contenir au moins 6 caractères.',
    'resetpw.successTitle': 'Succès',
    'resetpw.successDesc': 'Votre mot de passe a été mis à jour avec succès.',
    'resetpw.errorTitle': 'Erreur',
    'resetpw.genericError': "Une erreur s'est produite lors de la mise à jour.",

      // Traductions pour les notifications d'authentification
      auth: {
        signInSuccess: 'Connexion réussie ! Bienvenue sur RankServeur.',
        signUpSuccess: 'Inscription réussie ! Bienvenue sur RankServeur.',
        passwordResetSent: 'Email de réinitialisation envoyé. Vérifiez votre boîte mail.',
        signInError: 'Erreur lors de la connexion. Vérifiez vos identifiants.',
        signUpError: 'Erreur lors de l\'inscription. Veuillez réessayer.',
        passwordResetError: 'Erreur lors de l\'envoi de l\'email de réinitialisation.',
        sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
        invalidCredentials: 'Identifiants invalides. Veuillez vérifier votre email et mot de passe.',
        userNotFound: 'Aucun compte trouvé avec cette adresse email.',
        weakPassword: 'Le mot de passe doit contenir au moins 6 caractères.',
        emailAlreadyExists: 'Un compte existe déjà avec cette adresse email.',
        emailNotConfirmed: 'Veuillez confirmer votre adresse email avant de vous connecter.'
      },
      // Admin Arena Import
      'admin.arena.title': 'Import Arena-Top100',
      'admin.arena.description': 'Importer des serveurs depuis arena-top100.com',
      'admin.arena.infoTitle': 'Informations importantes',
      'admin.arena.info1': 'Les serveurs seront créés avec is_approved=false (nécessitent validation)',
      'admin.arena.info2': 'Seuls les serveurs dont le jeu existe dans votre base seront importés',
      'admin.arena.info3': 'Les doublons (nom ou URL) seront automatiquement ignorés',
      'admin.arena.info4': 'Les images seront téléchargées et compressées automatiquement',
      'admin.arena.info5': 'Vous serez défini comme propriétaire de tous les serveurs importés',
      'admin.arena.importLimit': 'Nombre de serveurs à importer',
      'admin.arena.importAll': 'Tous les serveurs',
      'admin.arena.importCustom': 'Nombre personnalisé',
      'admin.arena.customLimitPlaceholder': 'Entrez le nombre de serveurs',
      'admin.arena.startImport': 'Lancer l\'importation',
      'admin.arena.importing': 'Importation en cours...',
      'admin.arena.importingDesc': 'Cela peut prendre quelques minutes',
      'admin.arena.importSuccess': 'Importation terminée',
      'admin.arena.importError': 'Erreur lors de l\'importation',
      'admin.arena.serversImported': 'serveurs importés',
      'admin.arena.total': 'Total',
      'admin.arena.imported': 'Importés',
      'admin.arena.skipped': 'Ignorés',
      'admin.arena.errors': 'Erreurs',
      'admin.arena.details': 'Détails de l\'importation',
      'admin.arena.game': 'Jeu',
      'admin.arena.slug': 'Slug',
      // Pages légales
      legal: {
        termsOfService: 'Conditions d\'Utilisation',
        termsOfSale: 'Conditions de Vente',
        privacyPolicy: 'Politique de Confidentialité',
        lastUpdated: 'Dernière mise à jour :'
      },
      // Traductions de l'interface d'authentification
      'auth.signInTitle': 'Connexion',
      'auth.signUpTitle': 'Inscription',
     'arcade.booking.imageFormat': '(format optimal: 512x288px)',
     'arcade.booking.title': 'Réserver un Arcade Spot',
     'arcade.booking.positionLabel': 'Position Arcade Spot *',
     'arcade.booking.imageLabel': 'Image publicitaire *',
     'arcade.booking.option1': 'Option 1: Upload d\'image',
    'arcade.booking.maxSize': '(max 5MB)',
    'arcade.booking.noFileChosen': 'Aucun fichier choisi',
    'arcade.booking.or': 'OU',
    'arcade.booking.option2': 'Option 2: URL d\'image',
    'arcade.booking.urlPlaceholder': 'https://exemple.com/image.jpg',
    'arcade.booking.websiteLabel': 'URL de destination (optionnel)',
    'arcade.booking.websitePlaceholder': 'https://votre-site.com',
    'arcade.booking.selectPosition': 'Sélectionnez une position',
    'arcade.booking.selectDates': 'Sélectionnez vos jours * (cliquez pour sélectionner/désélectionner)',
    'arcade.booking.daysSelected': 'Jours sélectionnés',
    'arcade.booking.positionLeft': 'Position Gauche',
    'arcade.booking.positionCenter': 'Position Centre',
    'arcade.booking.positionRight': 'Position Droite',
    'arcade.booking.addAdmin': 'Ajouter (Admin)',
    'arcade.booking.proceedPayment': 'Procéder au paiement',
    'arcade.booking.totalPrice': 'Prix total',
    'arcade.booking.days': 'jour(s)',
    'arcade.booking.processing': 'Traitement...',
     'arcade.booking.preview': 'Aperçu (512x288px recommandé)',
    
    // Sky Spot
    'skySpot.title': 'Sky Spot',
    'skySpot.clickToReserve': 'Cliquez pour réserver',
    'skySpot.bookSpot': 'Réserver le Sky Spot',
    'skySpot.bookingDescription': 'Publicité verticale 160x600px',
    'skySpot.suspended': 'Suspendu',
    'skySpot.visit': 'Visiter',
    'skySpot.reserved': 'Réservé',
    'skySpot.startsOn': 'Démarre le',
    'skySpot.booking.title': 'Réserver le Sky Spot',
    'skySpot.booking.selectDates': 'Sélectionnez vos jours * (cliquez pour sélectionner/désélectionner)',
    'skySpot.booking.daysSelected': 'Jours sélectionnés',
    'skySpot.booking.imageLabel': 'Image publicitaire *',
    'skySpot.booking.imageFormat': '(format optimal: 160x600px)',
    'skySpot.booking.option1': 'Option 1: Upload d\'image',
    'skySpot.booking.maxSize': '(max 5MB)',
    'skySpot.booking.or': 'OU',
    'skySpot.booking.option2': 'Option 2: URL d\'image',
    'skySpot.booking.urlPlaceholder': 'https://exemple.com/image.jpg',
    'skySpot.booking.websiteLabel': 'URL de destination (optionnel)',
    'skySpot.booking.websitePlaceholder': 'https://votre-site.com',
    'skySpot.booking.totalPrice': 'Prix total',
    'skySpot.booking.days': 'jour(s)',
    'skySpot.booking.processing': 'Traitement...',
    'skySpot.booking.preview': 'Aperçu (160x600px recommandé)',
    'skySpot.booking.addAdmin': 'Ajouter (Admin)',
    'skySpot.booking.proceedPayment': 'Procéder au paiement',

    // Games List
    'games.availableGames': 'JEUX DISPONIBLES',
    'games.top': 'TOP',
    'games.categories': 'CATÉGORIES',
    'games.loadingGames': 'Chargement des jeux...',
    'games.errorLoading': 'Erreur lors du chargement des jeux',
    'games.noGamesAvailable': 'Aucun jeu disponible',
    'games.gamesCount': '{count} jeux disponibles',
    'games.viewAll': 'Voir tous les serveurs {gameName}',
    
    // Report Dialog
    'report.title': 'Signaler {type}',
    'report.titleServer': 'ce serveur',
    'report.titleComment': 'ce commentaire',
    'report.description': 'Aidez-nous à maintenir la qualité de la plateforme en signalant les contenus inappropriés.',
    'report.serverName': 'Signalement pour: {serverName}',
    'report.reasonLabel': 'Raison du signalement :',
    'report.reasons.inappropriate_content': 'Contenu inapproprié',
    'report.reasons.spam': 'Spam ou publicité',
    'report.reasons.fake_server': 'Serveur factice',
    'report.reasons.malicious_content': 'Contenu malveillant',
    'report.reasons.copyright': 'Violation de copyright',
    'report.reasons.other': 'Autre',
    'report.customReasonLabel': 'Précisez la raison :',
    'report.customReasonPlaceholder': 'Décrivez le problème...',
    'report.cancel': 'Annuler',
    'report.submit': 'Envoyer le signalement',
    'report.submitting': 'Envoi...',
    'report.selectReason': 'Veuillez sélectionner une raison',
    'report.specifyReason': 'Veuillez préciser la raison',
     'report.success': 'Signalement envoyé avec succès',
     'report.error': 'Erreur lors de l\'envoi du signalement',

     // Server Detail Page
     'serverDetail.backToServers': 'Retour aux serveurs',
     'serverDetail.vote': 'Voter pour ce serveur',
     'serverDetail.website': 'Site web',
     'serverDetail.discord': 'Discord',
     'serverDetail.aboutServer': 'À propos du serveur',
     'serverDetail.articlesTitle': 'Articles à la une de ce serveur',
     'serverDetail.noDescription': 'Aucune description disponible pour ce serveur.',
      'serverDetail.serverStats': 'Statistiques du serveur',
      'serverDetail.monthlyVotes': 'Votes du mois',
      'serverDetail.totalVotes': 'Votes total',
     'serverDetail.clicks': 'Clics',
     'serverDetail.rank': 'Rang',
     'serverDetail.addedOn': 'Ajouté le',
     'serverDetail.voteHistory': 'Historique des votes',
     'serverDetail.weeklyVotes': 'Votes de la semaine',
     'serverDetail.noVoteData': 'Pas encore de données de vote',
     'serverDetail.playerReviews': 'Avis des joueurs',
     'serverDetail.loginToReview': 'Connectez-vous pour laisser un avis',
     'serverDetail.addReview': 'Ajouter un avis',
     'serverDetail.yourRating': 'Votre note',
     'serverDetail.reviewComment': 'Votre commentaire',
     'serverDetail.reviewPlaceholder': 'Partagez votre expérience sur ce serveur...',
     'serverDetail.publishReview': 'Publier l\'avis',
     'serverDetail.noReviews': 'Aucun avis pour l\'instant. Soyez le premier à donner votre opinion !',
     'serverDetail.serverNotFound': 'Serveur introuvable',
     'serverDetail.serverNotFoundDesc': 'Ce serveur n\'existe pas ou a été supprimé.',
     'serverDetail.loadingServer': 'Chargement du serveur...',
     'serverDetail.connectToIP': 'Se connecter avec cette IP:',
     'serverDetail.copyIP': 'Copier l\'IP',
     'serverDetail.ipCopied': 'Copié !',
     'serverDetail.ipCopiedDesc': 'L\'adresse IP a été copiée dans le presse-papiers.',
     'serverDetail.reviewAdded': 'Avis ajouté !',
     'serverDetail.reviewAddedDesc': 'Votre avis a été publié avec succès.',
     'serverDetail.reviewError': 'Erreur',
     'serverDetail.reviewErrorDesc': 'Une erreur est survenue lors de l\'ajout de votre avis.',
     'serverDetail.commentRequired': 'Commentaire requis',
     'serverDetail.commentRequiredDesc': 'Veuillez saisir un commentaire.',
     'serverDetail.loginRequired': 'Connexion requise',
     'serverDetail.loginRequiredDesc': 'Vous devez être connecté pour laisser un avis.',
     'serverDetail.vote.nextVoteIn': 'Prochain vote dans',
     'serverDetail.votesToday': 'Votes aujourd\'hui',
     'serverDetail.version': 'Version',
     'serverDetail.gameType': 'Type de jeu',
     'serverDetail.tags': 'Tags',
     'serverDetail.connectToServer': 'Connexion au serveur',
     'serverDetail.voteEvolution': 'Évolution des votes (7 derniers jours)',
     'serverDetail.communityReviews': 'Avis de la communauté',
     'serverDetail.anonymousUser': 'Utilisateur anonyme',
     'serverDetail.noReviewsYet': 'Aucun avis pour le moment',
     'serverDetail.firstToShare': 'Soyez le premier à partager votre expérience !',
     'serverDetail.shareExperience': 'Partagez votre expérience',
      'serverDetail.publishing': 'Publication...',

      // Vote Dialog
      'vote.labelMinecraft': 'Pseudo Minecraft',
      'vote.labelDofus': 'Pseudo Dofus', 
      'vote.labelCS': 'Pseudo Counter-Strike',
      'vote.labelDefault': 'Pseudo de jeu',
      'vote.placeholderMinecraft': 'VotrePseudoMinecraft',
      'vote.placeholderDofus': 'VotrePseudoDofus',
      'vote.placeholderCS': 'VotrePseudoCS',
      'vote.placeholderDefault': 'VotrePseudo',
      'vote.securityVerification': 'Vérification de sécurité',
      'vote.captchaLoading': 'Chargement du captcha...',
      'vote.checkingActivity': 'Vérification de votre dernière activité...',
      'vote.cooldownMessage': '⏱ Vous pourrez revoter dans {time}.',
      'vote.voteButton': 'Voter',
      'vote.verifyingInProgress': 'Vérification en cours...',
     'vote.nicknameNotValidated': 'Le pseudo n\'est pas vérifié, vous pouvez entrer n\'importe quel nom.',
     'vote.savedPlayerName': 'Pseudo sauvegardé pour vos prochains votes',
     
     // Profile empty states & server management texts
     'profile.noArcadeSpots': 'Vous n\'avez aucune réservation d\'Arcade Spot pour le moment.',
     'profile.noReviews': 'Vous n\'avez laissé aucun avis pour le moment.',
     'profile.noPrestigePayments': 'Aucun paiement Prestige pour le moment.',
     'profile.noServersMessage': 'Vous n\'avez ajouté aucun serveur pour le moment.',
     'profile.serverHidden': 'Masqué',
     'profile.serverDisabledMessage': 'Ce serveur a été désactivé suite à un signalement ou une action administrative.',
     'profile.serverDisabledSub': 'Il n\'apparaît plus dans les classements et n\'est plus accessible aux visiteurs.',
     'profile.serverHiddenMessage': 'Ce serveur est masqué et n\'apparaît pas dans les classements publics.',
     'profile.serverHiddenSub': 'Vous pouvez le rendre visible à tout moment ci-dessous.',
     'profile.unknownGame': 'Jeu inconnu',
     'profile.prestigeLabel': 'PRESTIGE',
     'profile.prestigeDaysLeft': '{days}j restant',
     'profile.standardLabel': 'Standard',
     'profile.createdOn': 'Créé le',
     'profile.apiVote': 'API de Vote',
     'profile.webhooks': 'Webhooks',
     'profile.privilegedAction': 'Action Privilégiée',
     'profile.deactivatePrestigeConfirm': 'Désactiver le Prestige pour "{name}" ? Cette action est immédiate et réservée aux fondateurs/administrateurs.',
     'profile.activatePrestigeConfirm': 'Activer le Prestige pour "{name}" pendant 30 jours ? Cette action est réservée aux fondateurs/administrateurs.',
     'profile.cancel': 'Annuler',
     'profile.deactivate': 'Désactiver',
     'profile.activate': 'Activer',
     'profile.arcadeSpotPosition': 'Arcade Spot Position',
     'profile.statusActive': 'Actif',
     'profile.statusPending': 'En attente',
     'profile.statusExpired': 'Expiré',
     'profile.serverLabel': 'Serveur :',
     'profile.deletedServerLabel': 'Serveur supprimé',
     'profile.dateFrom': 'Du',
     'profile.dateTo': 'au',
     'profile.viewWebsite': 'Voir le site web',
     'profile.deleteReview': 'Supprimer l\'avis',
     'profile.deleteReviewConfirm': 'Êtes-vous sûr de vouloir supprimer cet avis ? Cette action est irréversible.',
     'profile.delete': 'Supprimer',
     'profile.prestigePaymentTitle': 'Prestige',
     'profile.statusPaid': 'Payé',
     'profile.daysCount': '{days} jour',
     'profile.daysCountPlural': '{days} jours',
     'profile.expiresOn': 'Expire le',
     'profile.upgradeToPrestige': 'Upgrade Prestige',
     'profile.disablePrestige': 'Désactiver Prestige (Privilège)',
     'profile.enablePrestige': 'Activer Prestige (Privilège)',
     'profile.addFirstServer': 'Ajoutez votre premier serveur pour commencer !',
     
     // Visibility control
     'visibility.visible': 'Visible',
     'visibility.hidden': 'Masqué',
     'visibility.hideServer': 'Masquer le serveur',
     'visibility.showServer': 'Rendre visible le serveur',
     'visibility.hideServerTitle': 'Masquer le serveur',
     'visibility.showServerTitle': 'Rendre le serveur visible',
     'visibility.hideConfirm': 'Êtes-vous sûr de vouloir masquer "{name}" ?',
     'visibility.hideWarning': 'Le serveur disparaîtra du classement public mais restera dans votre gestion.',
     'visibility.showConfirm': 'Êtes-vous sûr de vouloir rendre "{name}" visible ?',
     'visibility.showInfo': 'Le serveur apparaîtra dans le classement public.',
     'visibility.slotsUsed': 'Vous utilisez {used} sur {max} serveurs autorisés.',
     'visibility.hide': 'Masquer',
     'visibility.show': 'Rendre visible',
     'visibility.limitReached': 'Limite atteinte',

      'vote.error.pseudoLength': 'Le pseudo doit contenir entre 2 et 50 caractères',
      'vote.error.invalidCharacters': 'Caractères non autorisés détectés dans le pseudo',
      'vote.error.loading': 'Chargement en cours, veuillez patienter...',
      'vote.error.captchaRequired': 'Veuillez compléter la vérification de sécurité',
      'vote.dialogTitle': 'Voter pour {serverName}',

      // Add Server Page
    'addServerPage.title': 'AJOUTER UN SERVEUR',
    'addServerPage.subtitle': 'Partagez votre serveur avec la communauté',
    'addServerPage.addMyServer': 'AJOUTER MON SERVEUR',
    'addServerPage.serverManagement': 'Gestion des serveurs',
    'addServerPage.visibleServers': 'Serveurs visibles',
    'addServerPage.available': 'Disponibles',
    'addServerPage.baseLimit': 'Limite de base',
    'addServerPage.servers': 'serveurs',
    'addServerPage.prestigeTip': 'Astuce Prestige',
    'addServerPage.prestigeDescription': 'Activez le Prestige sur un serveur pour débloquer +1 emplacement serveur supplémentaire !',
    'addServerPage.limitReached': 'Vous avez atteint votre limite de',
    'addServerPage.prestigeBonus': 'Ce serveur sera créé grâce à votre bonus Prestige ! Il sera automatiquement masqué si votre Prestige expire.',

     // Leaderboard Page
     'leaderboard.title': 'CLASSEMENTS VOTEURS',
     'leaderboard.subtitle': 'Découvrez les meilleurs voteurs de la communauté',
     'leaderboard.addMyServer': 'Ajouter mon serveur',
     'leaderboard.loading': 'Chargement...',
     'leaderboard.noVoters': 'Aucun voteur trouvé',
     'leaderboard.topMonthly': 'Top Voteurs du Mois',
     'leaderboard.topWeekly': 'Top Voteurs de la Semaine',
     'leaderboard.topDaily': 'Top Voteurs du Jour',
     'leaderboard.registeredUser': 'Utilisateur enregistré',
     'leaderboard.guestVoter': 'Voteur invité',

    // Filter Section
    'filter.filterByGame': 'Filtrer par Jeu',
    'filter.allGames': 'Tous les Jeux',
    'filter.sortBy': 'Trier par:',
    'filter.topVotes': 'Top Votes',
    'filter.mostPopular': 'Plus Populaires',
    'filter.recent': 'Récents',
    'filter.activeFilter': 'Filtrage actif:',
    'filter.clickToRemove': 'Cliquez pour supprimer le filtre',
    'addServerPage.whyAdd': 'Pourquoi ajouter votre serveur ?',
    'addServerPage.benefit1': 'Augmentez votre visibilité auprès de milliers de joueurs',
    'addServerPage.benefit2': 'Recevez des votes quotidiens de la communauté',
    'addServerPage.benefit3': 'Personnalisez l\'apparence avec des couleurs et images',
    'addServerPage.benefit4': 'Accédez aux statistiques détaillées',
    'addServerPage.prestigeOption': 'Option Prestige',
    'addServerPage.prestigeTitle': 'PRESTIGE',
    'addServerPage.prestigeFeature1': 'Position prioritaire dans le classement et badge exclusif',
    'addServerPage.prestigeFeature2': 'Mise en avant avec couleurs personnalisées et effets visuels',
    'addServerPage.prestigeFeature3': '+1 serveur supplémentaire ajouté à votre limite',
    'addServerPage.prestigeFeature4': 'Le serveur bonus est automatiquement masqué si le Prestige expire',
    'addServerPage.loginRequired': 'CONNEXION REQUISE',
    'addServerPage.mustBeLoggedIn': 'Vous devez être connecté pour ajouter un serveur',
    'addServerPage.signupNow': 'S\'inscrire maintenant',
    'addServerPage.login': 'Se connecter',


    // Legal Pages  
    'legal.termsOfService': 'Conditions Générales d\'Utilisation',
    'legal.termsOfSale': 'Conditions Générales de Vente (CGV)',
    'legal.privacyPolicy': 'Politique de Confidentialité',
    'legal.lastUpdated': 'Dernière mise à jour :',

    // Game Category Pages
    'gameCategory.servers': 'SERVEURS {gameName}',
    'gameCategory.updatedRealTime': 'Classement mis à jour en temps réel',
    'gameCategory.noServersFound': 'Aucun serveur trouvé',

    // Propose Tag Dialog
    'proposeTag.title': 'Proposer un nouveau tag',
    'proposeTag.description': 'Proposez un nouveau tag qui sera examiné par les administrateurs avant d\'être ajouté.',
    'proposeTag.nameLabel': 'Nom du tag *',
    'proposeTag.namePlaceholder': 'Ex: Hardcore, PvP, Roleplay...',
    'proposeTag.descriptionLabel': 'Description (optionnelle)',
    'proposeTag.descriptionPlaceholder': 'Décrivez brièvement ce que représente ce tag...',
    'proposeTag.colorLabel': 'Couleur',
    'proposeTag.preview': 'Aperçu',
    'proposeTag.submitting': 'Envoi...',
    'proposeTag.submit': 'Proposer',

    // Vote System
    'vote.alreadyVoted': 'Vous avez déjà voté !',
    'vote.waitTime': 'Vous pourrez voter à nouveau dans {hours} heure(s).',
    'vote.success': 'Vote enregistré avec succès !',
    'vote.failed': 'Erreur lors du vote.',
    'vote.every2hours': 'Votez toutes les 2 heures',
    'vote.nextVoteIn': 'Prochain vote dans',
	
	// Serveur List – Pagination
    'serverList.page': 'Page {current} sur {total}',
    'serverList.display': '(Affichage de {from} à {to} sur {count} serveurs)',

    // Prestige Pass / Battle Pass
    'prestigePass.title': 'Prestige Pass',
    'prestigePass.subtitle': 'Gagnez des jours de Prestige gratuits grâce à vos votes',
    'prestigePass.free': 'GRATUIT',
    'prestigePass.availableTokens': 'Jours disponibles',
    'prestigePass.totalVotes': 'Votes totaux',
    'prestigePass.currentMilestone': 'Palier actuel',
    'prestigePass.nextMilestone': 'Prochain palier',
    'prestigePass.votesRemaining': 'votes restants',
    'prestigePass.myTokens': 'Mes jours de Prestige',
    'prestigePass.noTokensYet': 'Aucun jour de Prestige pour le moment',
    'prestigePass.earnTokensInfo': 'Accumulez 1000 votes pour débloquer 1 jour de Prestige gratuit',
    'prestigePass.milestone': 'Palier',
    'prestigePass.earnedAt': 'Gagné à',
    'prestigePass.votes': 'votes',
    'prestigePass.applyToken': 'Utiliser',
    'prestigePass.used': 'Utilisé',
    'prestigePass.usedOn': 'Utilisé le',
    'prestigePass.applyTokenTitle': 'Appliquer un jour de Prestige',
    'prestigePass.applyTokenDescription': 'Choisissez le serveur sur lequel appliquer ce jour de Prestige. Il s\'ajoutera au Prestige existant si le serveur en possède déjà.',
    'prestigePass.selectServer': 'Sélectionnez un serveur',
    'prestigePass.noServers': 'Aucun serveur disponible',
    'prestigePass.confirm': 'Confirmer',

    // Admin Settings
    'admin.settings.registrationEnabled': 'Inscriptions ouvertes',
    'admin.settings.registrationEnabledDesc': 'Permet aux nouveaux utilisateurs de s\'inscrire',
    'admin.imageOptimizer.title': 'Optimiseur d\'Images',
    'admin.imageOptimizer.description': 'Optimisez toutes les images existantes en WebP pour améliorer les performances SEO',
    'admin.imageOptimizer.warning': 'Cette opération peut prendre plusieurs minutes. Toutes les images seront converties en format WebP optimisé.',
    'admin.imageOptimizer.startOptimization': 'Lancer l\'Optimisation',
    'admin.imageOptimizer.optimizing': 'Optimisation en cours...',
    'admin.imageOptimizer.total': 'Total',
    'admin.imageOptimizer.optimized': 'Optimisées',
    'admin.imageOptimizer.skipped': 'Ignorées',
    'admin.imageOptimizer.failed': 'Échecs',
    'admin.imageOptimizer.progress': 'Progression',
    'admin.imageOptimizer.errors': 'Erreurs',
    'admin.imageOptimizer.moreErrors': '... et {{count}} erreurs supplémentaires',

    // Competitive Events
    'competitive_events_admin_menu': 'Événements Compétitifs',
    'competitive_event_filter_title': 'Événement Compétitif en cours',
    'competitive_event_filter_button': 'Voir classement événement',
    'competitive_event_filter_active': 'Classement événement',
    'competitive_upcoming_title': 'Événement à venir',
    'competitive_events_title': 'Événement Compétitif',
    'competitive_events_active': 'Événement en cours',
    'competitive_events_upcoming': 'À venir',
    'competitive_events_ended': 'Terminé',
    'competitive_events_objective_progress': 'Progression vers l\'objectif',
    'competitive_events_current_ranking': 'Classement actuel',
    'competitive_events_participants': 'participants',
    'competitive_events_winners': 'gagnants',
    'competitive_events_winner': 'Gagnant',
    'competitive_events_top_winners': 'Top {count} gagnent {days} jours de Prestige',
    'competitive_events_ended_success': 'Événement terminé - Objectif atteint !',
    'competitive_events_ended_failed': 'Événement terminé - Objectif non atteint',
    'competitive_events_objective_not_reached': 'Objectif non atteint - Pas de récompense',
    'competitive_events_no_reward': 'Pas de récompense',
    'competitive_events_prestige_days': 'jours de Prestige',
    'competitive_events_start_date': 'Début',
    'competitive_events_end_date': 'Fin',
    'competitive_events_event_name_all_games': 'Événement vote pour tous les jeux',
    'competitive_events_event_desc_all_games': 'Événement de votes compétitifs pour tous les jeux de la plateforme. Participez et remportez du Prestige en obtenant le plus de votes pour votre serveur !',
    'competitive_events_event_name_single_game': 'Événement vote pour {game}',
    'competitive_events_event_name_multi_games': 'Événement vote pour {count} jeux',
    'competitive_events_event_desc_single_game': 'Événement de votes compétitifs dédié aux serveurs {game}. Objectif : {targetVotes} votes pour remporter {prestigeDays} jours de Prestige !',
    'competitive_events_event_desc_multi_games': 'Événement de votes compétitifs pour {games}. Objectif : {targetVotes} votes pour remporter {prestigeDays} jours de Prestige !',
     'competitive_events_starts_in': 'Commence dans',

    // FAQ Section
    'faq.title': 'Questions Fréquentes',
    'faq.subtitle': 'Tout ce que vous devez savoir sur RankServeur',
    'faq.question1': 'Comment voter pour un serveur ?',
    'faq.answer1': 'Pour voter pour un serveur, il suffit de cliquer sur le bouton "Voter" sur la page du serveur. Vous pouvez voter une fois toutes les 2 heures par serveur. Vos votes aident à augmenter la visibilité du serveur dans notre classement.',
    'faq.question2': 'Qu\'est-ce que le Prestige Pass ?',
    'faq.answer2': 'Le Prestige Pass est un système de récompenses gratuit. Chaque 1000 votes que vous accumulez, vous débloquez 1 jour de Prestige que vous pouvez appliquer à vos serveurs préférés. Le Prestige met en avant les serveurs dans le classement.',
    'faq.question3': 'Comment ajouter mon serveur ?',
    'faq.answer3': 'Cliquez sur le bouton "Ajouter un serveur" dans le menu principal. Remplissez le formulaire avec les détails de votre serveur (nom, IP, description, logo). Votre serveur sera examiné par nos modérateurs avant d\'apparaître dans le classement.',
    'faq.question4': 'À quelle fréquence puis-je voter ?',
    'faq.answer4': 'Vous pouvez voter une fois toutes les 2 heures par serveur. Le compteur de temps d\'attente s\'affiche sur chaque page de serveur. Les votes sont réinitialisés chaque mois, offrant de nouvelles opportunités de classement.',
    'faq.question5': 'Que sont les événements compétitifs ?',
    'faq.answer5': 'Les événements compétitifs sont des compétitions temporaires où les serveurs s\'affrontent pour obtenir le plus de votes. Les gagnants reçoivent des jours de Prestige gratuits. Ces événements sont annoncés sur la page d\'accueil.',
    'faq.question6': 'Comment fonctionne le classement mensuel ?',
    'faq.answer6': 'Les votes de tous les serveurs sont réinitialisés le 1er de chaque mois. Cela offre des opportunités régulières aux nouveaux serveurs de gagner en visibilité et aux joueurs de découvrir de nouveaux serveurs.',

    // Admin Universal Importer
    'adminImport.title': 'Importation Universelle de Serveurs',
    'adminImport.description': 'Importez des serveurs depuis différentes sources',
    'adminImport.source': 'Source d\'importation',
    'adminImport.games': 'Jeux à importer ({count} sélectionnés)',
    'adminImport.loadGames': 'Charger les jeux',
    'adminImport.loading': 'Chargement...',
    'adminImport.servers': 'serveurs',
    'adminImport.limit': 'Nombre de serveurs à prévisualiser',
    'adminImport.preview': 'Prévisualiser les serveurs',
    'adminImport.previewing': 'Prévisualisation en cours...',
    'adminImport.selectGame': 'Veuillez sélectionner au moins un jeu',
    'adminImport.gamesLoaded': '{count} jeux disponibles',
    'adminImport.serversLoaded': '{count} serveurs chargés',
    'adminImport.noServersFound': 'Aucun serveur trouvé',
    'adminImport.errorLoading': 'Erreur lors du chargement',
    'adminImport.errorPreview': 'Erreur lors de la prévisualisation',
    'adminImport.errorImport': 'Erreur lors de l\'importation',

    // Admin App Versions
    'admin.accessDenied': 'Accès refusé',
    'admin.appVersions.title': 'Gestion des Versions',
    'admin.appVersions.newVersion': 'Nouvelle version',
    'admin.appVersions.editVersion': 'Modifier la version',
    'admin.appVersions.allVersions': 'Toutes les versions',
    'admin.appVersions.version': 'Version',
    'admin.appVersions.platform': 'Plateforme',
    'admin.appVersions.size': 'Taille',
    'admin.appVersions.file': 'Fichier',
    'admin.appVersions.orManualUrl': 'ou URL manuelle',
    'admin.appVersions.mandatory': 'Obligatoire',
    'admin.appVersions.active': 'Active',
    'admin.appVersions.create': 'Créer la version',
    'admin.appVersions.update': 'Mettre à jour',
    'admin.appVersions.uploadSuccess': 'Fichier uploadé avec succès',
    'admin.appVersions.uploadError': 'Erreur lors de l\'upload',
    'admin.appVersions.uploadLargeFileHint': 'Pour les gros fichiers .exe, un upload résumable est utilisé automatiquement.',
    'admin.appVersions.uploadExceededHint': 'Le fichier dépasse la limite autorisée ou l\'upload direct a été refusé. Réessayez avec le nouvel upload résumable.',
    'admin.appVersions.missingFields': 'Version et URL requis',
    'admin.appVersions.confirmDelete': 'Supprimer cette version ?',
    'admin.appVersions.noVersions': 'Aucune version publiée',
    'admin.appVersions.permanentUrl': 'Lien permanent',
    'admin.appVersions.permanentUrlExplanation': 'Le fichier est uploadé sous un nom fixe (latest.exe). Le lien ne change jamais, seul le contenu est remplacé.',
    'admin.appVersions.permanentUrlTitle': 'Lien permanent de téléchargement',
    'admin.appVersions.permanentUrlDesc': 'Chaque upload remplace le fichier latest.exe. Le launcher utilise toujours le même lien et vérifie le hash SHA-256 pour savoir s\'il doit mettre à jour.',
    'admin.appVersions.backupUrl': 'URL de backup (versionné)',
    'admin.appVersions.copied': 'Copié !',
    'admin.appVersions.pythonExampleTitle': 'Intégration Python (Launcher)',
   'admin.appVersions.pythonExampleDesc': 'Exemple de code pour vérifier et télécharger les mises à jour depuis votre launcher Python. Le hash SHA-256 permet de valider l\'intégrité du fichier.',
   'admin.appVersions.setActive': 'Activer',
   'admin.appVersions.confirmActivate': 'Activer cette version ? Le fichier latest.exe sera remplacé par cette version (rollback).',
   'admin.appVersions.activated': 'Version activée',
   'admin.appVersions.activateError': 'Erreur d\'activation',
   'admin.appVersions.activateNoBackup': 'Aucun fichier versionné disponible pour cette version (rollback impossible).',

    // Admin Discord Logs
    'admin.discordLogs.title': 'Logs Discord & audit',
    'admin.discordLogs.subtitle': 'Historique complet des actions Discord : rétrogradations, promotions, DM, erreurs API, commandes /link et /licence.',
    'admin.discordLogs.loading': 'Chargement…',
    'admin.discordLogs.accessDenied': 'Accès refusé',
    'admin.discordLogs.accessDeniedDesc': 'Cette page est réservée aux administrateurs.',
    'admin.discordLogs.runSync': 'Lancer la sync',
    'admin.discordLogs.runReconcile': 'Audit one-shot Discord',
    'admin.discordLogs.purge90': 'Purger > 90 jours',
    'admin.discordLogs.purgeAll': 'Tout purger',
    'admin.discordLogs.purgeConfirmTitle': 'Confirmer la purge',
    'admin.discordLogs.purgeConfirmDesc90': 'Supprimer toutes les entrées de plus de 90 jours ? Action irréversible.',
    'admin.discordLogs.purgeAllConfirmTitle': '⚠️ Tout supprimer',
    'admin.discordLogs.purgeAllConfirmDesc': 'Supprimer la totalité de l\'historique Discord. Action irréversible.',
    'admin.discordLogs.confirmPurge': 'Purger',
    'admin.discordLogs.confirmPurgeAll': 'Tout supprimer',
    'admin.discordLogs.cancel': 'Annuler',
    'admin.discordLogs.last24h': 'Dernières 24h',
    'admin.discordLogs.last7d': '7 derniers jours',
    'admin.discordLogs.last30d': '30 derniers jours',
    'admin.discordLogs.total': 'Total',
    'admin.discordLogs.filters': 'Filtres',
    'admin.discordLogs.actionType': 'Type d\'action',
    'admin.discordLogs.source': 'Source',
    'admin.discordLogs.status': 'Statut',
    'admin.discordLogs.all': 'Tous',
    'admin.discordLogs.success': 'Succès',
    'admin.discordLogs.failed': 'Échec',
    'admin.discordLogs.searchPlaceholder': 'Rechercher Discord ID, pseudo, licence…',
    'admin.discordLogs.recentEntries': 'Entrées récentes',
    'admin.discordLogs.noResults': 'Aucun résultat',
    'admin.discordLogs.col.date': 'Date',
    'admin.discordLogs.col.action': 'Action',
    'admin.discordLogs.col.source': 'Source',
    'admin.discordLogs.col.status': 'Statut',
    'admin.discordLogs.col.discord': 'Discord',
    'admin.discordLogs.col.license': 'Licence',
    'admin.discordLogs.col.details': 'Détails / erreur',
    'admin.discordLogs.viewMetadata': 'Voir métadonnées',
    'admin.discordLogs.exclusions.title': 'Exclusions de purge',
    'admin.discordLogs.exclusions.subtitle': 'Les Discord IDs listés ici sont préservés lors de toute purge (même « Tout purger »).',
    'admin.discordLogs.exclusions.discordIdLabel': 'Discord ID',
    'admin.discordLogs.exclusions.discordIdPlaceholder': '123456789012345678',
    'admin.discordLogs.exclusions.usernameLabel': 'Pseudo (optionnel)',
    'admin.discordLogs.exclusions.noteLabel': 'Note (optionnelle)',
    'admin.discordLogs.exclusions.add': 'Ajouter',
    'admin.discordLogs.exclusions.remove': 'Retirer',
    'admin.discordLogs.exclusions.empty': 'Aucune exclusion configurée.',
    'admin.discordLogs.exclusions.addedOn': 'Ajouté le',

    // Common
    'common.optional': 'optionnel',

    // Admin License Pricing
    'admin.pricing.title': 'Tarifs des Licences',
    'admin.pricing.subtitle': 'Configurez les prix appliqués aux paiements Stripe et affichés dans Discord.',
    'admin.pricing.licensesTitle': 'Tarifs Stripe (Licences logicielles)',
    'admin.pricing.monthly': 'Tarif mensuel',
    'admin.pricing.monthlyHelp': 'Prix appliqué pour une licence d\'1 mois.',
    'admin.pricing.yearly': 'Tarif annuel',
    'admin.pricing.yearlyHelp': 'Prix appliqué pour une licence d\'1 an.',
    'admin.pricing.save': 'Enregistrer',
    'admin.pricing.saved': 'Tarifs enregistrés',
    'admin.pricing.savedDesc': 'Les nouveaux tarifs sont appliqués immédiatement aux futurs achats.',
    'admin.pricing.invalidAmount': 'Montant invalide',
    'admin.pricing.warning': 'Les paiements déjà initiés conservent leur ancien tarif. Seuls les futurs paiements utiliseront ces nouveaux montants.',

    // Admin Discord Tickets
    'admin.tickets.title': 'Tickets Discord',
    'admin.tickets.subtitle': 'Configurez la création automatique de salons de support privés.',
    'admin.tickets.configTitle': 'Configuration du système de tickets',
    'admin.tickets.enabled': 'Activer les tickets',
    'admin.tickets.enabledHelp': 'Si désactivé, la commande /ticket open ne créera plus de salon.',
    'admin.tickets.guildId': 'ID du serveur Discord (Guild ID)',
    'admin.tickets.categoryId': 'ID de la catégorie',
    'admin.tickets.categoryHelp': 'Catégorie Discord dans laquelle les salons tickets seront créés.',
    'admin.tickets.staffRoleId': 'ID du rôle staff',
    'admin.tickets.staffRoleHelp': 'Rôle qui aura automatiquement accès aux nouveaux tickets.',
    'admin.tickets.intro': 'Message d\'introduction',
    'admin.tickets.introHelp': 'Variables disponibles : {user}, {user_id}, {staff_role}, {purchase_token}.',
    'admin.tickets.botPerm': 'Le bot doit avoir les permissions « Gérer les salons » et « Voir les salons » dans la catégorie configurée.',
    'admin.tickets.save': 'Enregistrer',
    'admin.tickets.permsRequiredIntro': 'Permissions Discord requises pour le bot :',
    'admin.tickets.permsWhere': 'Où les configurer :',
    'admin.tickets.registerCmdTitle': 'Commandes Discord (slash)',
    'admin.tickets.registerCmdHelp': 'Enregistre/met à jour les commandes /licence et /ticket sur votre serveur Discord. À cliquer après chaque modification de la liste des commandes.',
    'admin.tickets.registerCmdBtn': 'Enregistrer les commandes Discord',
    'admin.tickets.commandsRegistered': 'Commandes Discord enregistrées',
    'admin.tickets.saved': 'Configuration enregistrée',

    // Admin Test Stripe
    'admin.testStripe.title': 'Test fulfillment Stripe',
    'admin.testStripe.subtitle': 'Simule un paiement Stripe réussi pour tester la livraison de licence (sans débit réel).',
    'admin.testStripe.formTitle': 'Paramètres du test',
    'admin.testStripe.discordUserId': 'ID Discord de l\'utilisateur',
    'admin.testStripe.discordHelp': 'ID Discord (snowflake) qui recevra le DM de livraison.',
    'admin.testStripe.discordUsername': 'Nom Discord',
    'admin.testStripe.licenseType': 'Type de licence',
    'admin.testStripe.monthly': '1 mois',
    'admin.testStripe.yearly': '1 an',
    'admin.testStripe.run': 'Lancer le test',
    'admin.testStripe.success': 'Test réussi',
    'admin.testStripe.successDesc': 'Licence créée et DM envoyé à l\'utilisateur Discord.',
    'admin.testStripe.missingUser': 'L\'ID Discord est requis',
    'admin.testStripe.noCharge': 'Aucun débit réel n\'est effectué. Cette action simule uniquement le webhook Stripe pour tester la création de licence et l\'envoi du DM.',

    // Admin – License Purchases
    'admin.licensePurchases.title': 'Licences achetées',
    'admin.licensePurchases.subtitle': 'Suivi des commandes Discord (/licence buy) et livraisons.',
    'admin.licensePurchases.configMethods': 'Méthodes de paiement',
    'admin.licensePurchases.search': 'Rechercher',
    'admin.licensePurchases.method': 'Méthode',
    'admin.licensePurchases.empty': 'Aucune commande.',
    'admin.licensePurchases.fulfillTitle': 'Livrer la licence',
    'admin.licensePurchases.externalRef': 'Référence externe (ID transaction)',
    'admin.licensePurchases.adminNotes': 'Notes administrateur',
    'admin.licensePurchases.fulfillHelp': 'Génère la clé de licence + lien de téléchargement (72h, 5 utilisations max), met la commande en "fulfilled" et envoie automatiquement le tout par DM Discord à l\'acheteur. À utiliser UNIQUEMENT après vérification du paiement.',
    'admin.licensePurchases.confirmFulfill': 'Livrer & envoyer DM',
    'admin.licensePurchases.cancel': 'Annuler',
    'admin.licensePurchases.cancelTitle': 'Annuler la commande',
    'admin.licensePurchases.cancelHelp': 'Marque la commande comme "cancelled". À utiliser si le paiement n\'a jamais été reçu ou si l\'acheteur abandonne. Aucun DM n\'est envoyé. Action irréversible.',
    'admin.licensePurchases.linkTitle': 'Nouveau lien de téléchargement',
    'admin.licensePurchases.linkHelp': 'Génère un lien de téléchargement neuf (l\'ancien devient inutilisable). Vous pouvez l\'envoyer en DM Discord et/ou le copier pour le transmettre vous-même.',
    'admin.licensePurchases.linkValidity': 'Validité (heures)',
    'admin.licensePurchases.linkMaxUses': 'Nombre de téléchargements autorisés',
    'admin.licensePurchases.linkSendDm': 'Envoyer aussi le DM Discord',
    'admin.licensePurchases.linkGenerate': 'Générer le lien',
    'admin.licensePurchases.linkResult': 'Lien généré',
    'admin.licensePurchases.linkCopy': 'Copier le lien',
    'admin.licensePurchases.linkExpires': 'Expire le',
    'admin.licensePurchases.cancelReason': 'Raison de l\'annulation (interne)',
    'admin.licensePurchases.confirmCancel': 'Confirmer l\'annulation',
    'admin.licensePurchases.cancelled': 'Commande annulée',
    'admin.licensePurchases.cancelOnlyPending': 'Seules les commandes non livrées peuvent être annulées.',

    // Admin – Licenses page – Discord buy toggle
    'admin.licenses.buyToggle.title': 'Commande Discord /licence buy',
    'admin.licenses.buyToggle.description': 'Active ou désactive la possibilité pour les utilisateurs Discord d\'acheter une licence via /licence buy. Quand désactivée, ils reçoivent un message expliquant que l\'achat est temporairement indisponible.',
    'admin.licenses.buyToggle.enabled': 'Activée — les utilisateurs peuvent acheter',
    'admin.licenses.buyToggle.disabled': 'Désactivée — achats bloqués',
    'admin.licenses.buyToggle.updated': 'Paramètre mis à jour',

    // Admin – Payment methods – auto-ticket info
    'admin.paymentMethods.autoTicketInfo': '✅ Auto-ticket activé : depuis la sélection d\'une méthode manuelle (PayPal, Skrill, Revolut, Coinbase) dans /licence buy, un ticket Discord est créé automatiquement avec le contexte de la commande pour le staff. L\'acheteur n\'a plus besoin de taper /ticket open.',

    // Admin — VM (Discord /vm buy)
    'admin.vm.title': 'VM — Discord /vm buy',
    'admin.vm.subtitle': 'Gérer le flux d\'achat VM (paiement PayPal manuel uniquement)',
    'admin.vm.enabledLabel': 'Commande /vm buy activée',
    'admin.vm.enabledHelp': 'Quand désactivée, les utilisateurs Discord reçoivent un message indiquant que l\'achat VM est temporairement indisponible.',
    'admin.vm.priceMember': 'Prix Membre Pixfus',
    'admin.vm.priceMemberHelp': 'Tarif appliqué automatiquement aux utilisateurs ayant le rôle Discord Membre Pixfus.',
    'admin.vm.priceStandard': 'Prix Standard',
    'admin.vm.priceStandardHelp': 'Tarif appliqué aux utilisateurs sans le rôle Membre (ou en cas d\'échec de détection).',
    'admin.vm.productLabel': 'Nom du produit',
    'admin.vm.productLabelHelp': 'Affiché dans le ticket et dans les instructions de paiement.',
    'admin.vm.paypalEmail': 'E-mail PayPal',
    'admin.vm.paypalEmailHelp': 'Adresse e-mail PayPal communiquée à l\'acheteur dans le ticket pour le paiement manuel "Entre proches".',
    'admin.vm.staffTitle': 'Staff Discord assigné aux tickets VM',
    'admin.vm.staffHelp': 'UIDs Discord à inviter automatiquement dans chaque ticket /vm buy (ils auront accès au salon et seront mentionnés).',
    'admin.vm.staffAdd': 'Ajouter',
    'admin.vm.staffEmpty': 'Aucun staff Discord assigné — seul le rôle staff global recevra les notifications.',
    'admin.vm.staffIdInvalid': 'UID Discord invalide (chiffres uniquement)',
    'admin.vm.staffIdDuplicate': 'Cet UID est déjà dans la liste',
    'admin.vm.roleInfoTitle': 'Détection du rôle Membre',
    'admin.vm.roleInfoBody': 'Le rôle Discord utilisé pour identifier les Membres Pixfus est défini par le secret edge function DISCORD_ROLE_MEMBRES_ID. Si le rôle n\'est pas trouvé sur l\'utilisateur, le prix standard est appliqué automatiquement.',
    'admin.vm.save': 'Enregistrer',
    'admin.vm.reload': 'Recharger',
    'admin.vm.saved': 'Paramètres VM enregistrés',
    'admin.vm.saveError': 'Erreur lors de l\'enregistrement',
    'admin.vm.loadError': 'Erreur lors du chargement',
    'admin.seo.discordAlertsLabel': 'Alertes Discord',
    'admin.seo.discordAlertsEnabled': 'Alertes Discord GSC activées',
    'admin.seo.discordAlertsDisabled': 'Alertes Discord GSC désactivées',
    'admin.seo.discordToggleError': 'Impossible de mettre à jour le réglage',

   },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.topVoters': 'Top Voters',
    'nav.favorites': 'Favorites',
    'nav.myVotes': 'My Votes',
    'nav.creatorTools': 'Creator Tools',
    'nav.profile': 'Profile',
    'nav.admin': 'Administration',
    'nav.logout': 'Logout',
    'nav.login': 'Login',
    'nav.signup': 'Sign Up',
    'nav.addServer': 'Add Server',

    // Success Pages
    'success.arcade.title': 'Payment confirmed!',
    'success.arcade.verifying': 'Verifying payment...',
    'success.arcade.description': 'Thank you for your purchase! Your Arcade Spot has been successfully reserved.',
    'success.arcade.subtitle': 'You can manage your Arcade Spots from your profile.',
    'success.arcade.viewArcadeSpots': 'View my Arcade Spots',
    'success.arcade.verifyingDesc': 'We are verifying your payment. This may take a few moments...',
    'success.prestige.title': 'Prestige activated!',
    'success.prestige.verifying': 'Verifying payment...',
    'success.prestige.description': 'Congratulations! Your server has been upgraded with Prestige status.',
    'success.prestige.subtitle': 'Your server now benefits from increased visibility and a distinctive badge.',
    'success.prestige.viewServers': 'View my servers',
    'success.prestige.verifyingDesc': 'We are verifying your payment. This may take a few moments...',
    'success.common.returnHome': 'Return home',
    'success.common.missingSession': 'Missing session ID',

    // Admin Notifications  
    'admin.server.reactivated': 'Server reactivated successfully',
    'admin.server.reactivateError': 'Error reactivating server',
    'admin.server.disabled': 'Server disabled successfully',
    'admin.server.disableError': 'Error disabling server',
    'admin.user.roleUpdated': 'User role updated successfully',
    'admin.user.roleUpdateError': 'Error updating role',
    'admin.user.deleted': 'User deleted successfully',
    'admin.user.deleteError': 'Error deleting user',
    'admin.warning.added': 'Warning added successfully',
    'admin.warning.addError': 'Error adding warning',
    'admin.warning.removed': 'Warning removed successfully',
    'admin.warning.removeError': 'Error removing warning',
    'admin.moderation.resolved': 'Action completed',
    'admin.moderation.resolveError': 'Error resolving report',
    'admin.user.banned': 'User banned successfully',
    'admin.user.banError': 'Error banning user',
    'admin.user.unbanned': 'User unbanned successfully',
    'admin.user.unbanError': 'Error unbanning user',
    'admin.ip.banned': 'IP banned successfully',
    'admin.ip.banError': 'Error banning IP',
    'admin.ip.unbanned': 'IP unbanned successfully',
    'admin.ip.unbanError': 'Error unbanning IP',

    // Common Notifications
    'notifications.auth.signedIn': 'Login successful',
    'notifications.auth.welcome': 'Welcome to RankServeur!',
    'notifications.auth.signedOut': 'Logout',
    'notifications.auth.goodbye': 'See you soon!',
    'notifications.auth.loginError': 'Login error',
    'notifications.auth.signupError': 'Signup error',
    'notifications.auth.signupSuccess': 'Signup successful',
    'notifications.auth.signupDesc': 'Check your email to confirm your account.',
    'notifications.auth.logoutError': 'Logout error',
    'notifications.error': 'Error',
    'notifications.success': 'Success',
    'notifications.serverReassigned': 'Server reassigned',
    'notifications.serverReassignedDesc': 'The owner has been updated.',
    'notifications.cannotReassign': 'Cannot reassign',
    
    // Hero Section
    'hero.title': 'Discover the Best Gaming Servers',
    'hero.title2': 'PRIVATE SERVERS',
    'hero.subtitle': 'Vote for your favorite servers and climb the rankings!',
    'hero.subtitle2': 'A community platform to explore and vote daily for the private servers you are passionate about.',
    'hero.search.placeholder': 'Search for a server...',
    'hero.addServer': 'Add server',
    
    // Stats Section
    'stats.title': 'Real-Time Statistics',
    'stats.description': 'Follow the gaming community activity',
    'stats.servers': 'Servers',
    'stats.votes': 'Total Votes',
    'stats.users': 'Users',
    'stats.activeServers': 'Active Servers',
    'stats.totalVotes': 'Total Votes',
    'stats.totalUsers': 'Users',
    'stats.globalStats': 'Global Statistics',
    'stats.gameStats': '{gameName} Statistics',
    'stats.realTimeData': 'Real-time data',
    
    // Monthly Reset
    'reset.title': 'Next Vote Reset',
    'reset.description': 'Votes are reset every month',
    'reset.resetIn': 'Reset in:',
    'reset.monthlyResetTitle': 'Monthly Vote Reset',
    'reset.monthlyReset': 'Monthly Reset',
    'reset.description2': 'All server votes will be reset to zero on the 1st of next month',
    'reset.newOpportunities': 'New ranking, new opportunities!',
    'reset.days': 'days',
    'reset.hours': 'hours',
    'reset.minutes': 'minutes',
    'reset.seconds': 'seconds',
    
    // Add Server Dialog
    'addServer.title': 'Add Server',
    'addServer.subtitle': 'Join our gaming community',
    'addServer.freeSubmission': 'Free submission',
    'addServer.prestigeFeatures': 'Prestige features available',
    'addServer.basicInfo': 'BASIC INFORMATION',
    'addServer.serverName': 'Server name',
    'addServer.serverNamePlaceholder': 'Ex: My Minecraft Server',
    'addServer.gameType': 'Game type',
    'addServer.selectGame': 'Select a game...',
    'addServer.description': 'Description',
    'addServer.descriptionPlaceholder': 'Describe your server in a few words...',
    'editor.placeholder': 'Describe your server, its features...',
    'addServer.presentationImage': 'Presentation Image (optional)',
    'addServer.presentationImageDesc': 'This image will be displayed in your server card',
    'addServer.serverLogo': 'SERVER LOGO',
    'addServer.serverLogoDesc': 'Logo displayed next to the name (optional)',
    'addServer.videoUrl': 'Video URL (optional)',
    'addServer.videoUrlPlaceholder': 'https://youtube.com/watch?v=...',
    'addServer.videoUrlDesc': 'Link to a presentation video of your server',
    'addServer.techConfig': 'TECHNICAL CONFIGURATION',
    'addServer.technicalConfig': 'TECHNICAL CONFIGURATION', 
    'addServer.serverIP': 'IP Address',
    'addServer.ipAddress': 'IP Address',
    'addServer.ipPlaceholder': 'Ex: play.myserver.com',
    'addServer.version': 'Version',
    'addServer.versionPlaceholder': 'Ex: 1.20.1',
    'addServer.community': 'COMMUNITY LINKS',
    'addServer.communityLinks': 'COMMUNITY LINKS',
    'addServer.discord': 'Discord (optional)',
    'addServer.discordUrl': 'Discord (optional)',
    'addServer.discordPlaceholder': 'https://discord.gg/...',
    'addServer.website': 'Website (optional)',
    'addServer.websiteUrl': 'Website (optional)', 
    'addServer.websitePlaceholder': 'https://myserver.com',
    'addServer.serverTags': 'SERVER TAGS',
    'addServer.prestigeTitle': 'PRESTIGE FEATURES',
    'addServer.prestigeInfo': 'PRESTIGE FEATURES',
    'addServer.prestigeDesc': 'Boost your server visibility',
    'addServer.prestigeNote': 'Available after server creation',
    'addServer.prestigeProfileNote': 'Go to your profile to activate the Prestige status of your servers.',
    'addServer.addServerButton': 'Add server',
    'addServer.limitAlert': 'Limit reached',
    'addServer.cancel': 'Cancel',
    'addServer.submit': 'Add server',
    
    // Profile Page
    'profile.title': 'MY PROFILE',
    'profile.myServers': 'My Servers',
    'profile.arcadeSpots': 'Arcade Spots',
    'profile.myReviews': 'My Reviews',
    'profile.payments': 'Payments',
    'profile.settings': 'Settings',
    'profile.tabs.profile': 'Profile',
    'profile.tabs.security': 'Security',
    'profile.tabs.danger': 'Danger Zone',
    'profile.profileInfo.title': 'Profile Information',
    'profile.profileInfo.description': 'Modify your personal information',
    'profile.profileInfo.emailLabel': 'Email address',
    'profile.profileInfo.usernameLabel': 'Username',
    'profile.profileInfo.usernamePlaceholder': 'Your username',
    'profile.profileInfo.updateButton': 'Update profile',
    'profile.profileInfo.updating': 'Updating...',
    'profile.security.title': 'Change Password',
    'profile.security.description': 'Make sure to use a strong and unique password',
    'profile.security.currentPasswordLabel': 'Current password',
    'profile.security.currentPasswordPlaceholder': 'Your current password',
    'profile.security.newPasswordLabel': 'New password',
    'profile.security.newPasswordPlaceholder': 'Your new password',
    'profile.security.confirmPasswordLabel': 'Confirm password',
    'profile.security.confirmPasswordPlaceholder': 'Confirm your new password',
    'profile.security.changePasswordButton': 'Change password',
    'profile.security.changing': 'Updating...',
    'profile.security.requirements': 'Password must contain at least 6 characters',
    'profile.danger.title': 'Danger Zone',
    'profile.danger.description': 'Irreversible actions on your account',
    'profile.danger.deleteAccount': 'Delete my account',
    'profile.danger.deleteDescription': 'This action will permanently delete your account and all your data. This action is irreversible.',
    'profile.danger.deleteConfirm': 'Are you absolutely sure you want to delete your account?',
    'profile.danger.deleteButton': 'Yes, delete my account',
    'profile.danger.cancel': 'Cancel',
    'profile.danger.deleting': 'Deleting...',
    'profile.emailNotEditable': 'Email cannot be modified',
    'profile.passwordsDontMatch': 'Passwords do not match',
    'profile.serverManagement': 'Server management',
    'profile.visibleServers': 'Visible servers',
    'profile.available': 'Available',
    'profile.baseLimit': 'Base limit',
    'profile.servers': 'Servers',
    'profile.prestigeTip': 'Prestige Tip',
    'profile.prestigeDesc': 'Activate Prestige on a server to unlock +1 additional server slot!',
    'profile.noServersYet': 'You haven\'t added any servers yet.',
    'profile.serverTotalVotes': 'Server Total Votes',
    'profile.userTotalVotes': 'Total Votes Made',
    'profile.prestige': 'Prestige',
    
    // Search
    'search.placeholder': 'Search for a server...',
    'search.noResults': 'No servers found for "{query}"',
    'search.votes': 'votes',
    
    // Tags and Filters
    'tags.loading': 'Loading tags...',
    'tags.searchPlaceholder': 'Search for a tag...',
    'tags.description': 'Select tags that best describe your server',
    'tags.selected': 'Selected tags',
    'tags.available': 'Available tags',
    'tags.proposeTag': 'Propose tag',
    'tags.filterByTags': 'Filter by tags',
    'tags.clearAll': 'Clear',
    'tags.noTagsFound': 'No tags found',
    'tags.anlaibe': 'Anlaibe',
    'tags.casual': 'Casual',
    'tags.communaute': 'Community',
    'tags.competitif': 'Competitive',
    'tags.creatif': 'Creative',
    'tags.debutant': 'Beginner',
    'tags.economie': 'Economy',
    'tags.enUS': 'EN-US',
    'tags.evenements': 'Events',
    'tags.frFR': 'FR-FR',
    'tags.fun': 'Fun',
    'tags.hardcore': 'Hardcore',
     'tags.moreVotes': 'More votes',
     'tags.morePopular': 'More popular',
     'tags.latestServers': 'Latest servers',
     'tags.moreClicks': 'Most Clicks',
     'tags.selectedTags': 'Selected tags',
     'tags.popular': 'Popular tags',
     'tags.searchResults': 'Search results',
    'tags.joinMoreServers': 'Join more than {count} servers on our platform',
    
    // Game Proposals
    'game.proposal.proposeGame': 'Suggest a game',
    'game.proposal.gameName': 'Game name',
    'game.proposal.gameNamePlaceholder': 'e.g. Minecraft, Dofus...',
    'game.proposal.officialLink': 'Official link',
    'game.proposal.comment': 'Comment',
    'game.proposal.commentPlaceholder': 'Why should this game be added?',
    'game.proposal.description': 'Suggest a new game to add to RankServeur. Your proposal will be reviewed by an administrator.',
    'game.proposal.success': 'Proposal sent!',
    'game.proposal.successMessage': 'Your game proposal has been submitted and will be reviewed by an administrator.',
    'game.proposal.error': 'Error',
    'game.proposal.nameRequired': 'Game name is required',
    'game.proposal.adminTitle': 'Game Proposals',
    'game.proposal.adminDescription': 'Manage game proposals submitted by users',
    'game.proposal.pending': 'Pending',
    'game.proposal.approved': 'Approved',
    'game.proposal.rejected': 'Rejected',
    'game.proposal.submittedBy': 'Submitted by',
    'game.proposal.unknownUser': 'Unknown user',
    'game.proposal.adminNotes': 'Admin notes',
    'game.proposal.approve': 'Approve',
    'game.proposal.reject': 'Reject',
    'game.proposal.approvedMessage': 'The proposal has been approved.',
    'game.proposal.rejectedMessage': 'The proposal has been rejected.',
    'game.proposal.rejectReasonRequired': 'Please provide a reason for rejection',
    'game.proposal.rejectConfirmTitle': 'Reject proposal',
    'game.proposal.rejectConfirmDescription': 'Please provide a reason for rejecting this proposal.',
    'game.proposal.rejectReasonPlaceholder': 'Rejection reason...',
    'game.proposal.confirmReject': 'Confirm rejection',
    'game.proposal.noPending': 'No pending proposals',
    'game.proposal.noApproved': 'No approved proposals',
    'game.proposal.noRejected': 'No rejected proposals',
    
     // Filters
     'filters.sortBy': 'Sort by',
     'filters.votes': 'Votes',
     'filters.newest': 'Newest',
     'filters.oldest': 'Oldest',
     'filters.clearAll': 'Clear all',
     'filters.proposeTag': 'Propose tag',
     'filters.tags': 'Filter by tags',
     'filters.prestigeFilter': 'Prestige Filter',
     'filters.allServers': 'All servers',
     'filters.prestigeOnly': 'Prestige only',
     'filters.regularOnly': 'Regular servers',
    
    // Server Card
    'server.vote': 'Vote',
    'server.report': 'Report',
    'server.online': 'Online',
    'server.offline': 'Offline',
    'server.players': 'players',
    'server.sponsored': 'Sponsored',
    'server.gold': 'Gold',
    'server.rank': 'Rank',
    'server.api': 'API',
    
    // Footer
    'footer.description': 'The best platform to discover and vote for the most popular private game servers.',
    'footer.madeWith': 'Made with passion for the gaming community',
    'footer.statsTitle': 'Real-Time Statistics',
    'footer.serversListed': 'servers listed',
    'footer.votesToday': 'votes today',
    'footer.serversOnline': 'servers online',
    'footer.updatedRealTime': 'Updated in real time',
    'footer.partnersTitle': 'Partners',
    'footer.linksTitle': 'Links',
    'footer.links': 'Useful links',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.termsOfService': 'Terms of Service',
    'footer.termsOfSale': 'Terms of Sale',
    'footer.privacy': 'Privacy Policy',
    'footer.copyright': 'All rights reserved.',
    'footer.discordBot': 'Discord Bot',
    
    // Discord Bot Page
    'discordBot.title': 'RankServeur Discord Bot',
    'discordBot.metaDescription': 'Discover all commands of the RankServeur Discord bot. Check rankings, vote and search game servers directly from Discord.',
    'discordBot.heroTitle': 'Discord Bot',
    'discordBot.heroSubtitle': 'Check rankings, vote and search game servers directly from your Discord server.',
    'discordBot.addToDiscord': 'Add to Discord',
    'discordBot.viewSource': 'Source code',
    'discordBot.commandsTitle': 'Available Commands',
    'discordBot.commandsSubtitle': 'All slash commands of the RankServeur bot',
    'discordBot.cmd.top': '/top',
    'discordBot.cmd.topDesc': 'Shows top servers by monthly votes. Filter by game and limit results.',
    'discordBot.cmd.topUsage': '/top [game:minecraft] [limit:10]',
    'discordBot.cmd.server': '/server',
    'discordBot.cmd.serverDesc': 'Shows detailed server info: IP, version, online players, tags, links.',
    'discordBot.cmd.serverUsage': '/server name:MyServer',
    'discordBot.cmd.stats': '/stats',
    'discordBot.cmd.statsDesc': 'Shows detailed server statistics with a 7-day vote chart.',
    'discordBot.cmd.statsUsage': '/stats name:MyServer',
    'discordBot.cmd.vote': '/vote',
    'discordBot.cmd.voteDesc': 'Generates a direct link to vote for a server. One vote every 2 hours.',
    'discordBot.cmd.voteUsage': '/vote name:MyServer',
    'discordBot.cmd.search': '/search',
    'discordBot.cmd.searchDesc': 'Search for a server by name. Returns results with direct links.',
    'discordBot.cmd.searchUsage': '/search query:minecraft pvp',
    'discordBot.cmd.games': '/games',
    'discordBot.cmd.gamesDesc': 'Lists all games available on RankServeur.',
    'discordBot.cmd.gamesUsage': '/games',
    'discordBot.cmd.serveroftheday': '/serveroftheday',
    'discordBot.cmd.serverofthedayDesc': 'Shows the server of the day — the one with the most votes today.',
    'discordBot.cmd.serverofthedayUsage': '/serveroftheday',
    'discordBot.hostingTitle': 'Hosting',
    'discordBot.hostingDesc': 'The bot requires Node.js hosting. Here are free options:',
    'discordBot.hosting.railway': 'Railway — 500h/month free',
    'discordBot.hosting.render': 'Render — Free hosting (with cold starts)',
    'discordBot.hosting.replit': 'Replit — Free with limitations',
    'discordBot.hosting.wispbyte': 'WispByte — Free Discord bot hosting',
    'discordBot.hosting.selfhost': 'Self-hosting — Run node bot.js on your own machine',
    'discordBot.languageTitle': 'Multilingual',
    'discordBot.languageDesc': 'The bot automatically detects your Discord language (FR, EN, ES) and responds accordingly.',
    'discordBot.required': 'Required',
    'discordBot.optional': 'Optional',
    'discordBot.downloadSources': 'Download sources',
    'discordBot.perfTitle': 'Optimized for free hosting',
    'discordBot.perfDesc': 'The bot uses only 1 Discord intent (Guilds) and makes lightweight API calls. It runs easily on free tiers (128 MB RAM, 25% CPU).',
    'discordBot.securityTitle': 'Secure source code',
    'discordBot.securityDesc': 'The source code contains NO API keys or passwords. Each user must create their own .env file with their own Discord credentials. Sharing is 100% safe.',
    'discordBot.securityDesc2': 'The source code is 100% safe to share. It contains NO secrets. The RankServeur API is public (read-only) — no API key needed. You only need your own Discord Token and Application ID.',
    'discordBot.setupTitle': '4-Step Installation',
    'discordBot.setupSubtitle': 'Host your own instance of the RankServeur bot',
    'discordBot.step1Title': 'Copy source files',
    'discordBot.step1Desc': 'Create a folder and copy these files:',
    'discordBot.stepCreateFolder': 'Create a folder, then copy-paste each file below. Click to expand, then copy with the button.',
    'discordBot.step2Title': 'Create a Discord application',
    'discordBot.step2Desc': 'Go to the Discord Developer Portal → New Application → Bot → copy the Token + Application ID.',
    'discordBot.step3Title': 'Create the .env file',
    'discordBot.step3Desc': 'Create a .env file at the project root with your own credentials:',
    'discordBot.step3Desc2': 'Create a .env file with only your Discord Token and Application ID:',
    'discordBot.step3Note': 'NEVER share your .env file.',
    'discordBot.noApiKeyNeeded': 'No RankServeur API key needed! The API is public and read-only.',
    'discordBot.step4Title': 'Install and run',
    'discordBot.step4npm': 'Install dependencies',
    'discordBot.step4deploy': 'Register Discord commands',
    'discordBot.step4start': 'Start the bot',
    'discordBot.voteNotifTitle': 'Vote Notifications',
    'discordBot.voteNotifDesc': 'Automatically receive a notification in your Discord channel every time someone votes for your server. Based on Server UUID to avoid conflicts.',
    'discordBot.voteNotifHow': 'Configure a Discord webhook in your server settings on RankServeur.',
    'discordBot.webhookSetupTitle': '📋 How to set up in 3 steps:',
    'discordBot.webhookStep1': 'In Discord: Server Settings → Integrations → Webhooks → Create Webhook → Copy the URL.',
    'discordBot.webhookStep2': 'On RankServeur: Go to your profile → My Servers → Webhooks → Paste the Discord URL and generate a secret.',
    'discordBot.webhookStep3': 'Done! Every vote will automatically send a notification to your Discord channel.',
    'discordBot.webhookPayloadTitle': 'Example payload sent on each vote:',
    'discordBot.webhookSignatureInfo': 'Each request is signed with HMAC-SHA256 (X-RS-Signature header) to verify authenticity.',
    'discordBot.webhookAdvancedLink': '📖 Advanced guide: HMAC verification, PHP/Node/Python examples → API Documentation',
    'discordBot.source.botDesc': 'Main bot',
    'discordBot.source.deployDesc': 'Command registration',
    'discordBot.source.apiDesc': 'RankServeur API client',
    'discordBot.source.i18nDesc': 'FR/EN/ES translations',
    'discordBot.source.packageDesc': 'npm dependencies',
    'discordBot.sourceNote': 'These files contain no secrets.',
    'footer.addServerCTA': '+ Add my server for free',
    
    // Legal pages titles
    'terms.title': 'Terms of Service',
    'terms.description': 'General terms and conditions for using the RankServeur platform and service usage rules',
    'terms.keywords': 'terms of service, regulations, platform, RankServeur',
    'termsOfSale.title': 'Terms of Sale',
    'termsOfSale.description': 'General terms and conditions for RankServeur advertising spaces',
    'termsOfSale.keywords': 'terms of sale, advertising spaces, RankServeur',
    'privacy.title': 'Privacy Policy',
    'privacy.description': 'How we collect, use and protect your personal data',
    'privacy.keywords': 'privacy, personal data, GDPR, protection',

    // API Documentation
    'api.title': "API Documentation",
    'api.description': "Complete API for server creators: statistics, reviews, rankings and real-time vote webhooks",
    'api.keywords': "API developers, webhooks, server statistics, gaming integration",
    
    // Votifier Guide
    'votifierGuide.title': 'Votifier Guide for Minecraft',
    'votifierGuide.description': 'Complete guide to configure Votifier on your Minecraft server and receive vote notifications automatically',
    'votifierGuide.keywords': 'votifier, minecraft, guide, configuration, plugin, votes, notifications',
    'votifierGuide.subtitle': 'Configure Votifier on your Minecraft server to automatically receive vote notifications and reward your players.',
    'votifierGuide.whatIsVotifier': 'What is Votifier?',
    'votifierGuide.whatIsVotifierDesc': 'Votifier is a Minecraft plugin that allows your server to receive instant notifications when a player votes on sites like RankServeur. This enables automatic player rewards.',
    'votifierGuide.prerequisites': 'Prerequisites',
    'votifierGuide.prereq1': 'A Minecraft server with Spigot, Paper, or Bukkit',
    'votifierGuide.prereq2': 'Administrator access to the server',
    'votifierGuide.prereq3': 'Open network port (default 8192)',
    'votifierGuide.prereq4': 'Server added on RankServeur',
    'votifierGuide.step1Title': 'Download Votifier Plugin',
    'votifierGuide.step1Desc': 'Choose the Votifier version that best suits your server:',
    'votifierGuide.votifierOriginalDesc': 'Stable original version, compatible with most servers',
    'votifierGuide.votifierPlusDesc': 'Enhanced version with more features and compatibility',
    'votifierGuide.recommended': 'Recommended',
    'votifierGuide.recommendedDesc': 'VotifierPlus is generally recommended for better compatibility and advanced features.',
    'votifierGuide.step2Title': 'Install the Plugin',
    'votifierGuide.step2Point1': 'Stop your Minecraft server',
    'votifierGuide.step2Point2': 'Place the downloaded .jar file in the /plugins/ folder',
    'votifierGuide.step2Point3': 'Restart your server',
    'votifierGuide.step2Point4': 'Check that the plugin loaded correctly in the logs',
    'votifierGuide.step3Title': 'Configure the Plugin',
    'votifierGuide.step3Desc': 'After first startup, edit the configuration file:',
    'votifierGuide.configExplanation': 'Parameter explanation:',
    'votifierGuide.hostExplanation': 'Leave 0.0.0.0 to listen on all interfaces',
    'votifierGuide.portExplanation': 'Port on which Votifier listens (8192 by default)',
    'votifierGuide.tokenExplanation': 'Shared security token (generate a unique one)',
    'votifierGuide.debugExplanation': 'Debug mode to diagnose issues',
    'votifierGuide.step4Title': 'RankServeur Configuration',
    'votifierGuide.step4Desc': 'Now configure Votifier on RankServeur side:',
    'votifierGuide.step4Point1': 'Log in to RankServeur',
    'votifierGuide.step4Point2': 'Go to your Profile > My servers',
    'votifierGuide.step4Point3': 'Click "Edit" on your Minecraft server',
    'votifierGuide.step4Point4': 'Scroll down to the "Votifier Configuration" section',
    'votifierGuide.step4Point5': 'Enter your server\'s IP, port, and token',
    'votifierGuide.automaticRewards': 'Automatic Rewards',
    'votifierGuide.automaticRewardsDesc': 'Once configured, your server will automatically receive vote notifications. You can use plugins like VotingPlugin or GAListener to automatically reward players.',
    'votifierGuide.troubleshooting': 'Troubleshooting',
    'votifierGuide.problem1': 'Votes are not reaching the server',
    'votifierGuide.solution1': 'Check that port 8192 is open in your firewall and with your hosting provider. Test the connection with telnet or a port scanner.',
    'votifierGuide.problem2': '"Connection refused" error',
    'votifierGuide.solution2': 'The Votifier plugin may not be started or the port is already in use. Check server logs and change port if necessary.',
    'votifierGuide.problem3': 'Invalid token',
    'votifierGuide.solution3': 'Make sure the token configured on RankServeur exactly matches the one in config.yml. Watch out for spaces and special characters.',
    'votifierGuide.problem4': 'Server crashes on startup',
    'votifierGuide.solution4': 'Check plugin compatibility with your server version. Try a different version of Votifier.',
    'votifierGuide.needHelp': 'Need Help?',
    'votifierGuide.needHelpDesc': 'If you encounter difficulties, our team is here to help!',
    'votifierGuide.contactSupport': 'Contact Support',
    'votifierGuide.apiDocs': 'API Documentation',
    'votifierGuide.copied': 'Copied!',
    'votifierGuide.copiedDesc': 'Code has been copied to clipboard.',
    'api.whereToFind': "Where to find your API key",
    'api.whereToFindDesc': "1. Log in to RankServeur\n2. Go to your Profile\n3. Click on 'My servers'\n4. Select your server\n5. Click on the 'API' icon (<>)\n6. Copy your API key",
    'api.webhookSetup': "Webhook Configuration",
    'api.webhookSetupDesc': "1. In 'My servers'\n2. Click on the Webhooks icon (⚙️ green)\n3. Add your endpoint URL\n4. Select events to receive\n5. Test the configuration",
    'api.security': "API Security",
    'api.securityDesc': "• Keep your API key secret\n• Use HTTPS only\n• Verify HMAC signature of webhooks\n• Limit access to necessary IPs",
    'api.gettingStarted': "Getting Started",
    'api.gettingStartedDesc': "The RankServeur API allows server creators to easily integrate the voting system into their launchers and websites.",

    // Creator Tools
    'creatorTools.title': 'Creator Tools',
    'creatorTools.description': 'Custom widget generator to easily integrate your server on your website or launcher',
    'creatorTools.keywords': 'widgets, generator, integration, badge, button, banner, iframe, script',
    'creatorTools.subtitle': 'Generate custom widgets to promote your server everywhere',
    'creatorTools.configuration': 'Configuration',
    'creatorTools.preview': 'Live Preview',
    'creatorTools.code': 'Code to Copy',
    'creatorTools.guide': 'Quick Guide',
    'creatorTools.selectServer': 'Select a server',
    'creatorTools.selectServerPlaceholder': 'Choose your server...',
    'creatorTools.format': 'Format',
    'creatorTools.formatBadge': 'Badge',
    'creatorTools.formatButton': 'Button',
    'creatorTools.formatBanner': 'Banner',
    'creatorTools.size': 'Size',
    'creatorTools.sizeSmall': 'Small',
    'creatorTools.sizeMedium': 'Medium',
    'creatorTools.sizeLarge': 'Large',
    'creatorTools.theme': 'Theme',
    'creatorTools.themeLight': 'Light',
    'creatorTools.themeDark': 'Dark',
    'creatorTools.themeRetro': 'Retro Gaming',
    'creatorTools.language': 'Language',
    'creatorTools.showVotes': 'Show votes',
    'creatorTools.rounded': 'Rounded corners',
    'creatorTools.animation': 'Animation',
    'creatorTools.tabIframe': 'Iframe',
    'creatorTools.tabScript': 'Script JS',
    'creatorTools.copyCode': 'Copy code',
    'creatorTools.copied': 'Code copied!',
    'creatorTools.step1': 'Configure your widget',
    'creatorTools.step1Desc': 'Choose your server and customize the appearance',
    'creatorTools.step2': 'Preview the render',
    'creatorTools.step2Desc': 'Check that the widget meets your expectations',
    'creatorTools.step3': 'Integrate the code',
    'creatorTools.step3Desc': 'Copy and paste the code on your site',
    'creatorTools.widgetFor': 'Widget for',
    'creatorTools.server': 'server',
    'creatorTools.widgetRankServeur': 'RankServeur Widget',
    'creatorTools.noServers': 'You must have at least one server to use this tool',
    'creatorTools.loginRequired': 'Sign in to access creator tools',
    'api.step1': "Step 1: Get your API key",
    'api.step2': "Step 2: Configure webhooks",
    'api.step3': "Step 3: Integrate into your application",
    'api.commonUse': "Common Use Cases",
    'api.commonUseDesc': "• Automatically reward players who vote\n• Display vote count in real-time\n• Track voting statistics\n• Create custom rankings",
    'common.results': 'results',
    'creatorTools.playerLabel': 'Pre-filled nickname (optional)',
'creatorTools.playerPlaceholder': 'Ex: PlayerNickname',
'creatorTools.playerHint': 'Adds ?player= to the vote URL (not validated server-side, the player can still edit it).',
    'creatorTools.logoSize': 'Logo size',
    'creatorTools.logoSizeSmall': 'Small',
    'creatorTools.logoSizeMedium': 'Medium',
    'creatorTools.logoSizeLarge': 'Large',
    'creatorTools.logoSizeNone': 'Hidden',
    'creatorTools.accentColor': 'Accent color',
    'creatorTools.effect': 'Visual effect',
    'creatorTools.effectNone': 'None',
    'creatorTools.effectGlow': 'Neon glow',
    'creatorTools.effectShadow': 'Drop shadow',
    'creatorTools.effectGradient': 'Gradient border',
    'creatorTools.borderStyle': 'Border style',
    'creatorTools.borderSolid': 'Solid',
    'creatorTools.borderDashed': 'Dashed',
    'creatorTools.borderDouble': 'Double',
    'creatorTools.borderNone': 'None',
    'creatorTools.ctaText': 'Button text',
    'creatorTools.ctaPlaceholder': 'Ex: Vote now',
    'creatorTools.appearance': 'Appearance',
    'creatorTools.effects': 'Effects & Style',
    'creatorTools.bgColor': 'Background color',
    'creatorTools.bgImage': 'Background image (URL)',
    'creatorTools.bgImagePlaceholder': 'https://example.com/my-background.jpg',
    'creatorTools.bgImageHint': 'The image is hosted on your side, we do not store it.',

    // Buttons
    'button.save': 'Save',
    'button.cancel': 'Cancel',
    'button.refresh': 'Refresh',
    'button.confirm': 'Confirm',
    'button.close': 'Close',
    'button.edit': 'Edit',
    'button.delete': 'Delete',
    'button.add': 'Add',
    'button.search': 'Search',
    'button.filter': 'Filter',
    
    // Games
    'games.allGames': 'All games',
    'games.selectGame': 'Select a game',
    
    // Competitive Events
    'competitive.showRanking': 'Event ranking',
    'competitive.showingRanking': 'Event ranking',
    
    // Index page
    'index.addMyServer': 'ADD MY SERVER',
    'index.joinServers': 'Join more than {count} servers on our platform',
    'index.joinListedServers': 'Join listed servers',
    'index.voteEvery2Hours': 'Vote every 2 hours for your favorite servers!',
    'index.nextVoteIn': 'Next vote in: {hours}h {minutes}m',
    'index.canVoteNow': 'You can vote now!',
    'index.topServers': 'TOP SERVERS',
    'index.rankingRealTime': 'Real-time updated ranking ({count} server total)',
    
    
    // Arcade Spot Booking
    'arcade.title': 'Book an Arcade Spot',
    'arcade.subtitle': 'Reserve your premium advertising space',
    'arcade.position': 'Position',
    'arcade.selectPosition': 'Select a position',
    'arcade.selectDates': 'Select dates',
    'arcade.uploadImage': 'Upload an image',
    'arcade.websiteUrl': 'Website URL',
    'arcade.totalPrice': 'Total price',
    'arcade.book': 'Book',
    'arcade.pricePerDay': '€/day',
    'arcade.selectPositionFirst': 'Please select a position',
    'arcade.selectDatesFirst': 'Please select dates',
    'arcade.uploadImageFirst': 'Please upload an image',
    'arcade.bookingSuccess': 'Booking completed successfully!',
    'arcade.selectMonth': 'Entire month',
    'arcade.clearSelection': 'Clear',
    
    // Terms of Sale
    'tos.title': 'Terms of Sale',
    'tos.lastUpdate': 'Last updated:',
    'tos.object': '1. Purpose',
    'tos.objectText': 'These terms govern the sale of advertising spaces (banners, sponsored inserts, highlights) on the website https://rankserveur.com, registered under SIREN 989 131 347.',
    'tos.services': '2. Services offered',
    'tos.servicesText': 'The client can reserve an advertising space for a defined duration (by day, week or month). Prices are displayed on the site and may vary according to location and duration.',

    // Arcade Spots (homepage)
    'arcade.leftSpot': 'Left Arcade Spot',
    'arcade.centerSpot': 'Center Arcade Spot', 
    'arcade.rightSpot': 'Right Arcade Spot',
    'arcade.clickToReserve': 'Click to reserve',
    'arcade.bookSpot': 'Book an Arcade Spot',
    'arcade.bookingDescription': 'Reserve your advertising space even if all spots are occupied',
     'arcade.suspended': 'Suspended',
     'arcade.visit': 'Visit',
     'arcade.reserved': 'Reserved',
     'arcade.startsOn': 'Starts on',

    // Arcade Spot Booking Dialog
    'arcade.booking.title': 'Book an Arcade Spot',
    'arcade.booking.positionLabel': 'Arcade Spot Position *',
    'arcade.booking.imageLabel': 'Advertising image *',
    'arcade.booking.imageFormat': '(optimal format: 512x288px)',
    'arcade.booking.option1': 'Option 1: Image upload',
    'arcade.booking.maxSize': '(max 5MB)',
    'arcade.booking.noFileChosen': 'No file chosen',
    'arcade.booking.or': 'OR',
    'arcade.booking.option2': 'Option 2: Image URL',
    'arcade.booking.urlPlaceholder': 'https://example.com/image.jpg',
    'arcade.booking.websiteLabel': 'Destination URL (optional)',
    'arcade.booking.websitePlaceholder': 'https://your-site.com',
    'arcade.booking.selectPosition': 'Select a position',
    'arcade.booking.selectDates': 'Select your days * (click to select/deselect)',
    'arcade.booking.daysSelected': 'Selected days',
    'arcade.booking.positionLeft': 'Left Position',
    'arcade.booking.positionCenter': 'Center Position',
    'arcade.booking.positionRight': 'Right Position',
    'arcade.booking.addAdmin': 'Add (Admin)',
    'arcade.booking.proceedPayment': 'Proceed to payment',
    'arcade.booking.totalPrice': 'Total price',
    'arcade.booking.days': 'day(s)',
    'arcade.booking.processing': 'Processing...',
     'arcade.booking.preview': 'Preview (512x288px recommended)',
    
    // Sky Spot
    'skySpot.title': 'Sky Spot',
    'skySpot.clickToReserve': 'Click to reserve',
    'skySpot.bookSpot': 'Book Sky Spot',
    'skySpot.bookingDescription': 'Vertical advertising 160x600px',
    'skySpot.suspended': 'Suspended',
    'skySpot.visit': 'Visit',
    'skySpot.reserved': 'Reserved',
    'skySpot.startsOn': 'Starts on',
    'skySpot.booking.title': 'Book Sky Spot',
    'skySpot.booking.selectDates': 'Select your days * (click to select/deselect)',
    'skySpot.booking.daysSelected': 'Selected days',
    'skySpot.booking.imageLabel': 'Advertising image *',
    'skySpot.booking.imageFormat': '(optimal format: 160x600px)',
    'skySpot.booking.option1': 'Option 1: Image upload',
    'skySpot.booking.maxSize': '(max 5MB)',
    'skySpot.booking.or': 'OR',
    'skySpot.booking.option2': 'Option 2: Image URL',
    'skySpot.booking.urlPlaceholder': 'https://example.com/image.jpg',
    'skySpot.booking.websiteLabel': 'Destination URL (optional)',
    'skySpot.booking.websitePlaceholder': 'https://your-site.com',
    'skySpot.booking.totalPrice': 'Total price',
    'skySpot.booking.days': 'day(s)',
    'skySpot.booking.processing': 'Processing...',
    'skySpot.booking.preview': 'Preview (160x600px recommended)',
    'skySpot.booking.addAdmin': 'Add (Admin)',
    'skySpot.booking.proceedPayment': 'Proceed to payment',

     // Games List
    'games.availableGames': 'AVAILABLE GAMES',
    'games.top': 'TOP',
    'games.categories': 'CATEGORIES',
    'games.loadingGames': 'Loading games...',
    'games.errorLoading': 'Error loading games',
    'games.noGamesAvailable': 'No games available',
    'games.gamesCount': '{count} games available',
    'games.viewAll': 'View all {gameName} servers',
    
    // Authentication Dialog
    'auth.title': 'AUTHENTICATION',
    'auth.signin': 'Sign In',
    'auth.signup': 'Sign Up',
    'auth.reset': 'Reset',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.username': 'Username',
    'auth.emailPlaceholder': 'your@email.com',
    'auth.passwordPlaceholder': '••••••••',
    'auth.usernamePlaceholder': 'YourUsername',
    'auth.signInButton': 'Sign In',
    'auth.signUpButton': 'Sign Up',
    'auth.signInLoading': 'Signing in...',
    'auth.signUpLoading': 'Signing up...',
    'auth.forgotPassword': 'Forgot password?',
    'auth.resetPassword': 'Send reset link',
    'auth.resetLoading': 'Sending...',
    'auth.resetDescription': 'Enter your email to receive a reset link',
    'auth.backToSignin': 'Back to sign in',
    'auth.or': 'or',
    'auth.googleSignIn': 'Continue with Google',
    'auth.googleSignUp': 'Sign up with Google',
    'auth.googleError': 'Google sign-in failed',
    'resetpw.checking': 'Checking the link...',
    'resetpw.title': 'New password',
    'resetpw.subtitle': 'Choose a new secure password',
    'resetpw.new': 'New password',
    'resetpw.confirm': 'Confirm password',
    'resetpw.submit': 'Update password',
    'resetpw.updating': 'Updating...',
    'resetpw.invalidTitle': 'Invalid link',
    'resetpw.invalidDesc': 'This reset link is invalid or has expired. Please request a new one.',
    'resetpw.mismatch': 'Passwords do not match.',
    'resetpw.tooShort': 'Password must be at least 6 characters.',
    'resetpw.successTitle': 'Success',
    'resetpw.successDesc': 'Your password has been updated successfully.',
    'resetpw.errorTitle': 'Error',
    'resetpw.genericError': 'An error occurred while updating your password.',

    'auth.error': 'Error',
    'auth.emailSent': 'Email sent',
    'auth.emailSentDescription': 'Check your mailbox to reset your password.',
    // Report Dialog
    'report.title': 'Report {type}',
    'report.titleServer': 'this server',
    'report.titleComment': 'this comment',
    'report.description': 'Help us maintain platform quality by reporting inappropriate content.',
    'report.serverName': 'Report for: {serverName}',
    'report.reasonLabel': 'Report reason:',
    'report.reasons.inappropriate_content': 'Inappropriate content',
    'report.reasons.spam': 'Spam or advertising',
    'report.reasons.fake_server': 'Fake server',
    'report.reasons.malicious_content': 'Malicious content',
    'report.reasons.copyright': 'Copyright violation',
    'report.reasons.other': 'Other',
    'report.customReasonLabel': 'Specify the reason:',
    'report.customReasonPlaceholder': 'Describe the problem...',
    'report.cancel': 'Cancel',
    'report.submit': 'Send report',
    'report.submitting': 'Sending...',
    'report.selectReason': 'Please select a reason',
    'report.specifyReason': 'Please specify the reason',
     'report.success': 'Report sent successfully',
     'report.error': 'Error sending report',

     // Server Detail Page
     'serverDetail.backToServers': 'Back to servers',
     'serverDetail.vote': 'Vote for this server',
     'serverDetail.website': 'Website',
     'serverDetail.discord': 'Discord',
     'serverDetail.aboutServer': 'About the server',
     'serverDetail.articlesTitle': 'Featured articles about this server',
     'serverDetail.noDescription': 'No description available for this server.',
      'serverDetail.serverStats': 'Server statistics',
      'serverDetail.monthlyVotes': 'Monthly votes',
      'serverDetail.totalVotes': 'Total votes',
     'serverDetail.clicks': 'Clicks',
     'serverDetail.rank': 'Rank',
     'serverDetail.addedOn': 'Added on',
     'serverDetail.voteHistory': 'Vote history',
     'serverDetail.weeklyVotes': 'Weekly votes',
     'serverDetail.noVoteData': 'No vote data yet',
     'serverDetail.playerReviews': 'Player reviews',
     'serverDetail.loginToReview': 'Login to leave a review',
     'serverDetail.addReview': 'Add a review',
     'serverDetail.yourRating': 'Your rating',
     'serverDetail.reviewComment': 'Your comment',
     'serverDetail.reviewPlaceholder': 'Share your experience on this server...',
     'serverDetail.publishReview': 'Publish review',
     'serverDetail.noReviews': 'No reviews yet. Be the first to share your opinion!',
     'serverDetail.serverNotFound': 'Server not found',
     'serverDetail.serverNotFoundDesc': 'This server does not exist or has been deleted.',
     'serverDetail.loadingServer': 'Loading server...',
     'serverDetail.connectToIP': 'Connect with this IP:',
     'serverDetail.copyIP': 'Copy IP',
     'serverDetail.ipCopied': 'Copied!',
     'serverDetail.ipCopiedDesc': 'The IP address has been copied to the clipboard.',
     'serverDetail.reviewAdded': 'Review added!',
     'serverDetail.reviewAddedDesc': 'Your review has been published successfully.',
     'serverDetail.reviewError': 'Error',
     'serverDetail.reviewErrorDesc': 'An error occurred while adding your review.',
     'serverDetail.commentRequired': 'Comment required',
     'serverDetail.commentRequiredDesc': 'Please enter a comment.',
     'serverDetail.loginRequired': 'Login required',
     'serverDetail.loginRequiredDesc': 'You must be logged in to leave a review.',
      'serverDetail.vote.nextVoteIn': 'Next vote in',
      'serverDetail.votesToday': 'Votes today',
      'serverDetail.version': 'Version',
      'serverDetail.gameType': 'Game type',
      'serverDetail.tags': 'Tags',
      'serverDetail.connectToServer': 'Connect to server',
      'serverDetail.voteEvolution': 'Vote evolution (last 7 days)',
      'serverDetail.communityReviews': 'Community reviews',
      'serverDetail.anonymousUser': 'Anonymous user',
      'serverDetail.noReviewsYet': 'No reviews yet',
      'serverDetail.firstToShare': 'Be the first to share your experience!',
      'serverDetail.shareExperience': 'Share your experience',
      'serverDetail.publishing': 'Publishing...',

      // Vote Dialog
      'vote.labelMinecraft': 'Minecraft Username',
      'vote.labelDofus': 'Dofus Username', 
      'vote.labelCS': 'Counter-Strike Username',
      'vote.labelDefault': 'Game Username',
      'vote.placeholderMinecraft': 'YourMinecraftUsername',
      'vote.placeholderDofus': 'YourDofusUsername',
      'vote.placeholderCS': 'YourCSUsername',
      'vote.placeholderDefault': 'YourUsername',
      'vote.securityVerification': 'Security verification',
      'vote.captchaLoading': 'Loading captcha...',
      'vote.checkingActivity': 'Checking your last activity...',
      'vote.cooldownMessage': '⏱ You can vote again in {time}.',
      'vote.voteButton': 'Vote',
      'vote.verifyingInProgress': 'Verifying in progress...',
     'vote.nicknameNotValidated': 'The nickname is not validated, you can type any name.',
     'vote.savedPlayerName': 'Nickname saved for your next votes',
     
     // Profile empty states & server management texts
     'profile.noArcadeSpots': 'You don\'t have any Arcade Spot reservations at the moment.',
     'profile.noReviews': 'You haven\'t left any reviews yet.',
     'profile.noPrestigePayments': 'No Prestige payments at the moment.',
     'profile.noServersMessage': 'You haven\'t added any server yet.',
     'profile.serverHidden': 'Hidden',
     'profile.serverDisabledMessage': 'This server has been disabled following a report or an administrative action.',
     'profile.serverDisabledSub': 'It no longer appears in rankings and is no longer accessible to visitors.',
     'profile.serverHiddenMessage': 'This server is hidden and does not appear in public rankings.',
     'profile.serverHiddenSub': 'You can make it visible at any time below.',
     'profile.unknownGame': 'Unknown game',
     'profile.prestigeLabel': 'PRESTIGE',
     'profile.prestigeDaysLeft': '{days}d left',
     'profile.standardLabel': 'Standard',
     'profile.createdOn': 'Created on',
     'profile.apiVote': 'Vote API',
     'profile.webhooks': 'Webhooks',
     'profile.privilegedAction': 'Privileged Action',
     'profile.deactivatePrestigeConfirm': 'Deactivate Prestige for "{name}"? This action is immediate and reserved for founders/administrators.',
     'profile.activatePrestigeConfirm': 'Activate Prestige for "{name}" for 30 days? This action is reserved for founders/administrators.',
     'profile.cancel': 'Cancel',
     'profile.deactivate': 'Deactivate',
     'profile.activate': 'Activate',
     'profile.arcadeSpotPosition': 'Arcade Spot Position',
     'profile.statusActive': 'Active',
     'profile.statusPending': 'Pending',
     'profile.statusExpired': 'Expired',
     'profile.serverLabel': 'Server:',
     'profile.deletedServerLabel': 'Deleted server',
     'profile.dateFrom': 'From',
     'profile.dateTo': 'to',
     'profile.viewWebsite': 'View website',
     'profile.deleteReview': 'Delete review',
     'profile.deleteReviewConfirm': 'Are you sure you want to delete this review? This action is irreversible.',
     'profile.delete': 'Delete',
     'profile.prestigePaymentTitle': 'Prestige',
     'profile.statusPaid': 'Paid',
     'profile.daysCount': '{days} day',
     'profile.daysCountPlural': '{days} days',
     'profile.expiresOn': 'Expires on',
     'profile.upgradeToPrestige': 'Upgrade to Prestige',
     'profile.disablePrestige': 'Disable Prestige (Privilege)',
     'profile.enablePrestige': 'Enable Prestige (Privilege)',
     'profile.addFirstServer': 'Add your first server to get started!',
     
     // Visibility control
     'visibility.visible': 'Visible',
     'visibility.hidden': 'Hidden',
     'visibility.hideServer': 'Hide server',
     'visibility.showServer': 'Make server visible',
     'visibility.hideServerTitle': 'Hide server',
     'visibility.showServerTitle': 'Make server visible',
     'visibility.hideConfirm': 'Are you sure you want to hide "{name}"?',
     'visibility.hideWarning': 'The server will disappear from public rankings but will remain in your management.',
     'visibility.showConfirm': 'Are you sure you want to make "{name}" visible?',
     'visibility.showInfo': 'The server will appear in public rankings.',
     'visibility.slotsUsed': 'You are using {used} out of {max} allowed servers.',
     'visibility.hide': 'Hide',
     'visibility.show': 'Make visible',
     'visibility.limitReached': 'Limit reached',

      'vote.error.pseudoLength': 'Username must contain between 2 and 50 characters',
      'vote.error.invalidCharacters': 'Unauthorized characters detected in username',
      'vote.error.loading': 'Loading in progress, please wait...',
      'vote.error.captchaRequired': 'Please complete the security verification',
      'vote.dialogTitle': 'Vote for {serverName}',


    // Profile Page Extended
    'profile.welcomeMessage': 'You must be logged in to access your profile.',
    'profile.founder': 'FOUNDER',
    'profile.admin': 'ADMIN',
    'profile.reviews': 'Reviews',
    'profile.activeWarnings': 'Active warnings',
    'profile.server': 'Server:',
    'profile.deletedServer': 'Deleted server',
    'profile.disabled': '(DISABLED)',
    'profile.loading': 'Loading...',
    'profile.prestigeExpires': 'Prestige expires in {days} day{plural}',
    'profile.prestigeExpired': 'Prestige expired',
    'profile.activatePrestige': 'Activate Prestige',
    'profile.deactivatePrestige': 'Deactivate Prestige',
    'profile.upgradePrestige': 'Upgrade Prestige',
    'profile.manageAPI': 'Manage API',
    'profile.edit': 'Edit',
    'profile.visibility': 'Visibility',
    'profile.votes': 'votes',

    // Legal Pages  
    'legal.termsOfService': 'Terms of Service',
    'legal.termsOfSale': 'Terms of Sale',
    'legal.privacyPolicy': 'Privacy Policy',
    'legal.lastUpdated': 'Last updated:',


    // Prestige Payment Dialog
    'prestige.upgradeTitle': 'Upgrade to Prestige',
    'prestige.duration': 'Duration:',
    'prestige.day': 'day',
    'prestige.days': 'days',
    'prestige.unitPrice': 'Unit price:',
    'prestige.total': 'Total:',
    'prestige.featuresIncluded': 'Prestige features included:',
    'prestige.cancel': 'Cancel',
    'prestige.loading': 'Loading...',
    'prestige.pay': 'Pay',

    // Edit Server Dialog
    'editServer.title': 'Edit server',
    'editServer.delete': 'Delete',
    'editServer.deleteConfirmTitle': 'Delete server',
    'editServer.deleteConfirmText': 'Are you sure you want to delete "{name}"? This action is irreversible.',
    'editServer.deleting': 'Deleting...',
    'editServer.basicInfo': 'BASIC INFORMATION',
    'editServer.serverName': 'Server name',
    'editServer.game': 'Game',
    'editServer.selectGame': 'Select a game',
    'editServer.description': 'Description',
    'editServer.descriptionPlaceholder': 'Describe your server...',
    'editServer.descriptionImage': 'Description image',
    'editServer.serverImages': 'SERVER IMAGES',
    'editServer.serverLogo': 'Server logo',
    'editServer.serverBanner': 'Server banner',
    'editServer.prestigeRequired': 'Prestige required',
    'editServer.bannerAvailableWithPrestige': 'Banner available with Prestige',
    'editServer.upgradeForBanner': 'Upgrade to Prestige to add a banner to your server',
    'editServer.locked': 'LOCKED',
    'editServer.technicalConfig': 'TECHNICAL CONFIGURATION',
    'editServer.ipAddress': 'IP Address',
    'editServer.version': 'Version',
    'editServer.communityLinks': 'COMMUNITY LINKS',
    'editServer.discord': 'Discord',
    'editServer.website': 'Website',
    'editServer.serverTags': 'SERVER TAGS',
    'editServer.prestigeCustomization': 'PRESTIGE CUSTOMIZATION',
    'editServer.customColor': 'Custom color',
    'editServer.upgradeForColor': 'Upgrade to Prestige to customize your server color',
    'editServer.updating': 'Updating...',
    'editServer.updateServer': 'Update server',
    'editServer.serverEvents': 'Current and upcoming events',
    'editServer.save': 'Save',
    'editServer.videoUrl': 'Video URL',
    'editServer.videoUrlPlaceholder': 'https://youtube.com/watch?v=...',
    'editServer.videoUrlDesc': 'Link to a presentation video (optional)',
     
     // Vote API Manager - English translations
    'voteApi.title': 'Vote API',
    'voteApi.description': 'Configure the vote API for your server and track real-time statistics.',
    'voteApi.tokenLabel': 'API Token',
    'voteApi.regenerateToken': 'Regenerate token',
    'voteApi.showToken': 'Show token',
    'voteApi.hideToken': 'Hide token',
    'voteApi.copyToken': 'Copy token',
    'voteApi.apiDocumentation': 'API Documentation',
    'voteApi.endpointLabel': 'API Endpoint',
    'voteApi.copyEndpoint': 'Copy endpoint',
    'voteApi.exampleRequest': 'Example request',
    'voteApi.copyExample': 'Copy example',
    'voteApi.tokenRegenerated': 'Token regenerated',
    'voteApi.tokenRegeneratedDesc': 'Your new API token has been generated successfully',
    'voteApi.copySuccess': 'Copied!',
    'voteApi.copySuccessDesc': 'Content has been copied to clipboard',
    'voteApi.error': 'Error',
    'voteApi.regenerateError': 'Unable to regenerate token',
    'voteApi.keepSecret': '⚠️ Keep this key secret and secure',
    'voteApi.apiUrl': 'API URL',
    'voteApi.replaceUserIp': 'Replace {USER_IP} with the user’s IP address',
    'voteApi.endpointFormat': 'Endpoint format',
    'voteApi.successResponse': 'Success response',
    'voteApi.failureResponse': 'Error response',
    'voteApi.phpExample': 'PHP example',
    'voteApi.importantInfoTitle': 'Important information',
    'voteApi.votesValid24h': 'Votes are valid once every 24 hours (1 vote/day/server)',
    'voteApi.ipHashingSecurity': 'IP hashing uses SHA-256 for security',
    'voteApi.limitTokenAccess': 'Limit API token access',
     'voteApi.testIntegration': 'Test your integration before going live',
     
    // Admin Arena Import
    'admin.arena.title': 'Arena-Top100 Import',
    'admin.arena.description': 'Import servers from arena-top100.com',
    'admin.arena.infoTitle': 'Important information',
    'admin.arena.info1': 'Servers will be created with is_approved=false (require validation)',
    'admin.arena.info2': 'Only servers for games in your database will be imported',
    'admin.arena.info3': 'Duplicates (name or URL) will be automatically skipped',
    'admin.arena.info4': 'Images will be downloaded and compressed automatically',
    'admin.arena.info5': 'You will be set as owner of all imported servers',
    'admin.arena.importLimit': 'Number of servers to import',
    'admin.arena.importAll': 'All servers',
    'admin.arena.importCustom': 'Custom number',
    'admin.arena.customLimitPlaceholder': 'Enter number of servers',
    'admin.arena.startImport': 'Start Import',
    'admin.arena.importing': 'Importing...',
    'admin.arena.importingDesc': 'This may take a few minutes',
    'admin.arena.importSuccess': 'Import completed',
    'admin.arena.importError': 'Import error',
    'admin.arena.serversImported': 'servers imported',
    'admin.arena.total': 'Total',
    'admin.arena.imported': 'Imported',
    'admin.arena.skipped': 'Skipped',
    'admin.arena.errors': 'Errors',
    'admin.arena.details': 'Import details',
    'admin.arena.game': 'Game',
    'admin.arena.slug': 'Slug',

    // Edit Server Dialog - New translations
    'editServer.editServer': 'Edit Server',
    'vote.created': 'vote.created',
    'editServer.events': 'Events',
    'editServer.tags': 'Tags',
    'editServer.mediaTags': 'Media & Tags',
    'editServer.integrations': 'Integrations',
    'editServer.prestige': 'Prestige',

    // Votifier Configuration - English translations
    'votifier.title': 'Votifier Configuration',
    'votifier.description': 'Votifier allows your Minecraft server to receive instant notifications when a player votes. Configure your Votifier plugin on your server and add the information below.',
    'votifier.activeConfigs': 'Active configurations',
    'votifier.addConfig': 'Add Votifier configuration',
    'votifier.serverHost': 'Server IP address *',
    'votifier.serverHostPlaceholder': '127.0.0.1 or your-domain.com',
    'votifier.port': 'Votifier port *',
    'votifier.portPlaceholder': '8192',
    'votifier.protocol': 'Votifier protocol',
    'votifier.protocolV2': 'v2 (Recommended - JSON/HTTP)',
    'votifier.protocolV1': 'v1 (Legacy - RSA/TCP)',
    'votifier.token': 'Token/Key *',
    'votifier.tokenPlaceholder': 'Votifier security token',
    'votifier.generate': 'Generate',
    'votifier.active': 'Active',
    'votifier.inactive': 'Inactive',
    'votifier.creating': 'Creating...',
    'votifier.createConfig': 'Create configuration',
    'votifier.installationGuide': 'Installation guide',
    'votifier.pluginInstall': 'Plugin installation:',
    'votifier.installStep1': 'Download Votifier or VotifierPlus from SpigotMC',
    'votifier.installStep2': 'Place the .jar in your plugins/ folder',
    'votifier.installStep3': 'Restart your server',
    'votifier.installStep4': 'Configure the plugin with your settings above',
    'votifier.installStep5': 'Activate the configuration on this page',
    'votifier.pluginConfig': 'Plugin configuration (config.yml):',
    'votifier.importantNote': 'Important:',
    'votifier.firewallNote': 'Make sure port {port} is open in your firewall and properly forwarded if you use a hosting provider.',
    'votifier.configCreated': 'Votifier configuration created',
    'votifier.configCreatedDesc': 'Your server will now receive vote notifications.',
    'votifier.configUpdated': 'Configuration updated',
    'votifier.configUpdatedDesc': 'Votifier status has been modified.',
    'votifier.configDeleted': 'Configuration deleted',
    'votifier.configDeletedDesc': 'Votifier configuration has been deleted.',
    'votifier.requiredFields': 'Required fields',
    'votifier.requiredFieldsDesc': 'Please fill in all required fields.',
    'votifier.invalidPort': 'Invalid port',
    'votifier.invalidPortDesc': 'Port must be between 1 and 65535.',
    'votifier.loading': 'Loading...',
    'votifier.error': 'Error',
    'votifier.createError': 'Unable to create Votifier configuration.',

    // Webhook Integration
    'webhookIntegration.title': 'Webhook Integration',
    'webhookIntegration.description': 'Receive real-time notifications when events occur',
    'webhookIntegration.urlLabel': 'Webhook URL',
    'webhookIntegration.secretLabel': 'HMAC Secret',
    'webhookIntegration.generateSecret': 'Generate',
    'webhookIntegration.createWebhook': 'Create webhook',
    'webhookIntegration.testWebhook': 'Test',
    'webhookIntegration.deleteWebhook': 'Delete',
    'webhookIntegration.webhookCreated': 'Webhook created',
    'webhookIntegration.webhookCreatedDesc': 'The webhook has been created successfully.',
    'webhookIntegration.webhookUpdated': 'Webhook updated',
    'webhookIntegration.webhookDeleted': 'Webhook deleted',
    'webhookIntegration.testSuccess': 'Webhook test successful',
    'webhookIntegration.testError': 'Webhook test error',
    'webhookIntegration.active': 'Active',
    'webhookIntegration.inactive': 'Inactive',
    'webhookIntegration.documentation': 'Documentation',
    'webhookIntegration.supportedEvents': 'Supported events:',
    'webhookIntegration.voteCreated': 'Triggered when a player votes for your server',
    'webhookIntegration.security': 'Security:',
    'webhookIntegration.hmacSigned': 'Each request is signed with HMAC-SHA256',
    'webhookIntegration.eventHeader': 'Header',
    'webhookIntegration.signatureHeader': 'Header',
    'webhookIntegration.signatureFormat': 'Signature format:',
    'webhookIntegration.urlRequired': 'URL and secret are required.',
    'webhookIntegration.urlPlaceholder': 'https://your-domain.com/webhook',
    'webhookIntegration.secretPlaceholder': 'Secret to sign requests',
    'webhookIntegration.testPayloadLabel': 'Test payload (JSON)',
    'webhookIntegration.sendTest': 'Send test',
    'webhookIntegration.testing': 'Testing...',
    'webhookIntegration.creating': 'Creating...',
    'webhookIntegration.configured': 'Configured webhooks',

    // Common
    'common.status': 'Status',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
     'common.noResults': 'No results found',
     'common.viewMore': 'View more',
     'common.viewLess': 'View less',
     'common.serverDetail': 'Server Detail',
     'common.comingSoon': 'Coming Soon',
     'common.create': 'Create',
     'common.add': 'Add',
     'common.edit': 'Edit',
     'common.delete': 'Delete',
     'common.cancel': 'Cancel',
     'common.submit': 'Submit',
     'common.submitting': 'Submitting...',
     'common.success': 'Success',
     'common.none': 'None',
     'common.all': 'All',
     'common.filter': 'Filter',
     'common.search': 'Search',
      'common.sort': 'Sort',
      'common.servers': 'Privates Servers',
      'common.showMore': 'Show more',
      'common.showLess': 'Show less',
      'common.available': 'Available',
      'common.notAvailable': 'Not Available',
     'common.noServersFound': 'No servers found',
     'common.noServersWithFilters': 'No servers found with these filters',
     'common.loadingServers': 'Loading servers...',
     'common.errorLoadingServers': 'Error loading servers',
     
     // Favorites Page
     'favorites.title': 'MY FAVORITES',
     'favorites.subtitle': 'Your favorite servers ({count})',
     'favorites.loginRequired': 'You must be logged in to view your favorites.',
     'favorites.noFavoritesTitle': 'No favorites yet',
     'favorites.noFavoritesDesc': 'Add servers to your favorites by clicking the heart in the server list.',
     
     // Server Management
     'serverManagement.loading': 'Loading...',
     'serverManagement.serverDisabled': 'Server Disabled',
     'serverManagement.activeWarnings': 'Active Warnings',
     'serverManagement.server': 'Server:',
     'serverManagement.serversVisible': 'Visible Servers',
     'serverManagement.available': 'Available',
     'serverManagement.baseLimit': 'Base Limit',
     'serverManagement.prestigeBonus': 'Prestige Bonus',
     'serverManagement.hiddenServers': 'Hidden Servers',
     'serverManagement.management': 'Server Management',
     'serverManagement.remaining': 'remaining',
     'serverManagement.hidden': 'hidden',
      'serverManagement.servers': 'servers',
      'serverManagement.server_single': 'server',
      // Admin Settings
     'admin.settings.registrationEnabled': 'Registrations open',
     'admin.settings.registrationEnabledDesc': 'Allows new users to register',
     'admin.imageOptimizer.title': 'Image Optimizer',
     'admin.imageOptimizer.description': 'Optimize all existing images to WebP format for better SEO performance',
     'admin.imageOptimizer.warning': 'This operation may take several minutes. All images will be converted to optimized WebP format.',
     'admin.imageOptimizer.startOptimization': 'Start Optimization',
     'admin.imageOptimizer.optimizing': 'Optimizing...',
     'admin.imageOptimizer.total': 'Total',
     'admin.imageOptimizer.optimized': 'Optimized',
     'admin.imageOptimizer.skipped': 'Skipped',
     'admin.imageOptimizer.failed': 'Failed',
     'admin.imageOptimizer.progress': 'Progress',
     'admin.imageOptimizer.errors': 'Errors',
     'admin.imageOptimizer.moreErrors': '... and {{count}} more errors',

     // Competitive Events
     'competitive_events_admin_menu': 'Competitive Events',
     'competitive_event_filter_title': 'Ongoing Competitive Event',
     'competitive_event_filter_button': 'View event ranking',
     'competitive_event_filter_active': 'Event ranking',
     'competitive_upcoming_title': 'Upcoming Event',
     'competitive_events_title': 'Competitive Event',
     'competitive_events_active': 'Ongoing event',
     'competitive_events_upcoming': 'Upcoming',
     'competitive_events_ended': 'Ended',
     'competitive_events_objective_progress': 'Progress towards objective',
     'competitive_events_current_ranking': 'Current ranking',
     'competitive_events_participants': 'participants',
     'competitive_events_winners': 'winners',
     'competitive_events_winner': 'Winner',
     'competitive_events_top_winners': 'Top {count} win {days} days of Prestige',
     'competitive_events_ended_success': 'Event completed - Objective reached!',
     'competitive_events_ended_failed': 'Event completed - Objective not reached',
     'competitive_events_objective_not_reached': 'Objective not reached - No reward',
     'competitive_events_no_reward': 'No reward',
     'competitive_events_prestige_days': 'Prestige days',
     'competitive_events_start_date': 'Start',
     'competitive_events_end_date': 'End',
     'competitive_events_event_name_all_games': 'Voting event for all games',
     'competitive_events_event_desc_all_games': 'Competitive voting event for all games on the platform. Participate and win Prestige by getting the most votes for your server!',
     'competitive_events_event_name_single_game': 'Voting event for {game}',
     'competitive_events_event_name_multi_games': 'Voting event for {count} games',
     'competitive_events_event_desc_single_game': 'Competitive voting event dedicated to {game} servers. Objective: {targetVotes} votes to win {prestigeDays} days of Prestige!',
     'competitive_events_event_desc_multi_games': 'Competitive voting event for {games}. Objective: {targetVotes} votes to win {prestigeDays} days of Prestige!',
      'competitive_events_starts_in': 'Starts in',

     // Game Category Pages
    'gameCategory.servers': '{gameName} SERVERS',
    'gameCategory.updatedRealTime': 'Ranking updated in real time',
    'gameCategory.noServersFound': 'No servers found',

    // Propose Tag Dialog
    'proposeTag.title': 'Propose a new tag',
    'proposeTag.description': 'Propose a new tag that will be reviewed by administrators before being added.',
    'proposeTag.nameLabel': 'Tag name *',
    'proposeTag.namePlaceholder': 'Ex: Hardcore, PvP, Roleplay...',
    'proposeTag.descriptionLabel': 'Description (optional)',
    'proposeTag.descriptionPlaceholder': 'Briefly describe what this tag represents...',
    'proposeTag.colorLabel': 'Color',
    'proposeTag.preview': 'Preview',
    'proposeTag.submitting': 'Submitting...',
    'proposeTag.submit': 'Propose',

    // Add Server Page
    'addServerPage.title': 'ADD A SERVER',
    'addServerPage.subtitle': 'Share your server with the community',
    'addServerPage.addMyServer': 'ADD MY SERVER',
    'addServerPage.whyAdd': 'Why add your server?',
    'addServerPage.benefit1': 'Increase your visibility to thousands of players',
    'addServerPage.benefit2': 'Receive daily votes from the community',
    'addServerPage.benefit3': 'Customize appearance with colors and images',
    'addServerPage.benefit4': 'Access detailed statistics',
    'addServerPage.prestigeOption': 'Prestige Option',
    'addServerPage.prestigeTitle': 'PRESTIGE',
    'addServerPage.prestigeFeature1': 'Priority ranking position and exclusive badge',
    'addServerPage.prestigeFeature2': 'Highlighted with custom colors and visual effects',
    'addServerPage.prestigeFeature3': '+1 additional server added to your limit',
    'addServerPage.prestigeFeature4': 'Bonus server automatically hidden if Prestige expires',
    'addServerPage.loginRequired': 'LOGIN REQUIRED',
    'addServerPage.mustBeLoggedIn': 'You must be logged in to add a server',
    'addServerPage.signupNow': 'Sign up now',
    'addServerPage.login': 'Login',
    'addServerPage.serverManagement': 'Server management',
    'addServerPage.visibleServers': 'Visible servers',
    'addServerPage.available': 'Available',
    'addServerPage.baseLimit': 'Base limit',
    'addServerPage.servers': 'servers',
    'addServerPage.prestigeTip': 'Prestige Tip',
    'addServerPage.prestigeDescription': 'Enable Prestige on a server to unlock +1 additional server slot!',
    'addServerPage.limitReached': 'You have reached your limit of',
    'addServerPage.prestigeBonus': 'This server will be created thanks to your Prestige bonus! It will be automatically hidden if your Prestige expires.',
    'addServerPage.whyAddEN': 'Why add your server?',
    'addServerPage.benefit1EN': 'Increase your visibility to thousands of players',
    'addServerPage.benefit2EN': 'Receive daily votes from the community',
    'addServerPage.benefit3EN': 'Customize appearance with colors and images',
    'addServerPage.benefit4EN': 'Access detailed statistics',
    'addServerPage.prestigeOptionEN': 'Prestige Option',
    'addServerPage.prestigeTitleEN': 'PRESTIGE',
    'addServerPage.prestigeFeature1EN': 'Priority position in ranking and exclusive badge',
    'addServerPage.prestigeFeature2EN': 'Highlighted with custom colors and visual effects',
    'addServerPage.prestigeFeature3EN': '+1 additional server added to your limit',
    'addServerPage.prestigeFeature4EN': 'Bonus server automatically hidden if Prestige expires',
    'addServerPage.loginRequiredEN': 'LOGIN REQUIRED',
    'addServerPage.mustBeLoggedInEN': 'You must be logged in to add a server',
    'addServerPage.signupNowEN': 'Sign up now',
    'addServerPage.loginEN': 'Log in',

    // Prestige Pass / Battle Pass
    'prestigePass.title': 'Prestige Pass',
    'prestigePass.subtitle': 'Earn free Prestige days through your votes',
    'prestigePass.free': 'FREE',
    'prestigePass.availableTokens': 'Available days',
    'prestigePass.totalVotes': 'Total votes',
    'prestigePass.currentMilestone': 'Current milestone',
    'prestigePass.nextMilestone': 'Next milestone',
    'prestigePass.votesRemaining': 'votes remaining',
    'prestigePass.myTokens': 'My Prestige days',
    'prestigePass.noTokensYet': 'No Prestige days yet',
    'prestigePass.earnTokensInfo': 'Accumulate 1000 votes to unlock 1 free Prestige day',
    'prestigePass.milestone': 'Milestone',
    'prestigePass.earnedAt': 'Earned at',
    'prestigePass.votes': 'votes',
    'prestigePass.applyToken': 'Use',
    'prestigePass.used': 'Used',
    'prestigePass.usedOn': 'Used on',
    'prestigePass.applyTokenTitle': 'Apply a Prestige day',
    'prestigePass.applyTokenDescription': 'Choose the server to apply this Prestige day to. It will be added to existing Prestige if the server already has some.',
    'prestigePass.selectServer': 'Select a server',
    'prestigePass.noServers': 'No servers available',
    'prestigePass.confirm': 'Confirm',

     // Leaderboard Page
     'leaderboard.title': 'VOTERS LEADERBOARDS',
     'leaderboard.subtitle': 'Discover the best voters in the gaming community',
     'leaderboard.addMyServer': 'Add my server',
     'leaderboard.loading': 'Loading...',
     'leaderboard.noVoters': 'No voters found',
     'leaderboard.topMonthly': 'Top Monthly Voters',
     'leaderboard.topWeekly': 'Top Weekly Voters',
     'leaderboard.topDaily': 'Top Daily Voters',
     'leaderboard.registeredUser': 'Registered User',
     'leaderboard.guestVoter': 'Guest Voter',

    // Filter Section
    'filter.filterByGame': 'Filter by Game',
    'filter.allGames': 'All Games',
    'filter.sortBy': 'Sort by:',
    'filter.topVotes': 'Top Votes',
    'filter.mostPopular': 'Most Popular',
    'filter.recent': 'Recent',
    'filter.activeFilter': 'Active filter:',
    'filter.clickToRemove': 'Click to remove filter',

    // Vote System
    'vote.alreadyVoted': 'You have already voted!',
    'vote.waitTime': 'You can vote again in {hours} hour(s).',
    'vote.success': 'Vote recorded successfully!',
    'vote.failed': 'Error during voting.',
    'vote.every2hours': 'Vote every 2 hours',
    'vote.nextVoteIn': 'Next vote in',
	
	// Server List – Pagination
'serverList.page': 'Page {current} of {total}',
'serverList.display': '(Showing {from} to {to} of {count} servers)',

     // Image Upload
     'imageUpload.supportedFormats': 'Supported formats: JPG, PNG, WebP. Max size: 5MB',
     'imageUpload.dragDropText': 'Drop your description image here or',
     'imageUpload.selectFile': 'Select a file',
     'imageUpload.recommendedSize.logo': ' - Recommended size: 96x96px',
     'imageUpload.recommendedSize.banner': ' - Recommended size: 1200x400px',
     'imageUpload.recommendedSize.backdrop': ' - Recommended size: 1920x1080px',
     'imageUpload.recommendedSize.description': ' - Recommended size: 800x600px',
     'imageUpload.cropInfo': 'You can crop the image before upload',
     
     // Image Crop Dialog
     'imageCrop.title': 'Crop image',
     'imageCrop.cancel': 'Cancel',
     'imageCrop.confirm': 'Confirm',
     'imageCrop.processing': 'Processing...',

    // FAQ Section
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything you need to know about RankServeur',
    'faq.question1': 'How to vote for a server?',
    'faq.answer1': 'To vote for a server, simply click on the "Vote" button on the server page. You can vote once every 2 hours per server. Your votes help increase the server\'s visibility in our ranking.',
    'faq.question2': 'What is the Prestige Pass?',
    'faq.answer2': 'The Prestige Pass is a free rewards system. Every 1000 votes you accumulate, you unlock 1 day of Prestige that you can apply to your favorite servers. Prestige highlights servers in the ranking.',
    'faq.question3': 'How to add my server?',
    'faq.answer3': 'Click on the "Add a server" button in the main menu. Fill out the form with your server details (name, IP, description, logo). Your server will be reviewed by our moderators before appearing in the ranking.',
    'faq.question4': 'How often can I vote?',
    'faq.answer4': 'You can vote once every 2 hours per server. The cooldown timer is displayed on each server page. Votes reset every month, offering new ranking opportunities.',
    'faq.question5': 'What are competitive events?',
    'faq.answer5': 'Competitive events are temporary competitions where servers compete to get the most votes. Winners receive free Prestige days. These events are announced on the homepage.',
    'faq.question6': 'How does the monthly ranking work?',
     'faq.answer6': 'All server votes reset on the 1st of each month. This provides regular opportunities for new servers to gain visibility and for players to discover new servers.',

     // Admin Universal Importer
     'adminImport.title': 'Universal Server Import',
     'adminImport.description': 'Import servers from different sources',
     'adminImport.source': 'Import source',
     'adminImport.games': 'Games to import ({count} selected)',
     'adminImport.loadGames': 'Load games',
     'adminImport.loading': 'Loading...',
     'adminImport.servers': 'servers',
     'adminImport.limit': 'Number of servers to preview',
     'adminImport.preview': 'Preview servers',
     'adminImport.previewing': 'Previewing...',
     'adminImport.selectGame': 'Please select at least one game',
     'adminImport.gamesLoaded': '{count} available games',
     'adminImport.serversLoaded': '{count} servers loaded',
     'adminImport.noServersFound': 'No servers found',
     'adminImport.errorLoading': 'Error loading',
     'adminImport.errorPreview': 'Error during preview',
     'adminImport.errorImport': 'Error during import',

    // Admin App Versions
    'admin.accessDenied': 'Access denied',
    'admin.appVersions.title': 'Version Management',
    'admin.appVersions.newVersion': 'New version',
    'admin.appVersions.editVersion': 'Edit version',
    'admin.appVersions.allVersions': 'All versions',
    'admin.appVersions.version': 'Version',
    'admin.appVersions.platform': 'Platform',
    'admin.appVersions.size': 'Size',
    'admin.appVersions.file': 'File',
    'admin.appVersions.orManualUrl': 'or manual URL',
    'admin.appVersions.mandatory': 'Mandatory',
    'admin.appVersions.active': 'Active',
    'admin.appVersions.create': 'Create version',
    'admin.appVersions.update': 'Update',
    'admin.appVersions.uploadSuccess': 'File uploaded successfully',
    'admin.appVersions.uploadError': 'Upload error',
    'admin.appVersions.uploadLargeFileHint': 'For large .exe files, a resumable upload is used automatically.',
    'admin.appVersions.uploadExceededHint': 'The file exceeded the allowed limit or direct upload was rejected. Retry with the new resumable upload flow.',
    'admin.appVersions.missingFields': 'Version and URL required',
    'admin.appVersions.confirmDelete': 'Delete this version?',
    'admin.appVersions.noVersions': 'No versions published',
    'admin.appVersions.permanentUrl': 'Permanent link',
    'admin.appVersions.permanentUrlExplanation': 'The file is uploaded under a fixed name (latest.exe). The link never changes, only the content is replaced.',
    'admin.appVersions.permanentUrlTitle': 'Permanent download link',
    'admin.appVersions.permanentUrlDesc': 'Each upload replaces the latest.exe file. The launcher always uses the same link and checks the SHA-256 hash to determine if an update is needed.',
    'admin.appVersions.backupUrl': 'Backup URL (versioned)',
    'admin.appVersions.copied': 'Copied!',
    'admin.appVersions.pythonExampleTitle': 'Python Integration (Launcher)',
    'admin.appVersions.pythonExampleDesc': 'Example code to check and download updates from your Python launcher. The SHA-256 hash validates file integrity.',
    'admin.appVersions.setActive': 'Activate',
    'admin.appVersions.confirmActivate': 'Activate this version? The latest.exe file will be replaced with this version (rollback).',
    'admin.appVersions.activated': 'Version activated',
    'admin.appVersions.activateError': 'Activation error',
    'admin.appVersions.activateNoBackup': 'No versioned file available for this version (rollback not possible).',

    // Admin Discord Logs
    'admin.discordLogs.title': 'Discord Logs & Audit',
    'admin.discordLogs.subtitle': 'Full history of Discord actions: demotions, promotions, DMs, API errors, /link and /licence commands.',
    'admin.discordLogs.loading': 'Loading…',
    'admin.discordLogs.accessDenied': 'Access denied',
    'admin.discordLogs.accessDeniedDesc': 'This page is restricted to administrators.',
    'admin.discordLogs.runSync': 'Run sync',
    'admin.discordLogs.runReconcile': 'One-shot Discord audit',
    'admin.discordLogs.purge90': 'Purge > 90 days',
    'admin.discordLogs.purgeAll': 'Purge all',
    'admin.discordLogs.purgeConfirmTitle': 'Confirm purge',
    'admin.discordLogs.purgeConfirmDesc90': 'Delete all entries older than 90 days? This cannot be undone.',
    'admin.discordLogs.purgeAllConfirmTitle': '⚠️ Delete everything',
    'admin.discordLogs.purgeAllConfirmDesc': 'Delete the entire Discord history. This cannot be undone.',
    'admin.discordLogs.confirmPurge': 'Purge',
    'admin.discordLogs.confirmPurgeAll': 'Delete all',
    'admin.discordLogs.cancel': 'Cancel',
    'admin.discordLogs.last24h': 'Last 24h',
    'admin.discordLogs.last7d': 'Last 7 days',
    'admin.discordLogs.last30d': 'Last 30 days',
    'admin.discordLogs.total': 'Total',
    'admin.discordLogs.filters': 'Filters',
    'admin.discordLogs.actionType': 'Action type',
    'admin.discordLogs.source': 'Source',
    'admin.discordLogs.status': 'Status',
    'admin.discordLogs.all': 'All',
    'admin.discordLogs.success': 'Success',
    'admin.discordLogs.failed': 'Failed',
    'admin.discordLogs.searchPlaceholder': 'Search Discord ID, username, license…',
    'admin.discordLogs.recentEntries': 'Recent entries',
    'admin.discordLogs.noResults': 'No results',
    'admin.discordLogs.col.date': 'Date',
    'admin.discordLogs.col.action': 'Action',
    'admin.discordLogs.col.source': 'Source',
    'admin.discordLogs.col.status': 'Status',
    'admin.discordLogs.col.discord': 'Discord',
    'admin.discordLogs.col.license': 'License',
    'admin.discordLogs.col.details': 'Details / error',
    'admin.discordLogs.viewMetadata': 'View metadata',
    'admin.discordLogs.exclusions.title': 'Purge exclusions',
    'admin.discordLogs.exclusions.subtitle': 'Discord IDs listed here are preserved during any purge (even "Purge all").',
    'admin.discordLogs.exclusions.discordIdLabel': 'Discord ID',
    'admin.discordLogs.exclusions.discordIdPlaceholder': '123456789012345678',
    'admin.discordLogs.exclusions.usernameLabel': 'Username (optional)',
    'admin.discordLogs.exclusions.noteLabel': 'Note (optional)',
    'admin.discordLogs.exclusions.add': 'Add',
    'admin.discordLogs.exclusions.remove': 'Remove',
    'admin.discordLogs.exclusions.empty': 'No exclusions configured.',
    'admin.discordLogs.exclusions.addedOn': 'Added on',

    // Common
    'common.optional': 'optional',

    // Admin License Pricing
    'admin.pricing.title': 'License Pricing',
    'admin.pricing.subtitle': 'Configure prices applied to Stripe payments and shown in Discord.',
    'admin.pricing.licensesTitle': 'Stripe pricing (Software licenses)',
    'admin.pricing.monthly': 'Monthly price',
    'admin.pricing.monthlyHelp': 'Price applied for a 1-month license.',
    'admin.pricing.yearly': 'Yearly price',
    'admin.pricing.yearlyHelp': 'Price applied for a 1-year license.',
    'admin.pricing.save': 'Save',
    'admin.pricing.saved': 'Pricing saved',
    'admin.pricing.savedDesc': 'New prices apply immediately to future purchases.',
    'admin.pricing.invalidAmount': 'Invalid amount',
    'admin.pricing.warning': 'Already initiated payments keep their old price. Only future payments will use the new amounts.',

    // Admin Discord Tickets
    'admin.tickets.title': 'Discord Tickets',
    'admin.tickets.subtitle': 'Configure automatic creation of private support channels.',
    'admin.tickets.configTitle': 'Ticket system configuration',
    'admin.tickets.enabled': 'Enable tickets',
    'admin.tickets.enabledHelp': 'If disabled, the /ticket open command will no longer create channels.',
    'admin.tickets.guildId': 'Discord Server ID (Guild ID)',
    'admin.tickets.categoryId': 'Category ID',
    'admin.tickets.categoryHelp': 'Discord category where ticket channels will be created.',
    'admin.tickets.staffRoleId': 'Staff Role ID',
    'admin.tickets.staffRoleHelp': 'Role that will automatically gain access to new tickets.',
    'admin.tickets.intro': 'Intro message',
    'admin.tickets.introHelp': 'Available variables: {user}, {user_id}, {staff_role}, {purchase_token}.',
    'admin.tickets.botPerm': 'The bot must have "Manage Channels" and "View Channels" permissions in the configured category.',
    'admin.tickets.save': 'Save',
    'admin.tickets.permsRequiredIntro': 'Discord permissions required for the bot:',
    'admin.tickets.permsWhere': 'Where to configure them:',
    'admin.tickets.registerCmdTitle': 'Discord slash commands',
    'admin.tickets.registerCmdHelp': 'Registers/updates the /licence and /ticket commands on your Discord server. Click after every change to the command list.',
    'admin.tickets.registerCmdBtn': 'Register Discord commands',
    'admin.tickets.commandsRegistered': 'Discord commands registered',
    'admin.tickets.saved': 'Configuration saved',

    // Admin Test Stripe
    'admin.testStripe.title': 'Stripe fulfillment test',
    'admin.testStripe.subtitle': 'Simulate a successful Stripe payment to test license delivery (no real charge).',
    'admin.testStripe.formTitle': 'Test parameters',
    'admin.testStripe.discordUserId': 'Discord User ID',
    'admin.testStripe.discordHelp': 'Discord ID (snowflake) that will receive the delivery DM.',
    'admin.testStripe.discordUsername': 'Discord Username',
    'admin.testStripe.licenseType': 'License type',
    'admin.testStripe.monthly': '1 month',
    'admin.testStripe.yearly': '1 year',
    'admin.testStripe.run': 'Run test',
    'admin.testStripe.success': 'Test successful',
    'admin.testStripe.successDesc': 'License created and DM sent to the Discord user.',
    'admin.testStripe.missingUser': 'Discord User ID is required',
    'admin.testStripe.noCharge': 'No real charge is made. This action only simulates the Stripe webhook to test license creation and DM delivery.',

    // Admin – License Purchases
    'admin.licensePurchases.title': 'Purchased licenses',
    'admin.licensePurchases.subtitle': 'Track Discord (/licence buy) orders and deliveries.',
    'admin.licensePurchases.configMethods': 'Payment methods',
    'admin.licensePurchases.search': 'Search',
    'admin.licensePurchases.method': 'Method',
    'admin.licensePurchases.empty': 'No orders.',
    'admin.licensePurchases.fulfillTitle': 'Deliver license',
    'admin.licensePurchases.externalRef': 'External reference (transaction ID)',
    'admin.licensePurchases.adminNotes': 'Admin notes',
    'admin.licensePurchases.fulfillHelp': 'Generates the license key + download link (72h, max 5 uses), marks the order as "fulfilled" and automatically DMs the buyer on Discord. Use ONLY after payment verification.',
    'admin.licensePurchases.confirmFulfill': 'Deliver & send DM',
    'admin.licensePurchases.cancel': 'Cancel',
    'admin.licensePurchases.cancelTitle': 'Cancel order',
    'admin.licensePurchases.cancelHelp': 'Marks the order as "cancelled". Use if payment was never received or the buyer abandoned. No DM is sent. Irreversible action.',
    'admin.licensePurchases.linkTitle': 'New download link',
    'admin.licensePurchases.linkHelp': 'Generates a brand-new download link (the old one stops working). You can send it via Discord DM and/or copy it to share it yourself.',
    'admin.licensePurchases.linkValidity': 'Validity (hours)',
    'admin.licensePurchases.linkMaxUses': 'Allowed downloads',
    'admin.licensePurchases.linkSendDm': 'Also send the Discord DM',
    'admin.licensePurchases.linkGenerate': 'Generate link',
    'admin.licensePurchases.linkResult': 'Generated link',
    'admin.licensePurchases.linkCopy': 'Copy link',
    'admin.licensePurchases.linkExpires': 'Expires on',
    'admin.licensePurchases.cancelReason': 'Cancellation reason (internal)',
    'admin.licensePurchases.confirmCancel': 'Confirm cancellation',
    'admin.licensePurchases.cancelled': 'Order cancelled',
    'admin.licensePurchases.cancelOnlyPending': 'Only undelivered orders can be cancelled.',

    'admin.licenses.buyToggle.title': 'Discord /licence buy command',
    'admin.licenses.buyToggle.description': 'Enable or disable the ability for Discord users to purchase a license via /licence buy. When disabled, they receive a message that purchases are temporarily unavailable.',
    'admin.licenses.buyToggle.enabled': 'Enabled — users can purchase',
    'admin.licenses.buyToggle.disabled': 'Disabled — purchases blocked',
    'admin.licenses.buyToggle.updated': 'Setting updated',

    'admin.paymentMethods.autoTicketInfo': '✅ Auto-ticket enabled: when a manual method (PayPal, Skrill, Revolut, Coinbase) is selected in /licence buy, a Discord ticket is automatically created with order context for staff. Buyers no longer need to type /ticket open.',

    // Admin — VM (Discord /vm buy)
    'admin.vm.title': 'VM — Discord /vm buy',
    'admin.vm.subtitle': 'Manage the VM purchase flow (manual PayPal payment only)',
    'admin.vm.enabledLabel': '/vm buy command enabled',
    'admin.vm.enabledHelp': 'When disabled, Discord users receive a message saying VM purchase is temporarily unavailable.',
    'admin.vm.priceMember': 'Pixfus Member price',
    'admin.vm.priceMemberHelp': 'Automatically applied to users with the Pixfus Member Discord role.',
    'admin.vm.priceStandard': 'Standard price',
    'admin.vm.priceStandardHelp': 'Applied to users without the Member role (or if detection fails).',
    'admin.vm.productLabel': 'Product name',
    'admin.vm.productLabelHelp': 'Shown in the ticket and payment instructions.',
    'admin.vm.paypalEmail': 'PayPal email',
    'admin.vm.paypalEmailHelp': 'PayPal email address shown to the buyer in the ticket for the manual "Friends & Family" payment.',
    'admin.vm.staffTitle': 'Discord staff assigned to VM tickets',
    'admin.vm.staffHelp': 'Discord UIDs automatically invited into every /vm buy ticket (they get channel access and are mentioned).',
    'admin.vm.staffAdd': 'Add',
    'admin.vm.staffEmpty': 'No Discord staff assigned — only the global staff role will be notified.',
    'admin.vm.staffIdInvalid': 'Invalid Discord UID (digits only)',
    'admin.vm.staffIdDuplicate': 'This UID is already in the list',
    'admin.vm.roleInfoTitle': 'Member role detection',
    'admin.vm.roleInfoBody': 'The Discord role used to identify Pixfus Members is set via the edge function secret DISCORD_ROLE_MEMBRES_ID. If the role is not present on the user, the standard price is applied automatically.',
    'admin.vm.save': 'Save',
    'admin.vm.reload': 'Reload',
    'admin.vm.saved': 'VM settings saved',
    'admin.vm.saveError': 'Save failed',
    'admin.vm.loadError': 'Load failed',
    'admin.seo.discordAlertsLabel': 'Discord alerts',
    'admin.seo.discordAlertsEnabled': 'GSC Discord alerts enabled',
    'admin.seo.discordAlertsDisabled': 'GSC Discord alerts disabled',
    'admin.seo.discordToggleError': 'Failed to update setting',

  },
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.topVoters': 'Top Votantes',
    'nav.favorites': 'Favoritos',
    'nav.myVotes': 'Mis Votos',
    'nav.creatorTools': 'Herramientas Creadores',
    'nav.profile': 'Perfil',
    'nav.admin': 'Administración',
    'nav.logout': 'Cerrar sesión',
    'nav.login': 'Iniciar sesión',
    'nav.signup': 'Registrarse',
    'nav.addServer': 'Agregar Servidor',

    // Notificaciones comunes
    'notifications.auth.signedIn': 'Inicio de sesión exitosa',
    'notifications.auth.welcome': '¡Bienvenido a RankServeur!',
    'notifications.auth.signedOut': 'Cerrar sesión',
    'notifications.auth.goodbye': '¡Hasta pronto!',
    'notifications.auth.loginError': 'Error de inicio de sesión',
    'notifications.auth.signupError': 'Error de registro',
    'notifications.auth.signupSuccess': 'Registro exitoso',
    'notifications.auth.signupDesc': 'Comprueba tu correo electrónico para confirmar tu cuenta.',
    'notifications.auth.logoutError': 'Error al cerrar sesión',
    'notifications.error': 'Error',
    'notifications.success': 'Éxito',
    'notifications.serverReassigned': 'Servidor reasignado',
    'notifications.serverReassignedDesc': 'El propietario ha sido actualizado.',
    'notifications.cannotReassign': 'No se puede reasignar',

    
    // Hero Section
    'hero.title': 'Descubre los Mejores Servidores Gaming',
    'hero.title2': 'SERVIDORES PRIVADOS',
    'hero.subtitle': '¡Vota por tus servidores favoritos y escala en el ranking!',
    'hero.subtitle2': 'Una plataforma comunitaria para explorar y votar diariamente por los servidores privados que te apasionan.',
    'hero.search.placeholder': 'Buscar un servidor...',
    'hero.addServer': 'Añadir servidor',
    
    // Stats Section
    'stats.title': 'Estadísticas en Tiempo Real',
    'stats.description': 'Sigue la actividad de la comunidad gaming',
    'stats.servers': 'Servidores',
    'stats.votes': 'Votos Totales',
    'stats.users': 'Usuarios',
    'stats.activeServers': 'Servidores Activos',
    'stats.totalVotes': 'Votos Totales',
    'stats.totalUsers': 'Usuarios',
    'stats.globalStats': 'Estadísticas Globales',
    'stats.gameStats': 'Estadísticas de {gameName}',
    'stats.realTimeData': 'Datos en tiempo real',
    
    // Monthly Reset
    'reset.title': 'Próximo Reset de Votos',
    'reset.description': 'Los votos se reinician cada mes',
    'reset.resetIn': 'Reset en:',
    'reset.monthlyResetTitle': 'Reset Mensual de Votos',
    'reset.monthlyReset': 'Reset Mensual',
    'reset.description2': 'Todos los votos de servidores se reiniciarán el 1er día del próximo mes',
    'reset.newOpportunities': '¡Nuevo ranking, nuevas oportunidades!',
    'reset.days': 'días',
    'reset.hours': 'horas',
    'reset.minutes': 'minutos',
    'reset.seconds': 'segundos',
    
    // Add Server Dialog
    'addServer.title': 'Agregar Servidor',
    'addServer.subtitle': 'Únete a nuestra comunidad gaming',
    'addServer.freeSubmission': 'Envío gratuito',
    'addServer.prestigeFeatures': 'Funciones Prestige disponibles',
    'addServer.basicInfo': 'INFORMACIÓN BÁSICA',
    'addServer.serverName': 'Nombre del servidor',
    'addServer.serverNamePlaceholder': 'Ej: MiServidor Minecraft',
    'addServer.gameType': 'Tipo de juego',
    'addServer.selectGame': 'Seleccionar un juego...',
    'addServer.description': 'Descripción',
    'addServer.descriptionPlaceholder': 'Describe tu servidor en pocas palabras...',
    'editor.placeholder': 'Describe tu servidor, sus características...',
    'addServer.presentationImage': 'Imagen de presentación (opcional)',
    'addServer.presentationImageDesc': 'Esta imagen se mostrará en la tarjeta de tu servidor',
    'addServer.serverLogo': 'LOGO DEL SERVIDOR',
    'addServer.serverLogoDesc': 'Logo mostrado junto al nombre (opcional)',
    'addServer.techConfig': 'CONFIGURACIÓN TÉCNICA',
    'addServer.technicalConfig': 'CONFIGURACIÓN TÉCNICA',
    'addServer.serverIP': 'Dirección IP',
    'addServer.ipAddress': 'Dirección IP',
    'addServer.ipPlaceholder': 'Ej: play.miservidor.com',
    'addServer.version': 'Versión',
    'addServer.versionPlaceholder': 'Ej: 1.20.1',
    'addServer.community': 'ENLACES DE COMUNIDAD',
    'addServer.communityLinks': 'ENLACES DE COMUNIDAD',
    'addServer.discord': 'Discord (opcional)',
    'addServer.discordUrl': 'Discord (opcional)',
    'addServer.discordPlaceholder': 'https://discord.gg/...',
    'addServer.website': 'Sitio Web (opcional)',
    'addServer.websiteUrl': 'Sitio Web (opcional)',
    'addServer.websitePlaceholder': 'https://miservidor.com',
    'addServer.serverTags': 'ETIQUETAS DEL SERVIDOR',
    'addServer.prestigeTitle': 'FUNCIONES PRESTIGE',
    'addServer.prestigeInfo': 'FUNCIONES PRESTIGE',
    'addServer.prestigeDesc': 'Aumenta la visibilidad de tu servidor',
    'addServer.prestigeNote': 'Disponible después de la creación del servidor',
    'addServer.prestigeProfileNote': 'Ve a tu perfil para activar el estado Prestige de tus servidores.',
    'addServer.addServerButton': 'Agregar servidor',
    'addServer.limitAlert': 'Límite alcanzado',
    'addServer.cancel': 'Cancelar',
    'addServer.submit': 'Agregar servidor',
    
    // Profile Page
    'profile.title': 'MI PERFIL',
    'profile.myServers': 'Mis Servidores',
    'profile.arcadeSpots': 'Arcade Spots',
    'profile.myReviews': 'Mis Reseñas',
    'profile.payments': 'Pagos',
    'profile.settings': 'Configuración',
    'profile.tabs.profile': 'Perfil',
    'profile.tabs.security': 'Seguridad',
    'profile.tabs.danger': 'Zona Peligrosa',
    'profile.profileInfo.title': 'Información del Perfil',
    'profile.profileInfo.description': 'Modifica tu información personal',
    'profile.profileInfo.emailLabel': 'Dirección de correo',
    'profile.profileInfo.usernameLabel': 'Nombre de usuario',
    'profile.profileInfo.usernamePlaceholder': 'Tu nombre de usuario',
    'profile.profileInfo.updateButton': 'Actualizar perfil',
    'profile.profileInfo.updating': 'Actualizando...',
    'profile.security.title': 'Cambiar Contraseña',
    'profile.security.description': 'Asegúrate de usar una contraseña fuerte y única',
    'profile.security.currentPasswordLabel': 'Contraseña actual',
    'profile.security.currentPasswordPlaceholder': 'Tu contraseña actual',
    'profile.security.newPasswordLabel': 'Nueva contraseña',
    'profile.security.newPasswordPlaceholder': 'Tu nueva contraseña',
    'profile.security.confirmPasswordLabel': 'Confirmar contraseña',
    'profile.security.confirmPasswordPlaceholder': 'Confirma tu nueva contraseña',
    'profile.security.changePasswordButton': 'Cambiar contraseña',
    'profile.security.changing': 'Actualizando...',
    'profile.security.requirements': 'La contraseña debe contener al menos 6 caracteres',
    'profile.danger.title': 'Zona Peligrosa',
    'profile.danger.description': 'Acciones irreversibles en tu cuenta',
    'profile.danger.deleteAccount': 'Eliminar mi cuenta',
    'profile.danger.deleteDescription': 'Esta acción eliminará permanentemente tu cuenta y todos tus datos. Esta acción es irreversible.',
    'profile.danger.deleteConfirm': '¿Estás absolutamente seguro de que quieres eliminar tu cuenta?',
    'profile.danger.deleteButton': 'Sí, eliminar mi cuenta',
    'profile.danger.cancel': 'Cancelar',
    'profile.danger.deleting': 'Eliminando...',
    'profile.emailNotEditable': 'El email no se puede modificar',
    'profile.passwordsDontMatch': 'Las contraseñas no coinciden',
    'profile.serverManagement': 'Gestión de servidores',
    'profile.visibleServers': 'Servidores visibles',
    'profile.available': 'Disponibles',
    'profile.baseLimit': 'Límite base',
    'profile.servers': 'Servidores',
    'profile.prestigeTip': 'Consejo Prestige',
    'profile.prestigeDesc': '¡Activa Prestige en un servidor para desbloquear +1 slot adicional de servidor!',
    'profile.noServersYet': 'No has agregado ningún servidor aún.',
    'profile.serverTotalVotes': 'Total de Votos del Servidor',
    'profile.userTotalVotes': 'Total de Votos Realizados',
    'profile.prestige': 'Prestige',
    
    // Search
    'search.placeholder': 'Buscar un servidor...',
    'search.noResults': 'No se encontraron servidores para "{query}"',
    'search.votes': 'votos',
    
    // Tags and Filters
    'tags.loading': 'Cargando etiquetas...',
    'tags.searchPlaceholder': 'Buscar una etiqueta...',
    'tags.description': 'Selecciona las etiquetas que mejor describan tu servidor',
    'tags.selected': 'Etiquetas seleccionadas',
    'tags.available': 'Etiquetas disponibles',
    'tags.proposeTag': 'Proponer etiqueta',
    'tags.filterByTags': 'Filtrar por etiquetas',
    'tags.clearAll': 'Limpiar',
    'tags.noTagsFound': 'No se encontraron etiquetas',
    'tags.anlaibe': 'Anlaibe',
    'tags.casual': 'Casual',
    'tags.communaute': 'Comunidad',
    'tags.competitif': 'Competitivo',
    'tags.creatif': 'Creativo',
    'tags.debutant': 'Principiante',
    'tags.economie': 'Economía',
    'tags.enUS': 'EN-US',
    'tags.evenements': 'Eventos',
    'tags.frFR': 'FR-FR',
    'tags.fun': 'Diversión',
    'tags.hardcore': 'Hardcore',
    'tags.moreVotes': 'Más votos',
    'tags.morePopular': 'Más populares',
    'tags.latestServers': 'Últimos servidores',
    'tags.joinMoreServers': 'Únete a más de {count} servidores en nuestra plataforma',
    
    // Game Proposals
    'game.proposal.proposeGame': 'Proponer un juego',
    'game.proposal.gameName': 'Nombre del juego',
    'game.proposal.gameNamePlaceholder': 'Ej: Minecraft, Dofus...',
    'game.proposal.officialLink': 'Enlace oficial',
    'game.proposal.comment': 'Comentario',
    'game.proposal.commentPlaceholder': '¿Por qué debería añadirse este juego?',
    'game.proposal.description': 'Propón un nuevo juego para añadir a RankServeur. Tu propuesta será revisada por un administrador.',
    'game.proposal.success': '¡Propuesta enviada!',
    'game.proposal.successMessage': 'Tu propuesta de juego ha sido enviada y será revisada por un administrador.',
    'game.proposal.error': 'Error',
    'game.proposal.nameRequired': 'El nombre del juego es obligatorio',
    'game.proposal.adminTitle': 'Propuestas de Juegos',
    'game.proposal.adminDescription': 'Gestiona las propuestas de juegos enviadas por los usuarios',
    'game.proposal.pending': 'Pendientes',
    'game.proposal.approved': 'Aprobadas',
    'game.proposal.rejected': 'Rechazadas',
    'game.proposal.submittedBy': 'Propuesto por',
    'game.proposal.unknownUser': 'Usuario desconocido',
    'game.proposal.adminNotes': 'Notas del administrador',
    'game.proposal.approve': 'Aprobar',
    'game.proposal.reject': 'Rechazar',
    'game.proposal.approvedMessage': 'La propuesta ha sido aprobada.',
    'game.proposal.rejectedMessage': 'La propuesta ha sido rechazada.',
    'game.proposal.rejectReasonRequired': 'Por favor proporciona una razón para el rechazo',
    'game.proposal.rejectConfirmTitle': 'Rechazar propuesta',
    'game.proposal.rejectConfirmDescription': 'Por favor proporciona una razón para rechazar esta propuesta.',
    'game.proposal.rejectReasonPlaceholder': 'Razón del rechazo...',
    'game.proposal.confirmReject': 'Confirmar rechazo',
    'game.proposal.noPending': 'No hay propuestas pendientes',
    'game.proposal.noApproved': 'No hay propuestas aprobadas',
    'game.proposal.noRejected': 'No hay propuestas rechazadas',
    
     // Filters
     'filters.sortBy': 'Ordenar por',
     'filters.votes': 'Votos',
     'filters.newest': 'Más reciente',
     'filters.oldest': 'Más antiguo',
     'filters.clearAll': 'Limpiar todo',
     'filters.proposeTag': 'Proponer etiqueta',
     'filters.tags': 'Filtrar por etiquetas',
     'filters.prestigeFilter': 'Filtro Prestige',
     'filters.allServers': 'Todos los servidores',
     'filters.prestigeOnly': 'Solo Prestige',
     'filters.regularOnly': 'Servidores regulares',
    
    // Server Card
    'server.vote': 'Votar',
    'server.report': 'Reportar',
    'server.online': 'En línea',
    'server.offline': 'Desconectado',
    'server.players': 'jugadores',
    'server.sponsored': 'Patrocinado',
    'server.gold': 'Gold',
    'server.rank': 'Rango',
    'server.api': 'API',
    
    // Footer
    'footer.description': 'La mejor plataforma para descubrir y votar por los servidores privados de juegos más populares.',
    'footer.madeWith': 'Hecho con pasión para la comunidad gaming',
    'footer.statsTitle': 'Estadísticas en Tiempo Real',
    'footer.serversListed': 'servidores listados',
    'footer.votesToday': 'votos hoy',
    'footer.serversOnline': 'servidores en línea',
    'footer.updatedRealTime': 'Actualizado en tiempo real',
    'footer.partnersTitle': 'Socios',
    'footer.linksTitle': 'Enlaces',
    'footer.links': 'Enlaces útiles',
    'footer.legal': 'Legal',
    'footer.contact': 'Contacto',
    'footer.termsOfService': 'Términos de Servicio',
    'footer.termsOfSale': 'Términos de Venta',
    'footer.privacy': 'Política de Privacidad',
    'footer.copyright': 'Todos los derechos reservados.',
    'footer.discordBot': 'Bot de Discord',
    
    // Discord Bot Page
    'discordBot.title': 'Bot de Discord RankServeur',
    'discordBot.metaDescription': 'Descubre todos los comandos del bot de Discord RankServeur. Consulta rankings, vota y busca servidores de juego directamente desde Discord.',
    'discordBot.heroTitle': 'Bot de Discord',
    'discordBot.heroSubtitle': 'Consulta rankings, vota y busca servidores de juego directamente desde tu servidor de Discord.',
    'discordBot.addToDiscord': 'Añadir a Discord',
    'discordBot.viewSource': 'Código fuente',
    'discordBot.commandsTitle': 'Comandos disponibles',
    'discordBot.commandsSubtitle': 'Todos los comandos slash del bot RankServeur',
    'discordBot.cmd.top': '/top',
    'discordBot.cmd.topDesc': 'Muestra el top de servidores por votos mensuales. Filtra por juego y limita resultados.',
    'discordBot.cmd.topUsage': '/top [game:minecraft] [limit:10]',
    'discordBot.cmd.server': '/server',
    'discordBot.cmd.serverDesc': 'Muestra info detallada del servidor: IP, versión, jugadores en línea, tags, enlaces.',
    'discordBot.cmd.serverUsage': '/server name:MiServidor',
    'discordBot.cmd.stats': '/stats',
    'discordBot.cmd.statsDesc': 'Muestra estadísticas detalladas del servidor con gráfico de votos de 7 días.',
    'discordBot.cmd.statsUsage': '/stats name:MiServidor',
    'discordBot.cmd.vote': '/vote',
    'discordBot.cmd.voteDesc': 'Genera un enlace directo para votar por un servidor. Un voto cada 2 horas.',
    'discordBot.cmd.voteUsage': '/vote name:MiServidor',
    'discordBot.cmd.search': '/search',
    'discordBot.cmd.searchDesc': 'Busca un servidor por nombre. Devuelve resultados con enlaces directos.',
    'discordBot.cmd.searchUsage': '/search query:minecraft pvp',
    'discordBot.cmd.games': '/games',
    'discordBot.cmd.gamesDesc': 'Lista todos los juegos disponibles en RankServeur.',
    'discordBot.cmd.gamesUsage': '/games',
    'discordBot.cmd.serveroftheday': '/serveroftheday',
    'discordBot.cmd.serverofthedayDesc': 'Muestra el servidor del día — el que tiene más votos hoy.',
    'discordBot.cmd.serverofthedayUsage': '/serveroftheday',
    'discordBot.hostingTitle': 'Alojamiento',
    'discordBot.hostingDesc': 'El bot necesita alojamiento Node.js. Aquí hay opciones gratuitas:',
    'discordBot.hosting.railway': 'Railway — 500h/mes gratis',
    'discordBot.hosting.render': 'Render — Alojamiento gratuito (con cold starts)',
    'discordBot.hosting.replit': 'Replit — Gratis con limitaciones',
    'discordBot.hosting.wispbyte': 'WispByte — Alojamiento gratuito para bots Discord',
    'discordBot.hosting.selfhost': 'Auto-alojamiento — Ejecuta node bot.js en tu propia máquina',
    'discordBot.languageTitle': 'Multilingüe',
    'discordBot.languageDesc': 'El bot detecta automáticamente el idioma de tu Discord (FR, EN, ES) y responde en consecuencia.',
    'discordBot.required': 'Requerido',
    'discordBot.optional': 'Opcional',
    'discordBot.downloadSources': 'Descargar fuentes',
    'discordBot.perfTitle': 'Optimizado para alojamiento gratuito',
    'discordBot.perfDesc': 'El bot usa solo 1 intent de Discord (Guilds) y hace llamadas API ligeras. Funciona fácilmente en planes gratuitos (128 MB RAM, 25% CPU).',
    'discordBot.securityTitle': 'Código fuente seguro',
    'discordBot.securityDesc': 'El código fuente NO contiene claves API ni contraseñas. Cada usuario debe crear su propio archivo .env con sus credenciales de Discord. Compartir es 100% seguro.',
    'discordBot.securityDesc2': 'El código fuente es 100% seguro para compartir. NO contiene secretos. La API de RankServeur es pública (solo lectura) — no necesitas clave API. Solo necesitas tu propio Token y Application ID de Discord.',
    'discordBot.setupTitle': 'Instalación en 4 pasos',
    'discordBot.setupSubtitle': 'Aloja tu propia instancia del bot RankServeur',
    'discordBot.step1Title': 'Copiar archivos fuente',
    'discordBot.step1Desc': 'Crea una carpeta y copia estos archivos:',
    'discordBot.stepCreateFolder': 'Crea una carpeta, luego copia y pega cada archivo. Haz clic para expandir, luego copia con el botón.',
    'discordBot.step2Title': 'Crear una aplicación Discord',
    'discordBot.step2Desc': 'Ve al Discord Developer Portal → New Application → Bot → copia el Token + Application ID.',
    'discordBot.step3Title': 'Crear el archivo .env',
    'discordBot.step3Desc': 'Crea un archivo .env en la raíz del proyecto con tus propias credenciales:',
    'discordBot.step3Desc2': 'Crea un archivo .env con solo tu Token de Discord y tu Application ID:',
    'discordBot.step3Note': 'NUNCA compartas tu archivo .env.',
    'discordBot.noApiKeyNeeded': '¡No necesitas clave API de RankServeur! La API es pública y de solo lectura.',
    'discordBot.step4Title': 'Instalar y ejecutar',
    'discordBot.step4npm': 'Instalar dependencias',
    'discordBot.step4deploy': 'Registrar comandos Discord',
    'discordBot.step4start': 'Iniciar el bot',
    'discordBot.voteNotifTitle': 'Notificaciones de votos',
    'discordBot.voteNotifDesc': 'Recibe automáticamente una notificación en tu canal de Discord cada vez que alguien vota por tu servidor. Basado en Server UUID para evitar conflictos.',
    'discordBot.voteNotifHow': 'Configura un webhook de Discord en la configuración de tu servidor en RankServeur.',
    'discordBot.webhookSetupTitle': '📋 Cómo configurar en 3 pasos:',
    'discordBot.webhookStep1': 'En Discord: Ajustes del servidor → Integraciones → Webhooks → Crear Webhook → Copia la URL.',
    'discordBot.webhookStep2': 'En RankServeur: Ve a tu perfil → Mis servidores → Webhooks → Pega la URL de Discord y genera un secreto.',
    'discordBot.webhookStep3': '¡Listo! Cada voto enviará automáticamente una notificación a tu canal de Discord.',
    'discordBot.webhookPayloadTitle': 'Ejemplo de payload enviado en cada voto:',
    'discordBot.webhookSignatureInfo': 'Cada solicitud está firmada con HMAC-SHA256 (header X-RS-Signature) para verificar la autenticidad.',
    'discordBot.webhookAdvancedLink': '📖 Guía avanzada: verificación HMAC, ejemplos PHP/Node/Python → Documentación API',
    'discordBot.source.botDesc': 'Bot principal',
    'discordBot.source.deployDesc': 'Registro de comandos',
    'discordBot.source.apiDesc': 'Cliente API RankServeur',
    'discordBot.source.i18nDesc': 'Traducciones FR/EN/ES',
    'discordBot.source.packageDesc': 'Dependencias npm',
    'discordBot.sourceNote': 'Estos archivos no contienen secretos.',
    'footer.addServerCTA': '+ Agregar mi servidor gratis',
    
    // Legal pages titles
    'terms.title': 'Términos de Servicio',
    'terms.description': 'Términos y condiciones generales de uso de la plataforma RankServeur y reglas de uso de servicios',
    'terms.keywords': 'términos de servicio, regulaciones, plataforma, RankServeur',
    'termsOfSale.title': 'Términos de Venta',
    'termsOfSale.description': 'Términos y condiciones generales para espacios publicitarios RankServeur',
    'termsOfSale.keywords': 'términos de venta, espacios publicitarios, RankServeur',
    'privacy.title': 'Política de Privacidad',
    'privacy.description': 'Cómo recopilamos, usamos y protegemos sus datos personales',
    'privacy.keywords': 'privacidad, datos personales, GDPR, protección',

    // API Documentation
    'api.title': "Documentación API",
    'api.description': "API completa para creadores de servidores: estadísticas, reseñas, rankings y webhooks de voto en tiempo real",
    'api.keywords': "API desarrolladores, webhooks, estadísticas servidores, integración gaming",
    'api.whereToFind': "Dónde encontrar su clave API",
    'api.whereToFindDesc': "1. Inicie sesión en RankServeur\n2. Vaya a su Perfil\n3. Haga clic en 'Mis servidores'\n4. Seleccione su servidor\n5. Haga clic en el icono 'API' (<>)\n6. Copie su clave API",
    'api.webhookSetup': "Configuración de Webhooks",
    'api.webhookSetupDesc': "1. En 'Mis servidores'\n2. Haga clic en el icono Webhooks (⚙️ verde)\n3. Agregue la URL de su endpoint\n4. Seleccione eventos a recibir\n5. Pruebe la configuración",
    'api.security': "Seguridad API",
    'api.securityDesc': "• Mantenga su clave API secreta\n• Use solo HTTPS\n• Verifique la firma HMAC de webhooks\n• Limite el acceso a IPs necesarias",
    'api.gettingStarted': "Primeros Pasos",
    'api.gettingStartedDesc': "La API de RankServeur permite a los creadores de servidores integrar fácilmente el sistema de votación en sus launchers y sitios web.",
    'api.step1': "Paso 1: Obtener su clave API",
    'api.step2': "Paso 2: Configurar webhooks",
    'api.step3': "Paso 3: Integrar en su aplicación",
    'api.commonUse': "Casos de Uso Comunes",
    'api.commonUseDesc': "• Recompensar automáticamente jugadores que votan\n• Mostrar conteo de votos en tiempo real\n• Rastrear estadísticas de votación\n• Crear rankings personalizados",
    
    
    // Buttons
    'button.save': 'Guardar',
    'button.cancel': 'Cancelar',
    'button.refresh': 'Actualizar',
    'button.confirm': 'Confirmar',
    'button.close': 'Cerrar',
    'button.edit': 'Editar',
    'button.delete': 'Eliminar',
    'button.add': 'Añadir',
    'button.search': 'Buscar',
    'button.filter': 'Filtrar',
    
    // Games
    'games.allGames': 'Todos los juegos',
    'games.selectGame': 'Seleccionar un juego',
    
    // Competitive Events
    'competitive.showRanking': 'Clasificación del evento',
    'competitive.showingRanking': 'Clasificación del evento',
    
    // Index page
    'index.addMyServer': 'AGREGAR MI SERVIDOR',
    'index.joinServers': 'Únete a más de {count} servidores en nuestra plataforma',
    'index.joinListedServers': 'Únete a los servidores listados',
    'index.voteEvery2Hours': '¡Vota cada 2 horas por tus servidores favoritos!',
    'index.nextVoteIn': 'Próximo voto en: {hours}h {minutes}m',
    'index.canVoteNow': '¡Puedes votar ahora!',
    'index.topServers': 'TOP SERVIDORES',
    'index.rankingRealTime': 'Ranking actualizado en tiempo real ({count} servidor total)',
    
     // Common
     'common.loading': 'Cargando...',
     'common.error': 'Ha ocurrido un error',
     'common.noResults': 'No se encontraron resultados',
     'common.viewMore': 'Ver más',
     'common.viewLess': 'Ver menos',
     'common.create': 'Crear',
     'common.add': 'Añadir',
     'common.edit': 'Editar', 
     'common.delete': 'Eliminar',
     'common.cancel': 'Cancelar',
     'common.submit': 'Enviar',
     'common.submitting': 'Enviando...',
     'common.success': 'Éxito',
     'common.none': 'Ninguno',
     'common.all': 'Todos',
     'common.filter': 'Filtrar',
     'common.search': 'Buscar',
      'common.sort': 'Ordenar',
      'common.servers': 'Servidores Privados',
      'common.showMore': 'Ver más',
      'common.showLess': 'Ver menos',
      'common.available': 'Disponible',
      'common.notAvailable': 'No disponible',
     'common.noServersFound': 'No se encontraron servidores',
     'common.noServersWithFilters': 'No se encontraron servidores con estos filtros',
     'common.loadingServers': 'Cargando servidores...',
     'common.errorLoadingServers': 'Error al cargar servidores',
     
     // Favorites Page
     'favorites.title': 'MIS FAVORITOS',
     'favorites.subtitle': 'Tus servidores favoritos ({count})',
     'favorites.loginRequired': 'Debes iniciar sesión para ver tus favoritos.',
     'favorites.noFavoritesTitle': 'Aún no hay favoritos',
     'favorites.noFavoritesDesc': 'Agrega servidores a tus favoritos haciendo clic en el corazón en la lista de servidores.',
      
       // Tags
       'tags.selectedTags': 'Etiquetas seleccionadas',
       'tags.popular': 'Etiquetas populares',
       'tags.searchResults': 'Resultados de búsqueda',
       'tags.moreClicks': 'Más clics',
     
     // Server Management
     'serverManagement.loading': 'Cargando...',
     'serverManagement.serverDisabled': 'Servidor deshabilitado',
     'serverManagement.activeWarnings': 'Advertencias activas',
     'serverManagement.server': 'Servidor:',
     'serverManagement.serversVisible': 'Servidores visibles',
     'serverManagement.available': 'Disponibles',
     'serverManagement.baseLimit': 'Límite base',
     'serverManagement.prestigeBonus': 'Bonus Prestigio',
     'serverManagement.hiddenServers': 'Servidores ocultos',
     'serverManagement.management': 'Gestión de servidores',
     'serverManagement.remaining': 'restante',
     'serverManagement.hidden': 'oculto',
     'serverManagement.servers': 'servidores',
        'serverManagement.server_single': 'servidor',
        
     // Admin Settings
     'admin.settings.registrationEnabled': 'Registros abiertos',
     'admin.settings.registrationEnabledDesc': 'Permite que nuevos usuarios se registren',
     'admin.imageOptimizer.title': 'Optimizador de Imágenes',
     'admin.imageOptimizer.description': 'Optimiza todas las imágenes existentes a formato WebP para mejorar el rendimiento SEO',
     'admin.imageOptimizer.warning': 'Esta operación puede tardar varios minutos. Todas las imágenes se convertirán a formato WebP optimizado.',
     'admin.imageOptimizer.startOptimization': 'Iniciar Optimización',
     'admin.imageOptimizer.optimizing': 'Optimizando...',
     'admin.imageOptimizer.total': 'Total',
     'admin.imageOptimizer.optimized': 'Optimizadas',
     'admin.imageOptimizer.skipped': 'Omitidas',
     'admin.imageOptimizer.failed': 'Fallidas',
     'admin.imageOptimizer.progress': 'Progreso',
     'admin.imageOptimizer.errors': 'Errores',
     'admin.imageOptimizer.moreErrors': '... y {{count}} errores más',

     // Competitive Events
     'competitive_events_admin_menu': 'Eventos Competitivos',
     'competitive_event_filter_title': 'Evento Competitivo en curso',
         'competitive_event_filter_button': 'Ver clasificación evento',
         'competitive_event_filter_active': 'Clasificación evento',
         'competitive_upcoming_title': 'Evento próximo',
         'competitive_events_title': 'Evento Competitivo',
         'competitive_events_active': 'Evento en curso',
         'competitive_events_upcoming': 'Próximo',
         'competitive_events_ended': 'Terminado',
         'competitive_events_objective_progress': 'Progreso hacia el objetivo',
         'competitive_events_current_ranking': 'Clasificación actual',
         'competitive_events_participants': 'participantes',
         'competitive_events_winners': 'ganadores',
         'competitive_events_winner': 'Ganador',
         'competitive_events_top_winners': 'Los {count} mejores ganan {days} días de Prestigio',
         'competitive_events_ended_success': 'Evento completado - ¡Objetivo alcanzado!',
         'competitive_events_ended_failed': 'Evento completado - Objetivo no alcanzado',
         'competitive_events_objective_not_reached': 'Objetivo no alcanzado - Sin recompensa',
         'competitive_events_no_reward': 'Sin recompensa',
         'competitive_events_prestige_days': 'días de Prestigio',
         'competitive_events_start_date': 'Inicio',
         'competitive_events_end_date': 'Fin',
         'competitive_events_event_name_all_games': 'Evento de votos para todos los juegos',
         'competitive_events_event_desc_all_games': 'Evento de votos competitivos para todos los juegos de la plataforma. ¡Participa y gana Prestigio obteniendo los más votos para tu servidor!',
         'competitive_events_event_name_single_game': 'Evento de votos para {game}',
         'competitive_events_event_name_multi_games': 'Evento de votos para {count} juegos',
         'competitive_events_event_desc_single_game': 'Evento de votos competitivos dedicado a servidores {game}. Objetivo: {targetVotes} votos para ganar {prestigeDays} días de Prestigio!',
         'competitive_events_event_desc_multi_games': 'Evento de votos competitivos para {games}. Objetivo: {targetVotes} votos para ganar {prestigeDays} días de Prestigio!',
          'competitive_events_starts_in': 'Comienza en',
     
     // Arcade Spot Booking
    'arcade.title': 'Reservar un Arcade Spot',
    'arcade.subtitle': 'Reserva tu espacio publicitario premium',
    'arcade.position': 'Posición',
    'arcade.selectPosition': 'Seleccionar una posición',
    'arcade.selectDates': 'Seleccionar fechas',
    'arcade.uploadImage': 'Subir una imagen',
    'arcade.websiteUrl': 'URL del sitio web',
    'arcade.totalPrice': 'Precio total',
    'arcade.book': 'Reservar',
    'arcade.pricePerDay': '€/día',
    'arcade.selectPositionFirst': 'Por favor selecciona una posición',
    'arcade.selectDatesFirst': 'Por favor selecciona fechas',
    'arcade.uploadImageFirst': 'Por favor sube una imagen',
    'arcade.bookingSuccess': '¡Reserva completada con éxito!',
    'arcade.selectMonth': 'Todo el mes',
    'arcade.clearSelection': 'Borrar',
    
    // Terms of Sale
    'tos.title': 'Términos de Venta',
    'tos.lastUpdate': 'Última actualización:',
    'tos.object': '1. Objeto',
    'tos.objectText': 'Estos términos rigen la venta de espacios publicitarios (banners, insertos patrocinados, destacados) en el sitio web https://rankserveur.com, registrado bajo SIREN 989 131 347.',
    'tos.services': '2. Servicios ofrecidos',
    'tos.servicesText': 'El cliente puede reservar un espacio publicitario por una duración definida (por día, semana o mes). Los precios se muestran en el sitio y pueden variar según la ubicación y duración.',

    // Arcade Spots (homepage)
    'arcade.leftSpot': 'Arcade Spot Izquierdo',
    'arcade.centerSpot': 'Arcade Spot Central', 
    'arcade.rightSpot': 'Arcade Spot Derecho',
    'arcade.clickToReserve': 'Haz clic para reservar',
    'arcade.bookSpot': 'Reservar un Arcade Spot',
    'arcade.bookingDescription': 'Reserva tu espacio publicitario incluso si todos los spots están ocupados',
     'arcade.suspended': 'Suspendido',
     'arcade.visit': 'Visitar',
     'arcade.reserved': 'Reservado',
     'arcade.startsOn': 'Comienza el',

    // Arcade Spot Booking Dialog
    'arcade.booking.title': 'Reservar un Arcade Spot',
    'arcade.booking.positionLabel': 'Posición del Arcade Spot *',
    'arcade.booking.imageLabel': 'Imagen publicitaria *',
    'arcade.booking.imageFormat': '(formato óptimo: 512x288px)',
    'arcade.booking.option1': 'Opción 1: Subir imagen',
    'arcade.booking.maxSize': '(máx 5MB)',
    'arcade.booking.noFileChosen': 'Ningún archivo elegido',
    'arcade.booking.or': 'O',
    'arcade.booking.option2': 'Opción 2: URL de imagen',
    'arcade.booking.urlPlaceholder': 'https://ejemplo.com/imagen.jpg',
    'arcade.booking.websiteLabel': 'URL de destino (opcional)',
    'arcade.booking.websitePlaceholder': 'https://tu-sitio.com',
    'arcade.booking.selectPosition': 'Selecciona una posición',
    'arcade.booking.selectDates': 'Selecciona tus días * (haz clic para seleccionar/deseleccionar)',
    'arcade.booking.daysSelected': 'Días seleccionados',
    'arcade.booking.positionLeft': 'Posición Izquierda',
    'arcade.booking.positionCenter': 'Posición Central',
    'arcade.booking.positionRight': 'Posición Derecha',
    'arcade.booking.addAdmin': 'Añadir (Admin)',
    'arcade.booking.proceedPayment': 'Proceder al pago',
    'arcade.booking.totalPrice': 'Precio total',
    'arcade.booking.days': 'día(s)',
    'arcade.booking.processing': 'Procesando...',
     'arcade.booking.preview': 'Vista previa (512x288px recomendado)',
    
    // Sky Spot
    'skySpot.title': 'Sky Spot',
    'skySpot.clickToReserve': 'Haz clic para reservar',
    'skySpot.bookSpot': 'Reservar Sky Spot',
    'skySpot.bookingDescription': 'Publicidad vertical 160x600px',
    'skySpot.suspended': 'Suspendido',
    'skySpot.visit': 'Visitar',
    'skySpot.reserved': 'Reservado',
    'skySpot.startsOn': 'Comienza el',
    'skySpot.booking.title': 'Reservar Sky Spot',
    'skySpot.booking.selectDates': 'Selecciona tus días * (haz clic para seleccionar/deseleccionar)',
    'skySpot.booking.daysSelected': 'Días seleccionados',
    'skySpot.booking.imageLabel': 'Imagen publicitaria *',
    'skySpot.booking.imageFormat': '(formato óptimo: 160x600px)',
    'skySpot.booking.option1': 'Opción 1: Subir imagen',
    'skySpot.booking.maxSize': '(máx 5MB)',
    'skySpot.booking.or': 'O',
    'skySpot.booking.option2': 'Opción 2: URL de imagen',
    'skySpot.booking.urlPlaceholder': 'https://ejemplo.com/imagen.jpg',
    'skySpot.booking.websiteLabel': 'URL de destino (opcional)',
    'skySpot.booking.websitePlaceholder': 'https://tu-sitio.com',
    'skySpot.booking.totalPrice': 'Precio total',
    'skySpot.booking.days': 'día(s)',
    'skySpot.booking.processing': 'Procesando...',
    'skySpot.booking.preview': 'Vista previa (160x600px recomendado)',
    'skySpot.booking.addAdmin': 'Añadir (Admin)',
    'skySpot.booking.proceedPayment': 'Proceder al pago',

     // Games List
    'games.availableGames': 'JUEGOS DISPONIBLES',
    'games.top': 'TOP',
    'games.categories': 'CATEGORÍAS',
    'games.loadingGames': 'Cargando juegos...',
    'games.errorLoading': 'Error al cargar juegos',
    'games.noGamesAvailable': 'No hay juegos disponibles',
    'games.gamesCount': '{count} juegos disponibles',
    'games.viewAll': 'Ver todos los servidores de {gameName}',
    
    // Authentication Dialog
    'auth.title': 'AUTENTICACIÓN',
    'auth.signin': 'Iniciar Sesión',
    'auth.signup': 'Registrarse',
    'auth.reset': 'Restablecer',
    'auth.email': 'Email',
    'auth.password': 'Contraseña',
    'auth.username': 'Nombre de usuario',
    'auth.emailPlaceholder': 'tu@email.com',
    'auth.passwordPlaceholder': '••••••••',
    'auth.usernamePlaceholder': 'TuUsuario',
    'auth.signInButton': 'Iniciar Sesión',
    'auth.signUpButton': 'Registrarse',
    'auth.signInLoading': 'Iniciando sesión...',
    'auth.signUpLoading': 'Registrando...',
    'auth.forgotPassword': '¿Olvidaste tu contraseña?',
    'auth.resetPassword': 'Enviar enlace',
    'auth.resetLoading': 'Enviando...',
    'auth.resetDescription': 'Ingresa tu email para recibir un enlace de restablecimiento',
    'auth.backToSignin': 'Volver al inicio de sesión',
    'auth.or': 'o',
    'auth.googleSignIn': 'Continuar con Google',
    'auth.googleSignUp': 'Registrarse con Google',
    'auth.googleError': 'No se pudo iniciar sesión con Google',
    'resetpw.checking': 'Verificando el enlace...',
    'resetpw.title': 'Nueva contraseña',
    'resetpw.subtitle': 'Elige una nueva contraseña segura',
    'resetpw.new': 'Nueva contraseña',
    'resetpw.confirm': 'Confirmar contraseña',
    'resetpw.submit': 'Actualizar la contraseña',
    'resetpw.updating': 'Actualizando...',
    'resetpw.invalidTitle': 'Enlace no válido',
    'resetpw.invalidDesc': 'Este enlace de restablecimiento no es válido o ha caducado. Solicita uno nuevo.',
    'resetpw.mismatch': 'Las contraseñas no coinciden.',
    'resetpw.tooShort': 'La contraseña debe tener al menos 6 caracteres.',
    'resetpw.successTitle': 'Listo',
    'resetpw.successDesc': 'Tu contraseña se ha actualizado correctamente.',
    'resetpw.errorTitle': 'Error',
    'resetpw.genericError': 'Se ha producido un error durante la actualización.',

    'auth.error': 'Error',
    'auth.emailSent': 'Email enviado',
    'auth.emailSentDescription': 'Revisa tu correo para restablecer tu contraseña.',
    // Report Dialog
    'report.title': 'Reportar {type}',
    'report.titleServer': 'este servidor',
    'report.titleComment': 'este comentario',
    'report.description': 'Ayúdanos a mantener la calidad de la plataforma reportando contenido inapropiado.',
    'report.serverName': 'Reporte para: {serverName}',
    'report.reasonLabel': 'Motivo del reporte:',
    'report.reasons.inappropriate_content': 'Contenido inapropiado',
    'report.reasons.spam': 'Spam o publicidad',
    'report.reasons.fake_server': 'Servidor falso',
    'report.reasons.malicious_content': 'Contenido malicioso',
    'report.reasons.copyright': 'Violación de copyright',
    'report.reasons.other': 'Otro',
    'report.customReasonLabel': 'Especifica el motivo:',
    'report.customReasonPlaceholder': 'Describe el problema...',
    'report.cancel': 'Cancelar',
    'report.submit': 'Enviar reporte',
    'report.submitting': 'Enviando...',
    'report.selectReason': 'Por favor selecciona un motivo',
    'report.specifyReason': 'Por favor especifica el motivo',
    'report.success': 'Reporte enviado con éxito',
     'report.error': 'Error al enviar el reporte',

      // Server Detail Page
      'serverDetail.backToServers': 'Volver a servidores',
      'serverDetail.vote': 'Votar por este servidor',
      'serverDetail.website': 'Sitio Web',
      'serverDetail.discord': 'Discord',
      'serverDetail.aboutServer': 'Acerca del servidor',
      'serverDetail.articlesTitle': 'Artículos destacados de este servidor',
      'serverDetail.noDescription': 'No hay descripción disponible para este servidor.',
      'serverDetail.serverStats': 'Estadísticas del servidor',
      'serverDetail.monthlyVotes': 'Votos del mes',
      'serverDetail.totalVotes': 'Votos totales',
      'serverDetail.clicks': 'Clics',
      'serverDetail.rank': 'Rango',
      'serverDetail.addedOn': 'Agregado el',
      'serverDetail.voteHistory': 'Historial de votos',
      'serverDetail.weeklyVotes': 'Votos semanales',
      'serverDetail.noVoteData': 'No hay datos de votos aún',
      'serverDetail.playerReviews': 'Reseñas de jugadores',
      'serverDetail.loginToReview': 'Inicia sesión para dejar una reseña',
      'serverDetail.addReview': 'Agregar una reseña',
      'serverDetail.yourRating': 'Tu calificación',
      'serverDetail.reviewComment': 'Tu comentario',
      'serverDetail.reviewPlaceholder': 'Comparte tu experiencia en este servidor...',
      'serverDetail.publishReview': 'Publicar reseña',
      'serverDetail.noReviews': '¡Aún no hay reseñas. ¡Sé el primero en compartir tu opinión!',
      'serverDetail.serverNotFound': 'Servidor no encontrado',
      'serverDetail.serverNotFoundDesc': 'Este servidor no existe o ha sido eliminado.',
      'serverDetail.loadingServer': 'Cargando servidor...',
      'serverDetail.connectToIP': 'Conectar con esta IP:',
      'serverDetail.copyIP': 'Copiar IP',
      'serverDetail.ipCopied': '¡Copiado!',
      'serverDetail.ipCopiedDesc': 'La dirección IP ha sido copiada al portapapeles.',
      'serverDetail.reviewAdded': '¡Reseña agregada!',
      'serverDetail.reviewAddedDesc': 'Tu reseña ha sido publicada exitosamente.',
      'serverDetail.reviewError': 'Error',
      'serverDetail.reviewErrorDesc': 'Ocurrió un error al agregar tu reseña.',
      'serverDetail.commentRequired': 'Comentario requerido',
      'serverDetail.commentRequiredDesc': 'Por favor ingresa un comentario.',
      'serverDetail.loginRequired': 'Inicio de sesión requerido',
      'serverDetail.loginRequiredDesc': 'Debes estar conectado para dejar una reseña.',
      'serverDetail.vote.nextVoteIn': 'Próximo voto en',
      'serverDetail.votesToday': 'Votos hoy',
      'serverDetail.version': 'Versión',
      'serverDetail.gameType': 'Tipo de juego',
      'serverDetail.tags': 'Etiquetas',
      'serverDetail.connectToServer': 'Conectar al servidor',
      'serverDetail.voteEvolution': 'Evolución de votos (últimos 7 días)',
      'serverDetail.communityReviews': 'Reseñas de la comunidad',
      'serverDetail.anonymousUser': 'Usuario anónimo',
      'serverDetail.noReviewsYet': 'Aún no hay reseñas',
      'serverDetail.firstToShare': '¡Sé el primero en compartir tu experiencia!',
      'serverDetail.shareExperience': 'Comparte tu experiencia',
      'serverDetail.publishing': 'Publicando...',

      // Vote Dialog
      'vote.labelMinecraft': 'Usuario Minecraft',
      'vote.labelDofus': 'Usuario Dofus', 
      'vote.labelCS': 'Usuario Counter-Strike',
      'vote.labelDefault': 'Usuario del juego',
      'vote.placeholderMinecraft': 'TuUsuarioMinecraft',
      'vote.placeholderDofus': 'TuUsuarioDofus',
      'vote.placeholderCS': 'TuUsuarioCS',
      'vote.placeholderDefault': 'TuUsuario',
      'vote.securityVerification': 'Verificación de seguridad',
      'vote.captchaLoading': 'Cargando captcha...',
      'vote.checkingActivity': 'Verificando tu última actividad...',
      'vote.cooldownMessage': '⏱ Podrás votar nuevamente en {time}.',
      'vote.voteButton': 'Votar',
      'vote.verifyingInProgress': 'Verificación en progreso...',
     'vote.nicknameNotValidated': 'El apodo no se valida, puedes escribir cualquier nombre.',
     'vote.savedPlayerName': 'Apodo guardado para tus próximos votos',
     
     // Profile empty states & server management texts
     'profile.noArcadeSpots': 'No tienes ninguna reserva de Arcade Spot por el momento.',
     'profile.noReviews': 'No has dejado ninguna reseña todavía.',
     'profile.noPrestigePayments': 'No hay pagos de Prestige por el momento.',
     'profile.noServersMessage': 'No has añadido ningún servidor todavía.',
     'profile.serverHidden': 'Oculto',
     'profile.serverDisabledMessage': 'Este servidor ha sido desactivado tras una denuncia o una acción administrativa.',
     'profile.serverDisabledSub': 'Ya no aparece en los rankings y no es accesible para los visitantes.',
     'profile.serverHiddenMessage': 'Este servidor está oculto y no aparece en los rankings públicos.',
     'profile.serverHiddenSub': 'Puedes hacerlo visible en cualquier momento a continuación.',
     'profile.unknownGame': 'Juego desconocido',
     'profile.prestigeLabel': 'PRESTIGE',
     'profile.prestigeDaysLeft': '{days}d restante',
     'profile.standardLabel': 'Estándar',
     'profile.createdOn': 'Creado el',
     'profile.apiVote': 'API de Voto',
     'profile.webhooks': 'Webhooks',
     'profile.privilegedAction': 'Acción Privilegiada',
     'profile.deactivatePrestigeConfirm': '¿Desactivar Prestige para "{name}"? Esta acción es inmediata y está reservada a fundadores/administradores.',
     'profile.activatePrestigeConfirm': '¿Activar Prestige para "{name}" durante 30 días? Esta acción está reservada a fundadores/administradores.',
     'profile.cancel': 'Cancelar',
     'profile.deactivate': 'Desactivar',
     'profile.activate': 'Activar',
     'profile.arcadeSpotPosition': 'Posición Arcade Spot',
     'profile.statusActive': 'Activo',
     'profile.statusPending': 'Pendiente',
     'profile.statusExpired': 'Expirado',
     'profile.serverLabel': 'Servidor:',
     'profile.deletedServerLabel': 'Servidor eliminado',
     'profile.dateFrom': 'Del',
     'profile.dateTo': 'al',
     'profile.viewWebsite': 'Ver sitio web',
     'profile.deleteReview': 'Eliminar reseña',
     'profile.deleteReviewConfirm': '¿Estás seguro de que quieres eliminar esta reseña? Esta acción es irreversible.',
     'profile.delete': 'Eliminar',
     'profile.prestigePaymentTitle': 'Prestige',
     'profile.statusPaid': 'Pagado',
     'profile.daysCount': '{days} día',
     'profile.daysCountPlural': '{days} días',
     'profile.expiresOn': 'Expira el',
     'profile.upgradeToPrestige': 'Upgrade a Prestige',
     'profile.disablePrestige': 'Desactivar Prestige (Privilegio)',
     'profile.enablePrestige': 'Activar Prestige (Privilegio)',
     'profile.addFirstServer': '¡Añade tu primer servidor para empezar!',
     
     // Visibility control
     'visibility.visible': 'Visible',
     'visibility.hidden': 'Oculto',
     'visibility.hideServer': 'Ocultar servidor',
     'visibility.showServer': 'Hacer servidor visible',
     'visibility.hideServerTitle': 'Ocultar servidor',
     'visibility.showServerTitle': 'Hacer servidor visible',
     'visibility.hideConfirm': '¿Estás seguro de que quieres ocultar "{name}"?',
     'visibility.hideWarning': 'El servidor desaparecerá del ranking público pero seguirá en tu gestión.',
     'visibility.showConfirm': '¿Estás seguro de que quieres hacer "{name}" visible?',
     'visibility.showInfo': 'El servidor aparecerá en el ranking público.',
     'visibility.slotsUsed': 'Estás usando {used} de {max} servidores permitidos.',
     'visibility.hide': 'Ocultar',
     'visibility.show': 'Hacer visible',
     'visibility.limitReached': 'Límite alcanzado',

      'vote.error.pseudoLength': 'El usuario debe contener entre 2 y 50 caracteres',
      'vote.error.invalidCharacters': 'Caracteres no autorizados detectados en el usuario',
      'vote.error.loading': 'Cargando en progreso, por favor espera...',
      'vote.error.captchaRequired': 'Por favor complete la verificación de seguridad',
      'vote.dialogTitle': 'Votar por {serverName}',

    // Add Server Page
    'addServerPage.title': 'AGREGAR UN SERVIDOR',
    'addServerPage.subtitle': 'Comparte tu servidor con la comunidad',
    'addServerPage.addMyServer': 'AGREGAR MI SERVIDOR',
    'addServerPage.serverManagement': 'Gestión de servidores',
    'addServerPage.visibleServers': 'Servidores visibles',
    'addServerPage.available': 'Disponibles',
    'addServerPage.baseLimit': 'Límite base',
    'addServerPage.servers': 'servidores',
    'addServerPage.prestigeTip': 'Consejo Prestige',
    'addServerPage.prestigeDescription': '¡Activa Prestige en un servidor para desbloquear +1 slot adicional de servidor!',
    'addServerPage.whyAdd': '¿Por qué agregar tu servidor?',
    'addServerPage.benefit1': 'Aumenta tu visibilidad ante miles de jugadores',
    'addServerPage.benefit2': 'Recibe votos diarios de la comunidad',
    'addServerPage.benefit3': 'Personaliza la apariencia con colores e imágenes',
    'addServerPage.benefit4': 'Accede a estadísticas detalladas',
    'addServerPage.prestigeOption': 'Opción Prestige',
    'addServerPage.prestigeTitle': 'PRESTIGE',
    'addServerPage.prestigeFeature1': 'Posición prioritaria en el ranking y badge exclusivo',
    'addServerPage.prestigeFeature2': 'Destacado con colores personalizados y efectos visuales',
    'addServerPage.prestigeFeature3': '+1 servidor adicional agregado a tu límite',
    'addServerPage.prestigeFeature4': 'El servidor bonus se oculta automáticamente si Prestige expira',
    'addServerPage.loginRequired': 'INICIO DE SESIÓN REQUERIDO',
    'addServerPage.mustBeLoggedIn': 'Debes estar conectado para agregar un servidor',
    'addServerPage.signupNow': 'Registrarse ahora',
    'addServerPage.login': 'Iniciar sesión',

    // Profile Page Extended
    'profile.welcomeMessage': 'Debes estar conectado para acceder a tu perfil.',
    'profile.founder': 'FUNDADOR',
    'profile.admin': 'ADMIN',
    'profile.reviews': 'Reseñas',
    'profile.activeWarnings': 'Advertencias activas',
    'profile.server': 'Servidor:',
    'profile.deletedServer': 'Servidor eliminado',
    'profile.disabled': '(DESHABILITADO)',
    'profile.loading': 'Cargando...',
    'profile.prestigeExpires': 'Prestige expira en {days} día{plural}',
    'profile.prestigeExpired': 'Prestige expirado',
    'profile.activatePrestige': 'Activar Prestige',
    'profile.deactivatePrestige': 'Desactivar Prestige',
    'profile.upgradePrestige': 'Actualizar Prestige',
    'profile.manageAPI': 'Gestionar API',
    'profile.edit': 'Editar',
    'profile.visibility': 'Visibilidad',
    'profile.votes': 'votos',

    // Legal Pages  
    'legal.termsOfService': 'Términos de Servicio',
    'legal.termsOfSale': 'Términos de Venta',
    'legal.privacyPolicy': 'Política de Privacidad',
    'legal.lastUpdated': 'Última actualización:',

    // --- Creator Tools ---
'creatorTools.title': 'Herramientas de Creadores',
'creatorTools.subtitle': 'Genera widgets personalizados para promocionar tu servidor en cualquier lugar',
'creatorTools.configuration': 'Configuración',
'creatorTools.preview': 'Vista previa en vivo',
'creatorTools.code': 'Código para copiar',
'creatorTools.guide': 'Guía rápida',
'creatorTools.selectServer': 'Seleccionar un servidor',
'creatorTools.selectServerPlaceholder': 'Elige tu servidor...',
'creatorTools.format': 'Formato',
'creatorTools.formatBadge': 'Insignia',
'creatorTools.formatButton': 'Botón',
'creatorTools.formatBanner': 'Banner',
'creatorTools.size': 'Tamaño',
'creatorTools.sizeSmall': 'Pequeño',
'creatorTools.sizeMedium': 'Mediano',
'creatorTools.sizeLarge': 'Grande',
'creatorTools.theme': 'Tema',
'creatorTools.themeLight': 'Claro',
'creatorTools.themeDark': 'Oscuro',
'creatorTools.themeRetro': 'Retro Gaming',
'creatorTools.language': 'Idioma',
'creatorTools.showVotes': 'Mostrar votos',
'creatorTools.rounded': 'Esquinas redondeadas',
'creatorTools.animation': 'Animación',
'creatorTools.tabIframe': 'Iframe',
'creatorTools.tabScript': 'Script JS',
'creatorTools.copyCode': 'Copiar código',
'creatorTools.copied': '¡Código copiado!',
'creatorTools.step1': 'Configura tu widget',
'creatorTools.step1Desc': 'Elige tu servidor y personaliza la apariencia',
'creatorTools.step2': 'Previsualiza el resultado',
'creatorTools.step2Desc': 'Verifica que el widget se vea como esperas',
    'creatorTools.step3': 'Integra el código',
    'creatorTools.step3Desc': 'Copia y pega el código en tu sitio',
    'creatorTools.widgetFor': 'Widget para',
    'creatorTools.server': 'servidor',
    'creatorTools.widgetRankServeur': 'Widget RankServeur',
    'creatorTools.noServers': 'Debes tener al menos un servidor para usar esta herramienta',
'creatorTools.loginRequired': 'Inicia sesión para acceder a las herramientas de creadores',
'creatorTools.playerLabel': 'Apodo pre-rellenado (opcional)',
'creatorTools.playerPlaceholder': 'Ej: PlayerNickname',
'creatorTools.playerHint': 'Agrega ?player= a la URL de voto (no validado en el servidor, el jugador puede modificarlo).',
    'creatorTools.logoSize': 'Tamaño del logo',
    'creatorTools.logoSizeSmall': 'Pequeño',
    'creatorTools.logoSizeMedium': 'Mediano',
    'creatorTools.logoSizeLarge': 'Grande',
    'creatorTools.logoSizeNone': 'Oculto',
    'creatorTools.accentColor': 'Color de acento',
    'creatorTools.effect': 'Efecto visual',
    'creatorTools.effectNone': 'Ninguno',
    'creatorTools.effectGlow': 'Brillo neón',
    'creatorTools.effectShadow': 'Sombra',
    'creatorTools.effectGradient': 'Borde degradado',
    'creatorTools.borderStyle': 'Estilo de borde',
    'creatorTools.borderSolid': 'Sólido',
    'creatorTools.borderDashed': 'Discontinuo',
    'creatorTools.borderDouble': 'Doble',
    'creatorTools.borderNone': 'Ninguno',
    'creatorTools.ctaText': 'Texto del botón',
    'creatorTools.ctaPlaceholder': 'Ej: Votar ahora',
    'creatorTools.appearance': 'Apariencia',
    'creatorTools.effects': 'Efectos y Estilo',
    'creatorTools.bgColor': 'Color de fondo',
    'creatorTools.bgImage': 'Imagen de fondo (URL)',
    'creatorTools.bgImagePlaceholder': 'https://ejemplo.com/mi-fondo.jpg',
    'creatorTools.bgImageHint': 'La imagen está alojada en tu sitio, no la almacenamos.',

// --- Común ---
'common.results': 'resultados',

    
    // Prestige Payment Dialog
    'prestige.upgradeTitle': 'Upgrade a Prestige',
    'prestige.duration': 'Duración:',
    'prestige.day': 'día',
    'prestige.days': 'días',
    'prestige.unitPrice': 'Precio unitario:',
    'prestige.total': 'Total:',
    'prestige.featuresIncluded': 'Características Prestige incluidas:',
    'prestige.cancel': 'Cancelar',
    'prestige.loading': 'Cargando...',
    'prestige.pay': 'Pagar',

    // Edit Server Dialog
    'editServer.title': 'Editar servidor',
    'editServer.delete': 'Eliminar',
    'editServer.deleteConfirmTitle': 'Eliminar servidor',
    'editServer.deleteConfirmText': '¿Estás seguro de que quieres eliminar "{name}"? Esta acción es irreversible.',
    'editServer.deleting': 'Eliminando...',
    'editServer.basicInfo': 'INFORMACIÓN BÁSICA',
    'editServer.serverName': 'Nombre del servidor',
    'editServer.game': 'Juego',
    'editServer.selectGame': 'Seleccionar un juego',
    'editServer.description': 'Descripción',
    'editServer.descriptionPlaceholder': 'Describe tu servidor...',
    'editServer.descriptionImage': 'Imagen para la descripción',
    'editServer.serverImages': 'IMÁGENES DEL SERVIDOR',
    'editServer.serverLogo': 'Logo del servidor',
    'editServer.serverBanner': 'Banner del servidor',
    'editServer.prestigeRequired': 'Prestige requerido',
    'editServer.bannerAvailableWithPrestige': 'Banner disponible con Prestige',
    'editServer.upgradeForBanner': 'Actualiza a Prestige para añadir un banner a tu servidor',
    'editServer.locked': 'BLOQUEADO',
    'editServer.technicalConfig': 'CONFIGURACIÓN TÉCNICA',
    'editServer.ipAddress': 'Dirección IP',
    'editServer.version': 'Versión',
    'editServer.communityLinks': 'ENLACES DE COMUNIDAD',
    'editServer.discord': 'Discord',
    'editServer.website': 'Sitio web',
    'editServer.serverTags': 'TAGS DEL SERVIDOR',
    'editServer.prestigeCustomization': 'PERSONALIZACIÓN PRESTIGE',
    'editServer.customColor': 'Color personalizado',
    'editServer.upgradeForColor': 'Actualiza a Prestige para personalizar el color de tu servidor',
     'editServer.updating': 'Actualizando...',
     'editServer.updateServer': 'Actualizar servidor',
     'editServer.serverEvents': 'Eventos actuales y próximos',
    'editServer.save': 'Guardar',
    'editServer.videoUrl': 'URL de vídeo',
    'editServer.videoUrlPlaceholder': 'https://youtube.com/watch?v=...',
    'editServer.videoUrlDesc': 'Enlace a un vídeo de presentación (opcional)',
     
     // Image Upload  
     'imageUpload.supportedFormats': 'Formatos aceptados: JPG, PNG, WebP. Tamaño máx: 5MB',
     'imageUpload.dragDropText': 'Arrastra tu imagen de descripción aquí o',
     'imageUpload.selectFile': 'Seleccionar un archivo',
     'imageUpload.recommendedSize.logo': ' - Tamaño recomendado: 96x96px',
     'imageUpload.recommendedSize.banner': ' - Tamaño recomendado: 1200x400px',
     'imageUpload.recommendedSize.backdrop': ' - Tamaño recomendado: 1920x1080px',
      'imageUpload.recommendedSize.description': ' - Tamaño recomendado: 800x600px',
      'imageUpload.cropInfo': 'Podrás recortar la imagen antes de subirla',
      
      // Image Crop Dialog
      'imageCrop.title': 'Recortar imagen',
      'imageCrop.cancel': 'Cancelar',
      'imageCrop.confirm': 'Confirmar',
      'imageCrop.processing': 'Procesando...',
     
     // Vote API Manager - Spanish translations
    'voteApi.title': 'API de Voto',
    'voteApi.description': 'Configura la API de voto para tu servidor y rastrea las estadísticas en tiempo real.',
    'voteApi.tokenLabel': 'Token API',
    'voteApi.regenerateToken': 'Regenerar token',
    'voteApi.showToken': 'Mostrar token',
    'voteApi.hideToken': 'Ocultar token',
    'voteApi.copyToken': 'Copiar token',
    'voteApi.apiDocumentation': 'Documentación API',
    'voteApi.endpointLabel': 'Endpoint API',
    'voteApi.copyEndpoint': 'Copiar endpoint',
    'voteApi.exampleRequest': 'Ejemplo de solicitud',
    'voteApi.copyExample': 'Copiar ejemplo',
    'voteApi.tokenRegenerated': 'Token regenerado',
    'voteApi.tokenRegeneratedDesc': 'Tu nuevo token API ha sido generado exitosamente',
    'voteApi.copySuccess': '¡Copiado!',
    'voteApi.copySuccessDesc': 'El contenido ha sido copiado al portapapeles',
    'voteApi.error': 'Error',
    'voteApi.regenerateError': 'No se pudo regenerar el token',
    'voteApi.keepSecret': '⚠️ Mantenga esta clave secreta y segura',
    'voteApi.apiUrl': 'URL de la API',
    'voteApi.replaceUserIp': 'Reemplaza {USER_IP} por la IP del usuario',
    'voteApi.endpointFormat': 'Formato del endpoint',
    'voteApi.successResponse': 'Respuesta de éxito',
    'voteApi.failureResponse': 'Respuesta de error',
    'voteApi.phpExample': 'Ejemplo PHP',
    'voteApi.importantInfoTitle': 'Información importante',
    'voteApi.votesValid24h': 'Los votos son válidos una vez cada 24 horas (1 voto/día/servidor)',
    'voteApi.ipHashingSecurity': 'El hashing de IP utiliza SHA-256 para la seguridad',
    'voteApi.limitTokenAccess': 'Limita el acceso al token API',
     'voteApi.testIntegration': 'Prueba tu integración antes de la producción',
     
    // Admin Arena Import
    'admin.arena.title': 'Importar Arena-Top100',
    'admin.arena.description': 'Importar servidores desde arena-top100.com',
    'admin.arena.infoTitle': 'Información importante',
    'admin.arena.info1': 'Los servidores se crearán con is_approved=false (requieren validación)',
    'admin.arena.info2': 'Solo se importarán servidores de juegos en tu base de datos',
    'admin.arena.info3': 'Los duplicados (nombre o URL) se omitirán automáticamente',
    'admin.arena.info4': 'Las imágenes se descargarán y comprimirán automáticamente',
    'admin.arena.info5': 'Serás definido como propietario de todos los servidores importados',
    'admin.arena.importLimit': 'Número de servidores a importar',
    'admin.arena.importAll': 'Todos los servidores',
    'admin.arena.importCustom': 'Número personalizado',
    'admin.arena.customLimitPlaceholder': 'Ingrese el número de servidores',
    'admin.arena.startImport': 'Iniciar importación',
    'admin.arena.importing': 'Importando...',
    'admin.arena.importingDesc': 'Esto puede tomar algunos minutos',
    'admin.arena.importSuccess': 'Importación completada',
    'admin.arena.importError': 'Error de importación',
    'admin.arena.serversImported': 'servidores importados',
    'admin.arena.total': 'Total',
    'admin.arena.imported': 'Importados',
    'admin.arena.skipped': 'Omitidos',
    'admin.arena.errors': 'Errores',
    'admin.arena.details': 'Detalles de importación',
    'admin.arena.game': 'Juego',
    'admin.arena.slug': 'Slug',

    // Edit Server Dialog - New translations
    'editServer.editServer': 'Editar Servidor',
    'vote.created': 'vote.created',
    'editServer.events': 'Eventos',
    'editServer.tags': 'Etiquetas',
    'editServer.mediaTags': 'Medios y Etiquetas',
    'editServer.integrations': 'Integraciones',
    'editServer.prestige': 'Prestigio',

    // Votifier Configuration - Spanish translations
    'votifier.title': 'Configuración Votifier',
    'votifier.description': 'Votifier permite a tu servidor de Minecraft recibir notificaciones instantáneas cuando un jugador vota. Configura tu plugin Votifier en tu servidor y añade la información a continuación.',
    'votifier.activeConfigs': 'Configuraciones activas',
    'votifier.addConfig': 'Añadir configuración Votifier',
    'votifier.serverHost': 'Dirección IP del servidor *',
    'votifier.serverHostPlaceholder': '127.0.0.1 o tu-dominio.com',
    'votifier.port': 'Puerto Votifier *',
    'votifier.portPlaceholder': '8192',
    'votifier.protocol': 'Protocolo Votifier',
    'votifier.protocolV2': 'v2 (Recomendado - JSON/HTTP)',
    'votifier.protocolV1': 'v1 (Legacy - RSA/TCP)',
    'votifier.token': 'Token/Clave *',
    'votifier.tokenPlaceholder': 'Token de seguridad Votifier',
    'votifier.generate': 'Generar',
    'votifier.active': 'Activo',
    'votifier.inactive': 'Inactivo',
    'votifier.creating': 'Creando...',
    'votifier.createConfig': 'Crear configuración',
    'votifier.installationGuide': 'Guía de instalación',
    'votifier.pluginInstall': 'Instalación del plugin:',
    'votifier.installStep1': 'Descarga Votifier o VotifierPlus desde SpigotMC',
    'votifier.installStep2': 'Coloca el .jar en tu carpeta plugins/',
    'votifier.installStep3': 'Reinicia tu servidor',
    'votifier.installStep4': 'Configura el plugin con tus ajustes de arriba',
    'votifier.installStep5': 'Activa la configuración en esta página',
    'votifier.pluginConfig': 'Configuración del plugin (config.yml):',
    'votifier.importantNote': 'Importante:',
    'votifier.firewallNote': 'Asegúrate de que el puerto {port} esté abierto en tu firewall y correctamente redirigido si usas un proveedor de hosting.',
    'votifier.configCreated': 'Configuración Votifier creada',
    'votifier.configCreatedDesc': 'Tu servidor ahora recibirá notificaciones de votos.',
    'votifier.configUpdated': 'Configuración actualizada',
    'votifier.configUpdatedDesc': 'El estado de Votifier ha sido modificado.',
    'votifier.configDeleted': 'Configuración eliminada',
    'votifier.configDeletedDesc': 'La configuración Votifier ha sido eliminada.',
    'votifier.requiredFields': 'Campos requeridos',
    'votifier.requiredFieldsDesc': 'Por favor, rellena todos los campos obligatorios.',
    'votifier.invalidPort': 'Puerto inválido',
    'votifier.invalidPortDesc': 'El puerto debe estar entre 1 y 65535.',
    'votifier.loading': 'Cargando...',
    'votifier.error': 'Error',
    'votifier.createError': 'No se pudo crear la configuración Votifier.',

    // Prestige Pass / Battle Pass
    'prestigePass.title': 'Prestige Pass',
    'prestigePass.subtitle': 'Gana días de Prestige gratis con tus votos',
    'prestigePass.free': 'GRATIS',
    'prestigePass.availableTokens': 'Días disponibles',
    'prestigePass.totalVotes': 'Votos totales',
    'prestigePass.currentMilestone': 'Hito actual',
    'prestigePass.nextMilestone': 'Próximo hito',
    'prestigePass.votesRemaining': 'votos restantes',
    'prestigePass.myTokens': 'Mis días de Prestige',
    'prestigePass.noTokensYet': 'Aún no tienes días de Prestige',
    'prestigePass.earnTokensInfo': 'Acumula 1000 votos para desbloquear 1 día de Prestige gratis',
    'prestigePass.milestone': 'Hito',
    'prestigePass.earnedAt': 'Ganado en',
    'prestigePass.votes': 'votos',
    'prestigePass.applyToken': 'Usar',
    'prestigePass.used': 'Usado',
    'prestigePass.usedOn': 'Usado el',
    'prestigePass.applyTokenTitle': 'Aplicar un día de Prestige',
    'prestigePass.applyTokenDescription': 'Elige el servidor al que aplicar este día de Prestige. Se sumará al Prestige existente si el servidor ya lo tiene.',
    'prestigePass.selectServer': 'Selecciona un servidor',
    'prestigePass.noServers': 'No hay servidores disponibles',
    'prestigePass.confirm': 'Confirmar',

    // Webhook Integration
    'webhookIntegration.title': 'Integración de Webhooks',
    'webhookIntegration.description': 'Reciba notificaciones en tiempo real cuando ocurran eventos',
    'webhookIntegration.urlLabel': 'URL del webhook',
    'webhookIntegration.secretLabel': 'Secreto HMAC',
    'webhookIntegration.generateSecret': 'Generar',
    'webhookIntegration.createWebhook': 'Crear webhook',
    'webhookIntegration.testWebhook': 'Probar',
    'webhookIntegration.deleteWebhook': 'Eliminar',
    'webhookIntegration.webhookCreated': 'Webhook creado',
    'webhookIntegration.webhookCreatedDesc': 'El webhook se ha creado exitosamente.',
    'webhookIntegration.webhookUpdated': 'Webhook actualizado',
    'webhookIntegration.webhookDeleted': 'Webhook eliminado',
    'webhookIntegration.testSuccess': 'Prueba de webhook exitosa',
    'webhookIntegration.testError': 'Error en prueba de webhook',
    'webhookIntegration.active': 'Activo',
    'webhookIntegration.inactive': 'Inactivo',
    'webhookIntegration.documentation': 'Documentación',
    'webhookIntegration.supportedEvents': 'Eventos soportados:',
    'webhookIntegration.voteCreated': 'Se activa cuando un jugador vota por tu servidor',
    'webhookIntegration.security': 'Seguridad:',
    'webhookIntegration.hmacSigned': 'Cada solicitud está firmada con HMAC-SHA256',
    'webhookIntegration.eventHeader': 'Header',
    'webhookIntegration.signatureHeader': 'Header',
    'webhookIntegration.signatureFormat': 'Formato de la firma:',
    'webhookIntegration.urlRequired': 'URL y secreto son obligatorios.',
    'webhookIntegration.urlPlaceholder': 'https://tu-dominio.com/webhook',
    'webhookIntegration.secretPlaceholder': 'Secreto para firmar solicitudes',
    'webhookIntegration.testPayloadLabel': 'Payload de prueba (JSON)',
    'webhookIntegration.sendTest': 'Enviar prueba',
    'webhookIntegration.testing': 'Probando...',
    'webhookIntegration.creating': 'Creando...',
    'webhookIntegration.configured': 'Webhooks configurados',

    // Common
    'common.status': 'Estado',

    // Common
    'common.serverDetail': 'Detalle del servidor',
    'common.comingSoon': 'Próximamente',

    // FAQ Section
    'faq.title': 'Preguntas Frecuentes',
    'faq.subtitle': 'Todo lo que necesitas saber sobre RankServeur',
    'faq.question1': '¿Cómo votar por un servidor?',
    'faq.answer1': 'Para votar por un servidor, simplemente haz clic en el botón "Votar" en la página del servidor. Puedes votar una vez cada 2 horas por servidor. Tus votos ayudan a aumentar la visibilidad del servidor en nuestro ranking.',
    'faq.question2': '¿Qué es el Prestige Pass?',
    'faq.answer2': 'El Prestige Pass es un sistema de recompensas gratuito. Cada 1000 votos que acumules, desbloqueas 1 día de Prestige que puedes aplicar a tus servidores favoritos. El Prestige destaca los servidores en el ranking.',
    'faq.question3': '¿Cómo añadir mi servidor?',
    'faq.answer3': 'Haz clic en el botón "Añadir un servidor" en el menú principal. Rellena el formulario con los detalles de tu servidor (nombre, IP, descripción, logo). Tu servidor será revisado por nuestros moderadores antes de aparecer en el ranking.',
    'faq.question4': '¿Con qué frecuencia puedo votar?',
    'faq.answer4': 'Puedes votar una vez cada 2 horas por servidor. El contador de tiempo de espera se muestra en cada página de servidor. Los votos se reinician cada mes, ofreciendo nuevas oportunidades de ranking.',
    'faq.question5': '¿Qué son los eventos competitivos?',
    'faq.answer5': 'Los eventos competitivos son competiciones temporales donde los servidores compiten para ganar la mayor cantidad de votos. Los ganadores reciben días de Prestige gratis. Estos eventos se anuncian en la página de inicio.',
    'faq.question6': '¿Cómo funciona el ranking mensual?',
    'faq.answer6': 'Los votos de todos los servidores se reinician el día 1 de cada mes. Esto ofrece oportunidades regulares para que los nuevos servidores ganen visibilidad y para que los jugadores descubran nuevos servidores.',

    // Admin Universal Importer
    'adminImport.title': 'Importación Universal de Servidores',
    'adminImport.description': 'Importa servidores desde diferentes fuentes',
    'adminImport.source': 'Fuente de importación',
    'adminImport.games': 'Juegos a importar ({count} seleccionados)',
    'adminImport.loadGames': 'Cargar juegos',
    'adminImport.loading': 'Cargando...',
    'adminImport.servers': 'servidores',
    'adminImport.limit': 'Número de servidores a previsualizar',
    'adminImport.preview': 'Previsualizar servidores',
    'adminImport.previewing': 'Previsualizando...',
    'adminImport.selectGame': 'Por favor selecciona al menos un juego',
    'adminImport.gamesLoaded': '{count} juegos disponibles',
    'adminImport.serversLoaded': '{count} servidores cargados',
    'adminImport.noServersFound': 'No se encontraron servidores',
    'adminImport.errorLoading': 'Error al cargar',
    'adminImport.errorPreview': 'Error durante la previsualización',
    'adminImport.errorImport': 'Error durante la importación',

    // Game Category Pages
    'gameCategory.servers': 'SERVIDORES DE {gameName}',
    'gameCategory.updatedRealTime': 'Ranking actualizado en tiempo real',
    'gameCategory.noServersFound': 'No se encontraron servidores',

    // Propose Tag Dialog
    'proposeTag.title': 'Proponer un nuevo tag',
    'proposeTag.description': 'Propone un nuevo tag que será revisado por los administradores antes de ser agregado.',
    'proposeTag.nameLabel': 'Nombre del tag *',
    'proposeTag.namePlaceholder': 'Ej: Hardcore, PvP, Roleplay...',
    'proposeTag.descriptionLabel': 'Descripción (opcional)',
    'proposeTag.descriptionPlaceholder': 'Describe brevemente lo que representa este tag...',
    'proposeTag.colorLabel': 'Color',
    'proposeTag.preview': 'Vista previa',
    'proposeTag.submitting': 'Enviando...',
    'proposeTag.submit': 'Proponer',

    // Filter Section
    'filter.filterByGame': 'Filtrar por juego',
    'filter.allGames': 'Todos los juegos',
    'filter.sortBy': 'Ordenar por:',
    'filter.topVotes': 'Más votados',
    'filter.mostPopular': 'Más populares',
    'filter.recent': 'Recientes',
    'filter.activeFilter': 'Filtro activo:',
    'filter.clickToRemove': 'Haz clic para eliminar el filtro',

    // Vote System
    'vote.alreadyVoted': '¡Ya has votado!',
    'vote.waitTime': 'Puedes votar de nuevo en {hours} hora(s).',
    'vote.success': '¡Voto registrado con éxito!',
    'vote.failed': 'Error al votar.',
    'vote.every2hours': 'Vota cada 2 horas',
    'vote.nextVoteIn': 'Próximo voto en',
	
	// Lista de Servidores – Paginación
'serverList.page': 'Página {current} de {total}',
'serverList.display': '(Mostrando {from} a {to} de {count} servidores)',

// Leaderboard Page
'leaderboard.title': 'RANKINGS DE VOTANTES',
'leaderboard.subtitle': 'Descubre los mejores votantes de la comunidad gaming',
'leaderboard.addMyServer': 'Añadir mi servidor',
'leaderboard.loading': 'Cargando...',
'leaderboard.noVoters': 'No se encontraron votantes',
'leaderboard.topMonthly': 'Top Votantes Mensuales',
'leaderboard.topWeekly': 'Top Votantes Semanales',
'leaderboard.topDaily': 'Top Votantes Diarios',
'leaderboard.registeredUser': 'Usuario Registrado',
'leaderboard.guestVoter': 'Votante Invitado',

// Votifier Guide
'votifierGuide.title': 'Guía Votifier para Minecraft',
'votifierGuide.description': 'Guía completa para configurar Votifier en tu servidor Minecraft y recibir notificaciones de voto automáticamente',
'votifierGuide.keywords': 'votifier, minecraft, guía, configuración, plugin, votos, notificaciones',
'votifierGuide.subtitle': 'Configura Votifier en tu servidor Minecraft para recibir automáticamente las notificaciones de voto y recompensar a tus jugadores.',
'votifierGuide.whatIsVotifier': '¿Qué es Votifier?',
'votifierGuide.whatIsVotifierDesc': 'Votifier es un plugin de Minecraft que permite a tu servidor recibir notificaciones instantáneas cuando un jugador vota en sitios como RankServeur. Esto permite recompensar automáticamente a los jugadores.',
'votifierGuide.prerequisites': 'Requisitos previos',
'votifierGuide.prereq1': 'Un servidor Minecraft con Spigot, Paper o Bukkit',
'votifierGuide.prereq2': 'Acceso administrador al servidor',
'votifierGuide.prereq3': 'Puerto de red abierto (por defecto 8192)',
'votifierGuide.prereq4': 'Servidor agregado en RankServeur',
'votifierGuide.step1Title': 'Descargar el plugin Votifier',
'votifierGuide.step1Desc': 'Elige la versión de Votifier que mejor se adapte a tu servidor:',
'votifierGuide.votifierOriginalDesc': 'Versión original estable, compatible con la mayoría de servidores',
'votifierGuide.votifierPlusDesc': 'Versión mejorada con más funcionalidades y compatibilidad',
'votifierGuide.recommended': 'Recomendado',
'votifierGuide.recommendedDesc': 'VotifierPlus generalmente se recomienda por su mejor compatibilidad y funcionalidades avanzadas.',
'votifierGuide.step2Title': 'Instalar el plugin',
'votifierGuide.step2Point1': 'Detén tu servidor Minecraft',
'votifierGuide.step2Point2': 'Coloca el archivo .jar descargado en la carpeta /plugins/',
'votifierGuide.step2Point3': 'Reinicia tu servidor',
'votifierGuide.step2Point4': 'Verifica que el plugin se haya cargado correctamente en los logs',
'votifierGuide.step3Title': 'Configurar el plugin',
'votifierGuide.step3Desc': 'Después del primer inicio, edita el archivo de configuración:',
'votifierGuide.configExplanation': 'Explicación de los parámetros:',
'votifierGuide.hostExplanation': 'Deja 0.0.0.0 para escuchar en todas las interfaces',
'votifierGuide.portExplanation': 'Puerto en el que Votifier escucha (8192 por defecto)',
'votifierGuide.tokenExplanation': 'Token de seguridad compartido (genera uno único)',
'votifierGuide.debugExplanation': 'Modo debug para diagnosticar problemas',
'votifierGuide.step4Title': 'Configuración en RankServeur',
'votifierGuide.step4Desc': 'Ahora, configura Votifier del lado de RankServeur:',
'votifierGuide.step4Point1': 'Inicia sesión en RankServeur',
'votifierGuide.step4Point2': 'Ve a tu Perfil > Mis servidores',
'votifierGuide.step4Point3': 'Haz clic en "Modificar" en tu servidor Minecraft',
'votifierGuide.step4Point4': 'Desplázate hasta la sección "Configuración Votifier"',
'votifierGuide.step4Point5': 'Ingresa la IP, puerto y token de tu servidor',
'votifierGuide.automaticRewards': 'Recompensas automáticas',
'votifierGuide.automaticRewardsDesc': 'Una vez configurado, tu servidor recibirá automáticamente las notificaciones de voto. Puedes usar plugins como VotingPlugin o GAListener para recompensar automáticamente a los jugadores.',
'votifierGuide.troubleshooting': 'Solución de problemas',
'votifierGuide.problem1': 'Los votos no llegan al servidor',
'votifierGuide.solution1': 'Verifica que el puerto 8192 esté abierto en tu firewall y en tu proveedor de hosting. Prueba la conexión con telnet o un escáner de puertos.',
'votifierGuide.problem2': 'Error "Connection refused"',
'votifierGuide.solution2': 'El plugin Votifier puede no haberse iniciado o el puerto ya está en uso. Verifica los logs del servidor y cambia el puerto si es necesario.',
'votifierGuide.problem3': 'Token inválido',
'votifierGuide.solution3': 'Asegúrate de que el token configurado en RankServeur coincida exactamente con el del config.yml. Cuidado con espacios y caracteres especiales.',
'votifierGuide.problem4': 'El servidor se bloquea al iniciar',
'votifierGuide.solution4': 'Verifica la compatibilidad del plugin con tu versión de servidor. Algunas versiones antiguas pueden requerir versiones específicas de Votifier.',
'votifierGuide.needHelp': '¿Necesitas ayuda?',
'votifierGuide.needHelpDesc': 'Si tienes problemas con la configuración de Votifier, no dudes en contactar a nuestro equipo de soporte o consultar la documentación de la API.',
'votifierGuide.contactSupport': 'Contactar Soporte',
'votifierGuide.apiDocs': 'Documentación API',
'votifierGuide.copied': '¡Copiado!',
'votifierGuide.copiedDesc': 'El código ha sido copiado al portapapeles.',

    // Admin App Versions
    'admin.accessDenied': 'Acceso denegado',
    'admin.appVersions.title': 'Gestión de Versiones',
    'admin.appVersions.newVersion': 'Nueva versión',
    'admin.appVersions.editVersion': 'Editar versión',
    'admin.appVersions.allVersions': 'Todas las versiones',
    'admin.appVersions.version': 'Versión',
    'admin.appVersions.platform': 'Plataforma',
    'admin.appVersions.size': 'Tamaño',
    'admin.appVersions.file': 'Archivo',
    'admin.appVersions.orManualUrl': 'o URL manual',
    'admin.appVersions.mandatory': 'Obligatoria',
    'admin.appVersions.active': 'Activa',
    'admin.appVersions.create': 'Crear versión',
    'admin.appVersions.update': 'Actualizar',
    'admin.appVersions.uploadSuccess': 'Archivo subido con éxito',
    'admin.appVersions.uploadError': 'Error al subir',
    'admin.appVersions.uploadLargeFileHint': 'Para archivos .exe grandes, se usa automáticamente una subida reanudable.',
    'admin.appVersions.uploadExceededHint': 'El archivo superó el límite permitido o la subida directa fue rechazada. Reintenta con el nuevo flujo de subida reanudable.',
    'admin.appVersions.missingFields': 'Versión y URL requeridos',
    'admin.appVersions.confirmDelete': '¿Eliminar esta versión?',
    'admin.appVersions.noVersions': 'No hay versiones publicadas',
    'admin.appVersions.permanentUrl': 'Enlace permanente',
    'admin.appVersions.permanentUrlExplanation': 'El archivo se sube con un nombre fijo (latest.exe). El enlace nunca cambia, solo se reemplaza el contenido.',
    'admin.appVersions.permanentUrlTitle': 'Enlace de descarga permanente',
    'admin.appVersions.permanentUrlDesc': 'Cada subida reemplaza el archivo latest.exe. El launcher siempre usa el mismo enlace y verifica el hash SHA-256 para saber si necesita actualizar.',
    'admin.appVersions.backupUrl': 'URL de respaldo (versionada)',
    'admin.appVersions.copied': '¡Copiado!',
    'admin.appVersions.pythonExampleTitle': 'Integración Python (Launcher)',
    'admin.appVersions.pythonExampleDesc': 'Ejemplo de código para verificar y descargar actualizaciones desde tu launcher Python. El hash SHA-256 valida la integridad del archivo.',
    'admin.appVersions.setActive': 'Activar',
    'admin.appVersions.confirmActivate': '¿Activar esta versión? El archivo latest.exe será reemplazado por esta versión (rollback).',
    'admin.appVersions.activated': 'Versión activada',
    'admin.appVersions.activateError': 'Error de activación',
    'admin.appVersions.activateNoBackup': 'No hay archivo versionado disponible para esta versión (rollback imposible).',

    // Common
    'common.optional': 'opcional',

    // Admin License Pricing
    'admin.pricing.title': 'Precios de Licencias',
    'admin.pricing.subtitle': 'Configura los precios aplicados a los pagos Stripe y mostrados en Discord.',
    'admin.pricing.licensesTitle': 'Precios Stripe (Licencias de software)',
    'admin.pricing.monthly': 'Precio mensual',
    'admin.pricing.monthlyHelp': 'Precio aplicado para una licencia de 1 mes.',
    'admin.pricing.yearly': 'Precio anual',
    'admin.pricing.yearlyHelp': 'Precio aplicado para una licencia de 1 año.',
    'admin.pricing.save': 'Guardar',
    'admin.pricing.saved': 'Precios guardados',
    'admin.pricing.savedDesc': 'Los nuevos precios se aplican inmediatamente a futuras compras.',
    'admin.pricing.invalidAmount': 'Monto inválido',
    'admin.pricing.warning': 'Los pagos ya iniciados conservan su precio anterior. Solo los pagos futuros usarán los nuevos montos.',

    // Admin Discord Tickets
    'admin.tickets.title': 'Tickets de Discord',
    'admin.tickets.subtitle': 'Configura la creación automática de canales privados de soporte.',
    'admin.tickets.configTitle': 'Configuración del sistema de tickets',
    'admin.tickets.enabled': 'Activar tickets',
    'admin.tickets.enabledHelp': 'Si está desactivado, el comando /ticket open ya no creará canales.',
    'admin.tickets.guildId': 'ID del servidor Discord (Guild ID)',
    'admin.tickets.categoryId': 'ID de la categoría',
    'admin.tickets.categoryHelp': 'Categoría de Discord donde se crearán los canales de tickets.',
    'admin.tickets.staffRoleId': 'ID del rol staff',
    'admin.tickets.staffRoleHelp': 'Rol que tendrá acceso automático a los nuevos tickets.',
    'admin.tickets.intro': 'Mensaje de introducción',
    'admin.tickets.introHelp': 'Variables disponibles: {user}, {user_id}, {staff_role}, {purchase_token}.',
    'admin.tickets.botPerm': 'El bot debe tener los permisos «Gestionar canales» y «Ver canales» en la categoría configurada.',
    'admin.tickets.save': 'Guardar',
    'admin.tickets.permsRequiredIntro': 'Permisos de Discord requeridos para el bot:',
    'admin.tickets.permsWhere': 'Dónde configurarlos:',
    'admin.tickets.registerCmdTitle': 'Comandos Discord (slash)',
    'admin.tickets.registerCmdHelp': 'Registra/actualiza los comandos /licence y /ticket en tu servidor de Discord. Haz clic después de cada cambio en la lista de comandos.',
    'admin.tickets.registerCmdBtn': 'Registrar comandos Discord',
    'admin.tickets.commandsRegistered': 'Comandos Discord registrados',
    'admin.tickets.saved': 'Configuración guardada',

    // Admin Test Stripe
    'admin.testStripe.title': 'Prueba de fulfillment Stripe',
    'admin.testStripe.subtitle': 'Simula un pago Stripe exitoso para probar la entrega de licencia (sin cargo real).',
    'admin.testStripe.formTitle': 'Parámetros de la prueba',
    'admin.testStripe.discordUserId': 'ID Discord del usuario',
    'admin.testStripe.discordHelp': 'ID Discord (snowflake) que recibirá el DM de entrega.',
    'admin.testStripe.discordUsername': 'Nombre Discord',
    'admin.testStripe.licenseType': 'Tipo de licencia',
    'admin.testStripe.monthly': '1 mes',
    'admin.testStripe.yearly': '1 año',
    'admin.testStripe.run': 'Ejecutar prueba',
    'admin.testStripe.success': 'Prueba exitosa',
    'admin.testStripe.successDesc': 'Licencia creada y DM enviado al usuario de Discord.',
    'admin.testStripe.missingUser': 'El ID Discord es obligatorio',
    'admin.testStripe.noCharge': 'No se realiza ningún cargo real. Esta acción solo simula el webhook de Stripe para probar la creación de licencia y el envío del DM.',

    // Admin – License Purchases
    'admin.licensePurchases.title': 'Licencias compradas',
    'admin.licensePurchases.subtitle': 'Seguimiento de pedidos Discord (/licence buy) y entregas.',
    'admin.licensePurchases.configMethods': 'Métodos de pago',
    'admin.licensePurchases.search': 'Buscar',
    'admin.licensePurchases.method': 'Método',
    'admin.licensePurchases.empty': 'Sin pedidos.',
    'admin.licensePurchases.fulfillTitle': 'Entregar licencia',
    'admin.licensePurchases.externalRef': 'Referencia externa (ID transacción)',
    'admin.licensePurchases.adminNotes': 'Notas administrador',
    'admin.licensePurchases.fulfillHelp': 'Genera la clave de licencia + enlace de descarga (72h, máx 5 usos), marca el pedido como "fulfilled" y envía automáticamente todo por DM Discord al comprador. Usar SOLO tras verificar el pago.',
    'admin.licensePurchases.confirmFulfill': 'Entregar y enviar DM',
    'admin.licensePurchases.cancel': 'Cancelar',
    'admin.licensePurchases.cancelTitle': 'Cancelar pedido',
    'admin.licensePurchases.cancelHelp': 'Marca el pedido como "cancelled". Usar si el pago nunca se recibió o si el comprador desistió. No se envía DM. Acción irreversible.',
    'admin.licensePurchases.linkTitle': 'Nuevo enlace de descarga',
    'admin.licensePurchases.linkHelp': 'Genera un enlace de descarga nuevo (el anterior deja de funcionar). Puedes enviarlo por DM de Discord y/o copiarlo para compartirlo tú mismo.',
    'admin.licensePurchases.linkValidity': 'Validez (horas)',
    'admin.licensePurchases.linkMaxUses': 'Descargas permitidas',
    'admin.licensePurchases.linkSendDm': 'Enviar también el DM de Discord',
    'admin.licensePurchases.linkGenerate': 'Generar enlace',
    'admin.licensePurchases.linkResult': 'Enlace generado',
    'admin.licensePurchases.linkCopy': 'Copiar enlace',
    'admin.licensePurchases.linkExpires': 'Expira el',
    'admin.licensePurchases.cancelReason': 'Razón de cancelación (interna)',
    'admin.licensePurchases.confirmCancel': 'Confirmar cancelación',
    'admin.licensePurchases.cancelled': 'Pedido cancelado',
    'admin.licensePurchases.cancelOnlyPending': 'Solo los pedidos no entregados pueden cancelarse.',

    'admin.licenses.buyToggle.title': 'Comando Discord /licence buy',
    'admin.licenses.buyToggle.description': 'Activa o desactiva la posibilidad de que los usuarios de Discord compren una licencia mediante /licence buy. Cuando está desactivado, reciben un mensaje indicando que la compra no está disponible temporalmente.',
    'admin.licenses.buyToggle.enabled': 'Activado — los usuarios pueden comprar',
    'admin.licenses.buyToggle.disabled': 'Desactivado — compras bloqueadas',
    'admin.licenses.buyToggle.updated': 'Parámetro actualizado',

    'admin.paymentMethods.autoTicketInfo': '✅ Auto-ticket activado: cuando se selecciona un método manual (PayPal, Skrill, Revolut, Coinbase) en /licence buy, se crea automáticamente un ticket Discord con el contexto del pedido para el equipo. El comprador ya no necesita escribir /ticket open.',

    // Admin — VM (Discord /vm buy)
    'admin.vm.title': 'VM — Discord /vm buy',
    'admin.vm.subtitle': 'Gestionar el flujo de compra VM (solo pago PayPal manual)',
    'admin.vm.enabledLabel': 'Comando /vm buy activado',
    'admin.vm.enabledHelp': 'Cuando está desactivado, los usuarios de Discord reciben un mensaje indicando que la compra de VM no está disponible temporalmente.',
    'admin.vm.priceMember': 'Precio Miembro Pixfus',
    'admin.vm.priceMemberHelp': 'Aplicado automáticamente a los usuarios con el rol Discord Miembro Pixfus.',
    'admin.vm.priceStandard': 'Precio estándar',
    'admin.vm.priceStandardHelp': 'Aplicado a los usuarios sin el rol Miembro (o si falla la detección).',
    'admin.vm.productLabel': 'Nombre del producto',
    'admin.vm.productLabelHelp': 'Se muestra en el ticket y en las instrucciones de pago.',
    'admin.vm.paypalEmail': 'Correo PayPal',
    'admin.vm.paypalEmailHelp': 'Dirección de correo PayPal mostrada al comprador en el ticket para el pago manual "Amigos y familiares".',
    'admin.vm.staffTitle': 'Staff Discord asignado a tickets VM',
    'admin.vm.staffHelp': 'UIDs de Discord invitados automáticamente a cada ticket /vm buy (tendrán acceso al canal y serán mencionados).',
    'admin.vm.staffAdd': 'Añadir',
    'admin.vm.staffEmpty': 'Sin staff Discord asignado — solo se notificará al rol staff global.',
    'admin.vm.staffIdInvalid': 'UID de Discord inválido (solo dígitos)',
    'admin.vm.staffIdDuplicate': 'Este UID ya está en la lista',
    'admin.vm.roleInfoTitle': 'Detección del rol Miembro',
    'admin.vm.roleInfoBody': 'El rol Discord usado para identificar a los Miembros Pixfus se define mediante el secreto de edge function DISCORD_ROLE_MEMBRES_ID. Si el rol no está en el usuario, se aplica automáticamente el precio estándar.',
    'admin.vm.save': 'Guardar',
    'admin.vm.reload': 'Recargar',
    'admin.vm.saved': 'Parámetros VM guardados',
    'admin.vm.saveError': 'Error al guardar',
    'admin.vm.loadError': 'Error al cargar',
    'admin.seo.discordAlertsLabel': 'Alertas Discord',
    'admin.seo.discordAlertsEnabled': 'Alertas Discord GSC activadas',
    'admin.seo.discordAlertsDisabled': 'Alertas Discord GSC desactivadas',
    'admin.seo.discordToggleError': 'No se pudo actualizar el ajuste',

  }
};

// Chemins avec segment localisé (langue déjà encodée dans l'URL) → ne pas rediriger.
const LOCALIZED_PREFIXES = [
  '/en/', '/es/', '/fr/',
  '/server/', '/servers-', '/votifier-guide',
  '/servidor/', '/servidores-', '/guia-votifier',
  '/serveur/', '/serveurs-', '/guide-votifier',
];

// Détecte les bots pour NE PAS rediriger (Google doit voir la version FR canonique sur route neutre).
const isBot = (ua: string) =>
  /bot|crawler|spider|crawling|googlebot|bingbot|yandex|duckduckbot|baiduspider|facebookexternalhit|twitterbot|slackbot|linkedinbot|whatsapp|telegrambot|discordbot|applebot|petalbot|semrushbot|ahrefsbot|mj12bot|dotbot/i.test(ua);

// Routes transactionnelles (auth) : jamais redirigées (les tokens du lien seraient perdus)
// et affichées dans la langue du navigateur de l'utilisateur.
const isAuthRoute = (pathname: string) =>
  /\/(reset-password|update-password|auth)\/?$/.test(pathname);

// Langue déduite du navigateur (fallback EN pour toute langue non couverte).
const browserLanguage = (): Language => {
  const nav = (typeof navigator !== 'undefined' ? navigator.language || '' : '').toLowerCase();
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('es')) return 'es';
  return 'en';
};

// Préférence explicitement enregistrée avant tout auto-save du provider.
const readSavedLanguage = (): Language | null => {
  try {
    const saved = localStorage.getItem('language');
    if (saved === 'fr' || saved === 'en' || saved === 'es') return saved;
  } catch {
    /* storage indisponible */
  }
  return null;
};

// Redirige un chemin neutre vers son équivalent localisé (préfixe simple).
const localizePath = (pathname: string, search: string, target: 'en' | 'es'): string => {
  // Homepage
  if (pathname === '/' || pathname === '') return `/${target}${search}`;
  // Sinon on préfixe
  return `/${target}${pathname}${search}`;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Valeur enregistrée AVANT que l'effet d'auto-sauvegarde n'écrive quoi que ce soit.
  const initialSavedRef = React.useRef<Language | null>(readSavedLanguage());

  const [language, setLanguage] = useState<Language>(() => {
    const pathname = window.location.pathname;

    // 1. PRIORITÉ ABSOLUE : préfixe de langue explicite dans l'URL
    if (pathname.startsWith('/en/') || pathname === '/en') return 'en';
    if (pathname.startsWith('/es/') || pathname === '/es') return 'es';
    if (pathname.startsWith('/fr/') || pathname === '/fr') return 'fr';

    // 2. Routes typées SANS préfixe (segment localisé => langue fixe)
    if (pathname.startsWith('/server/') || pathname.startsWith('/servers-') || pathname.startsWith('/votifier-guide')) return 'en';
    if (pathname.startsWith('/servidor/') || pathname.startsWith('/servidores-') || pathname.startsWith('/guia-votifier')) return 'es';
    if (pathname.startsWith('/serveur/') || pathname.startsWith('/serveurs-') || pathname.startsWith('/guide-votifier')) return 'fr';

    // 3. Choix utilisateur déjà enregistré → prioritaire
    const saved = initialSavedRef.current;
    if (saved) return saved;

    // 4. Routes d'authentification (liens e-mail) → langue du navigateur
    if (isAuthRoute(pathname)) return browserLanguage();

    // 5. Routes neutres → FR par défaut (match sitemap hreflang="fr" + canonical auto-référentiel)
    return 'fr';
  });


  // Sauvegarder la langue dans localStorage
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Redirection intelligente humain → langue préférée (une seule fois, jamais pour les bots).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ua = navigator.userAgent || '';
    if (isBot(ua)) return; // les crawlers restent sur l'URL demandée, canonical auto-référentiel

    // Si l'utilisateur avait déjà choisi une langue avant cette visite, on respecte
    if (initialSavedRef.current) return;


    const pathname = window.location.pathname;
    const search = window.location.search || '';
    const hash = window.location.hash || '';

    // Ne jamais rediriger les routes d'authentification (les tokens du lien seraient perdus)
    if (isAuthRoute(pathname)) return;

    if (search.includes('code=') || search.includes('token_hash=') || hash.includes('access_token')) return;

    // Ne redirige que sur les routes neutres (pas de préfixe ni segment localisé)
    const isLocalized = LOCALIZED_PREFIXES.some(p => pathname === p.replace(/\/$/, '') || pathname.startsWith(p));
    if (isLocalized) return;


    // Détecte la langue du navigateur
    const nav = (navigator.language || 'fr').toLowerCase();
    let target: 'en' | 'es' | 'fr' = 'fr';
    if (nav.startsWith('es')) target = 'es';
    else if (nav.startsWith('fr')) target = 'fr';
    else target = 'en'; // tout autre langue → EN par défaut (couverture internationale)

    if (target === 'fr') {
      // Marque le choix pour éviter de re-évaluer à chaque nav
      localStorage.setItem('language', 'fr');
      return;
    }

    const nextUrl = localizePath(pathname, search, target) + hash;
    localStorage.setItem('language', target);
    // Redirection immédiate (replace pour ne pas polluer l'historique)
    window.location.replace(nextUrl);
  }, []);

  // Synchroniser avec l'URL lors des changements de navigation (important pour SPA)
  useEffect(() => {
    const pathname = window.location.pathname;
    let urlLang: Language | null = null;
    if (pathname.startsWith('/en/') || pathname === '/en') urlLang = 'en';
    else if (pathname.startsWith('/es/') || pathname === '/es') urlLang = 'es';
    else if (pathname.startsWith('/servidor/') || pathname.startsWith('/servidores-') || pathname.startsWith('/guia-votifier')) urlLang = 'es';
    else if (pathname.startsWith('/server/') || pathname.startsWith('/servers-') || pathname.startsWith('/votifier-guide')) urlLang = 'en';
    else if (pathname.startsWith('/serveur/') || pathname.startsWith('/serveurs-') || pathname.startsWith('/guide-votifier')) urlLang = 'fr';
    // Routes d'authentification (liens e-mail) → langue du navigateur si aucun choix enregistré
    else if (isAuthRoute(pathname)) urlLang = initialSavedRef.current || browserLanguage();
    // Routes neutres (/, /game/*, ...) → choix utilisateur, sinon FR par défaut
    else urlLang = initialSavedRef.current || 'fr';


    if (urlLang && urlLang !== language) {
      setLanguage(urlLang);
    }
  }, []);


  const t = (key: string, vars?: Record<string, string | number>): string => {
    let text = translations[language]?.[key] || key;
    
    if (vars) {
      Object.entries(vars).forEach(([varKey, varValue]) => {
        text = text.replace(`{${varKey}}`, String(varValue));
      });
    }
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};